import { formatProgramArchiveDate, formatProgramArchiveDateTime, archiveWorkLabel, archivePhaseNumber, useProgramStatusRefresh } from "@/lib/programDisplay";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Archive,
  Award,
  Bell,
  BookOpen,
  BookOpenText,
  CalendarDays,
  CalendarCheck2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Compass,
  Download,
  FileText,
  Folder,
  Gem,
  Home,
  Headphones,
  Hash,
  LogOut,
  Menu,
  MessageCircle,
  Pause,
  Play,
  Radio,
  Save,
  Search,
  ShieldCheck,
  User,
  Users,
  Video,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Sidebar, SidebarItem, SidebarItemGroup, SidebarItems } from "flowbite-react/components/Sidebar";
import { api, formatApiError } from "@/lib/api";
import { usePortalContentActivity } from "@/lib/portalContentActivity";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ClientAutomationPanel from "@/components/client/ClientAutomationPanel";
import { NextWorkCountdown } from "@/components/client/DailyProgramCard";
import IndividualConsultingSection from "@/components/client/IndividualConsultingSection";
import { consultingPrograms } from "@/content/consultingPrograms";

const practitionerLevels = {
  level_1: "1. Seviye",
  level_2: "2. Seviye",
  level_3: "3. Seviye",
  mastery: "Ustalık Seviyesi",
};
const practitionerTitles = {
  level_1: "Pleiades Uygulayıcısı",
  level_2: "Pleiades Uygulayıcısı",
  level_3: "3. Seviye Eğitmen",
  mastery: "Ustalık Seviyesi Uygulayıcı",
};
const audioCategoryLabels = {
  meditation_narration: "Meditasyon Anlatımı",
  meditation_music: "Meditasyon Müziği",
  guided_practice: "Rehberli Çalışma",
  training_narration: "Eğitim Anlatımı",
  tasavvuf_narration: "Tasavvuf Anlatımı",
  other_audio: "Diğer Ses İçeriği",
};
const panelSectionKeys = new Set([
  "journey",
  "home",
  "topics",
  "practitioner",
  "programs",
  "audio",
  "videos",
  "works",
  "journal",
  "archives",
  "sessions",
  "guide",
  "profile",
  "consulting-guide",
  "stone-sets",
  "individual-consulting",
]);
const dailyProgramNotificationKinds = new Set([
  "daily_program_ready",
  "daily_program_reminder",
  "daily_program_rest_started",
  "daily_program_extension",
]);

function notificationMatchesProgramState(notification, program) {
  if (!dailyProgramNotificationKinds.has(notification?.kind)) return true;
  if (program?.status === "active") {
    return ["daily_program_ready", "daily_program_reminder"].includes(notification.kind);
  }
  if (program?.status === "resting") {
    return notification.kind === "daily_program_rest_started";
  }
  return false;
}
const panelSectionSlugs = {
  journey: "surecim",
  home: "duyurular",
  topics: "konular",
  practitioner: "uygulayici",
  programs: "programlar",
  audio: "sesler",
  videos: "videolar",
  works: "calismalar",
  archives: "arsiv",
  sessions: "seans-haklarim",
  guide: "kullanim-kilavuzu",
  profile: "profil",
  "consulting-guide": "danismanlik-programlari",
  "stone-sets": "dogal-tas-setleri",
  "individual-consulting": "bireysel-danismanlik",
};
const panelSectionBySlug = new Map(
  Object.entries(panelSectionSlugs).map(([section, slug]) => [slug, section]),
);
panelSectionBySlug.set("ana-sayfa", "home");
const announcementChildSections = new Set([
  "topics",
  "programs",
  "audio",
  "videos",
  "sessions",
  "consulting-guide",
  "stone-sets",
  "individual-consulting",
]);
const disabledPortalModules = {
  programs_enabled: false,
  audio_enabled: false,
};
const openProgramRequestStatuses = new Set(["submitted", "reviewed", "code_redeemed"]);
const terminalProgramOfferStatuses = new Set(["completed", "expired", "cancelled_by_admin"]);

function programSelectionLocked(automation) {
  const requestStatus = automation?.request?.status;
  const offerStatus = automation?.offer?.status;
  return (
    openProgramRequestStatuses.has(requestStatus) ||
    (Boolean(offerStatus) && !terminalProgramOfferStatuses.has(offerStatus))
  );
}

function panelSectionFromLocation() {
  const requested = new URLSearchParams(globalThis.location?.search || "").get("sekme");
  if (panelSectionKeys.has(requested)) return requested;
  return panelSectionBySlug.get(requested) || null;
}

function writePanelSectionToHistory(section, fallback, { replace = false } = {}) {
  if (!globalThis.history || !globalThis.location) return;
  const url = new URL(globalThis.location.href);
  if (section === fallback) url.searchParams.delete("sekme");
  else url.searchParams.set("sekme", panelSectionSlugs[section] || section);
  const method = replace ? "replaceState" : "pushState";
  globalThis.history[method]({}, "", `${url.pathname}${url.search}${url.hash}`);
}

export default function ClientPortalPage({ previewMode = false }) {
  const { user, logout } = useAuth();
  const panelScrollRef = useRef(null);
  const [data, setData] = useState(null);
  const [section, setSection] = useState(null);
  const [error, setError] = useState("");
  const [experienceDrafts, setExperienceDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [archiveItems, setArchiveItems] = useState([]);
  const [archiveNextOffset, setArchiveNextOffset] = useState(0);
  const [archiveHasMore, setArchiveHasMore] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [programArchiveItems, setProgramArchiveItems] = useState([]);
  const [programArchiveLoading, setProgramArchiveLoading] = useState(false);
  const [archiveRevision, setArchiveRevision] = useState(0);
  const [archiveDetail, setArchiveDetail] = useState(null);
  const [archiveDetailLoading, setArchiveDetailLoading] = useState(false);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [practitionerChoice, setPractitionerChoice] = useState(null);
  const [practitionerLevel, setPractitionerLevel] = useState("");
  const [practitionerSaving, setPractitionerSaving] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [audioItems, setAudioItems] = useState([]);
  const [videoItems, setVideoItems] = useState([]);
  const [programItems, setProgramItems] = useState([]);
  const [stoneSetItems, setStoneSetItems] = useState([]);
  const [articleItems, setArticleItems] = useState([]);
  const [topicNotificationTarget, setTopicNotificationTarget] = useState(null);
  const [remoteSessionOffers, setRemoteSessionOffers] = useState([]);
  const [portalNotificationFeed, setPortalNotificationFeed] = useState({
    items: [],
    unread_count: 0,
  });
  const loadRemoteSessionOffers = useCallback(async () => {
    const { data: items } = await api.get("/client-portal/remote-session-offers");
    setRemoteSessionOffers(anchorRemoteSessionClock(items || [], Date.now()));
  }, []);
  const updateRemoteSessionAttendance = async (offerId, attending) => {
    const { data: updated } = await api.put(
      `/client-portal/remote-session-offers/${offerId}/attendance`,
      { attending },
    );
    setRemoteSessionOffers((current) => current.map((item) => (
      item.id === offerId ? { ...item, ...updated } : item
    )));
    return updated;
  };

  useEffect(() => {
    if (!section) return;
    window.dispatchEvent(new CustomEvent("pleiades:portal-section", {
      detail: { section },
    }));
  }, [section]);
  const [portalModules, setPortalModules] = useState(null);
  const [contactPhone, setContactPhone] = useState("");
  const [automation, setAutomation] = useState(null);
  const [invitationPrompt, setInvitationPrompt] = useState({ required: false, loaded: false });
  const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);
  const [programPickerOpen, setProgramPickerOpen] = useState(false);
  const [programPickerSelection, setProgramPickerSelection] = useState(null);
  const [selectedConsultingPackage, setSelectedConsultingPackage] = useState(null);
  const [invitationCode, setInvitationCode] = useState("");
  const [invitationSaving, setInvitationSaving] = useState(false);
  const [invitationError, setInvitationError] = useState("");
  const [currentAudio, setCurrentAudio] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const initialSectionSet = useRef(false);
  const overviewRequestRef = useRef(null);
  const overviewWorkRef = useRef(undefined);
  const archiveGenerationRef = useRef(0);
  const archiveDetailRequestRef = useRef(0);
  const panelNavigationHistoryRef = useRef([]);
  const specialProgramActive =
    data?.portal_mode === "special_client" &&
    !terminalProgramOfferStatuses.has(automation?.offer?.status);
  const portalNotifications = (portalNotificationFeed.items || []).filter((notification) =>
    notificationMatchesProgramState(notification, automation?.daily_program),
  );

  const loadPortalNotifications = useCallback(async () => {
    const { data: result } = await api.get("/client-portal/notifications");
    setPortalNotificationFeed({
      items: result?.items || [],
      unread_count: Math.max(0, Number(result?.unread_count) || 0),
    });
    return result;
  }, []);

  const loadPortalArticles = useCallback(async () => {
    const { data: items } = await api.get("/client-portal/articles");
    const nextItems = items || [];
    setArticleItems(nextItems);
    return nextItems;
  }, []);

  useEffect(() => {
    api
      .get("/auth/config")
      .then(({ data: config }) =>
        setContactPhone(config?.whatsapp_phone || config?.contact_phone || ""),
      )
      .catch(() => setContactPhone(""));
  }, []);

  useEffect(() => {
    if (!programSelectionLocked(automation)) return;
    setProgramPickerOpen(false);
    setProgramPickerSelection(null);
  }, [automation]);

  const invalidateArchive = () => {
    archiveGenerationRef.current += 1;
    setArchiveItems([]);
    setArchiveNextOffset(0);
    setArchiveHasMore(false);
    setArchiveLoading(false);
    setProgramArchiveItems([]);
    setProgramArchiveLoading(false);
    setArchiveRevision((current) => current + 1);
  };

  const loadOverview = ({ silent = false } = {}) => {
    if (overviewRequestRef.current) return overviewRequestRef.current;
    const request = api
      .get("/client-portal/overview")
      .then(({ data: overview }) => {
        const nextWorkKey = overviewWorkFingerprint(overview.current_work);
        if (overviewWorkRef.current !== undefined && overviewWorkRef.current !== nextWorkKey) {
          invalidateArchive();
        }
        overviewWorkRef.current = nextWorkKey;
        setData({ ...overview, experiences: (overview.experiences || []).map(experienceReceipt) });
        setError("");
        if (!initialSectionSet.current) {
          setSection(panelSectionFromLocation() || "home");
          initialSectionSet.current = true;
        }
        return overview;
      })
      .catch((e) => {
        if (!silent) setError(formatApiError(e));
        return null;
      })
      .finally(() => {
        if (overviewRequestRef.current === request) overviewRequestRef.current = null;
      });
    overviewRequestRef.current = request;
    return request;
  };
  const loadAutomation = async ({ silent = false } = {}) => {
    try {
      const { data: result } = await api.get("/client-portal/automation");
      setAutomation(result);
      return result;
    } catch (requestError) {
      if (!silent && requestError?.response?.status !== 404) {
        setError(formatApiError(requestError));
      }
      return null;
    }
  };
  const refreshAutomationJourney = async () => {
    const result = await loadAutomation({ silent: true });
    if (result?.next_action === "follow_plan") {
      await loadOverview({ silent: true });
    }
    return result;
  };
  useProgramStatusRefresh({ enabled: section === "journey" && Boolean(automation?.daily_program),
    status: automation?.daily_program?.status, nextTransitionAt: automation?.daily_program?.next_transition_at,
    refresh: refreshAutomationJourney });
  const redeemInvitationCode = async () => {
    if (!invitationCode.trim() || invitationSaving) return;
    setInvitationSaving(true);
    setInvitationError("");
    try {
      await api.post("/client-portal/invitation-codes/redeem", {
        code: invitationCode.trim(),
        kvkk_notice_acknowledged: true,
        kvkk_notice_version: "2026-07-29",
      });
      setInvitationPrompt({ required: false, loaded: true });
      setInvitationDialogOpen(false);
      setInvitationCode("");
      await Promise.all([loadOverview({ silent: true }), loadAutomation({ silent: true })]);
      setSection("works");
      writePanelSectionToHistory("works", "works", { replace: true });
      panelScrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
      toast.success("Davet kodunuz doğrulandı. Başlangıç soruları açıldı.");
    } catch (requestError) {
      setInvitationError(formatApiError(requestError));
    } finally {
      setInvitationSaving(false);
    }
  };
  useEffect(() => {
    loadOverview();
    loadAutomation({ silent: true });
    api
      .get("/client-portal/modules")
      .then(({ data: modules }) => {
        const visibility = {
          programs_enabled: Boolean(modules?.programs_enabled),
          audio_enabled: Boolean(modules?.audio_enabled),
        };
        setPortalModules(visibility);
        if (visibility.audio_enabled) {
          api
            .get("/client-portal/audio-library")
            .then(({ data }) => setAudioItems(data))
            .catch(() => {});
        }
        api
          .get("/client-portal/programs")
          .then(({ data }) => setProgramItems(data))
          .catch(() => setProgramItems([]));
      })
      .catch(() => setPortalModules(disabledPortalModules));
    api
      .get("/client-portal/invitation-prompt")
      .then(({ data }) => setInvitationPrompt({ required: Boolean(data?.required), loaded: true }))
      .catch(() => setInvitationPrompt({ required: false, loaded: true }));
    api
      .get("/client-portal/stone-sets")
      .then(({ data: items }) => setStoneSetItems(items || []))
      .catch(() => setStoneSetItems([]));
    loadPortalArticles().catch(() => setArticleItems([]));
    api.get("/client-portal/video-library").then(({ data: items }) => setVideoItems(items || [])).catch(() => setVideoItems([]));
    loadPortalNotifications().catch(() => {});
    loadRemoteSessionOffers()
      .catch(() => setRemoteSessionOffers([]));
    // Initial bootstrap intentionally runs once; later refreshes use the dedicated listeners below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") loadPortalNotifications().catch(() => {});
    };
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [loadPortalNotifications]);
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") loadRemoteSessionOffers().catch(() => {});
    };
    const timer = window.setInterval(refresh, 15000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [loadRemoteSessionOffers]);
  useEffect(() => {
    const nextAt = data?.daily_experience?.next_available_at;
    if (!nextAt) return undefined;
    const delay = Math.max(1000, new Date(nextAt).getTime() - Date.now() + 1000);
    const timer = window.setTimeout(
      () => loadOverview({ silent: true }),
      Math.min(delay, 2147483647),
    );
    return () => window.clearTimeout(timer);
    // loadOverview is intentionally backed by stable refs and request deduplication.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.daily_experience?.next_available_at]);
  const loadArchive = async ({ reset = false } = {}) => {
    if (archiveLoading || data?.portal_mode !== "special_client") return;
    const offset = reset ? 0 : archiveNextOffset;
    const generation = archiveGenerationRef.current;
    setArchiveLoading(true);
    try {
      const { data: result } = await api.get("/client-portal/work-history", {
        params: { offset, limit: 10 },
      });
      if (archiveGenerationRef.current !== generation) return;
      const items = result?.items || [];
      setArchiveItems((current) => (reset ? items : [...current, ...items]));
      setArchiveHasMore(Boolean(result?.has_more));
      setArchiveNextOffset(result?.next_offset ?? offset + items.length);
    } catch (e) {
      if (archiveGenerationRef.current === generation) setError(formatApiError(e));
    } finally {
      if (archiveGenerationRef.current === generation) setArchiveLoading(false);
    }
  };
  const loadProgramArchive = async () => {
    if (programArchiveLoading || data?.portal_mode !== "special_client") return;
    const generation = archiveGenerationRef.current;
    setProgramArchiveLoading(true);
    try {
      const { data: result } = await api.get("/client-portal/program-archive", {
        params: { offset: 0, limit: 10 },
      });
      if (archiveGenerationRef.current === generation) setProgramArchiveItems(result?.items || []);
    } catch (e) {
      if (archiveGenerationRef.current === generation) setError(formatApiError(e));
    } finally {
      if (archiveGenerationRef.current === generation) setProgramArchiveLoading(false);
    }
  };
  useEffect(() => {
    if (
      section === "archives" &&
      data?.portal_mode === "special_client" &&
      archiveItems.length === 0
    ) {
      loadArchive({ reset: true });
      loadProgramArchive();
    }
    // Archive loading is deliberately section-driven.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, data?.portal_mode, archiveRevision]);
  useEffect(
    () => () => {
      archiveDetailRequestRef.current += 1;
    },
    [],
  );
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const updateKeyboardState = () => {
      const nextKeyboardOpen = window.innerHeight - viewport.height > 120;
      if (!nextKeyboardOpen && (window.scrollX !== 0 || window.scrollY !== 0)) {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
    };
    updateKeyboardState();
    viewport.addEventListener("resize", updateKeyboardState);
    viewport.addEventListener("scroll", updateKeyboardState);
    return () => {
      viewport.removeEventListener("resize", updateKeyboardState);
      viewport.removeEventListener("scroll", updateKeyboardState);
    };
  }, []);
  useEffect(() => {
    const refreshVisibleOverview = () => {
      if (document.visibilityState === "visible") loadOverview({ silent: true });
    };
    window.addEventListener("focus", refreshVisibleOverview);
    document.addEventListener("visibilitychange", refreshVisibleOverview);
    const intervalId = window.setInterval(refreshVisibleOverview, 45000);
    return () => {
      window.removeEventListener("focus", refreshVisibleOverview);
      document.removeEventListener("visibilitychange", refreshVisibleOverview);
      window.clearInterval(intervalId);
    };
    // loadOverview is intentionally backed by stable refs and request deduplication.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(
    () => () => {
      if (audioUrl?.startsWith("blob:")) URL.revokeObjectURL(audioUrl);
    },
    [audioUrl],
  );
  useEffect(() => {
    if (!audioUrl || !audioRef.current) return;
    audioRef.current.load();
    audioRef.current.play().catch(() => {});
  }, [audioUrl]);
  useEffect(() => {
    if (section === "audio") return;
    if (!currentAudio && !audioUrl) return;
    audioRef.current?.pause();
    setPlaying(false);
    setCurrentAudio(null);
    setAudioUrl("");
  }, [section, currentAudio, audioUrl]);

  const navItems = useMemo(() => {
    const shared = {
      home: { key: "home", label: "Ana Sayfa", short: "Ana Sayfa", icon: Home },
      programs: { key: "programs", label: "Programlar", short: "Programlar", icon: CalendarDays },
      audio: {
        key: "audio",
        label: "Sesler",
        short: "Sesler",
        icon: Headphones,
        badge: audioItems.length,
      },
      videos: {
        key: "videos",
        label: "Videolar",
        short: "Videolar",
        icon: Video,
        badge: videoItems.length,
      },
      works: {
        key: "works",
        label: "Bugünkü Çalışmam",
        short: "Bugünkü Çalışmam",
        icon: BookOpen,
      },
      journal: {
        key: "journal",
        label: "Deneyim Günlüğü",
        short: "Günlük",
        icon: BookOpenText,
      },
      archives: { key: "archives", label: "Arşivim", short: "Arşiv", icon: Archive },
      sessions: {
        key: "sessions",
        label: "Seans Haklarım",
        short: "Seanslar",
        icon: CalendarCheck2,
      },
      guide: {
        key: "guide",
        label: "Kullanım Kılavuzu",
        short: "Kılavuz",
        icon: CircleHelp,
      },
      profile: { key: "profile", label: "Profilim", short: "Profil", icon: User },
    };
    if (specialProgramActive) {
      const specialItems = [
        shared.home,
        shared.works,
        { key: "journey", label: "Sürecim", short: "Sürecim", icon: Compass },
        shared.journal,
        shared.guide,
        shared.archives,
        shared.profile,
      ];
      const remoteSessionStatus = automation?.daily_program?.remote_session?.status;
      if (["active", "upcoming"].includes(remoteSessionStatus)) {
        specialItems.splice(4, 0, shared.sessions);
      }
      if (data.practitioner?.status === "approved") {
        specialItems.push({
          key: "practitioner",
          label: "Uygulayıcı Alanım",
          short: "Uygulayıcı",
          icon: Award,
        });
      }
      if (portalModules?.programs_enabled) specialItems.push(shared.programs);
      if (portalModules?.audio_enabled) specialItems.push(shared.audio);
      specialItems.push(shared.videos);
      return specialItems;
    }
    const generalItems = [shared.home];
    if (data?.portal_mode === "special_client") generalItems.push(shared.archives);
    if (portalModules?.programs_enabled) generalItems.push(shared.programs);
    if (portalModules?.audio_enabled) generalItems.push(shared.audio);
    generalItems.push(shared.videos);
    generalItems.push(shared.profile);
    return generalItems;
  }, [data, audioItems.length, videoItems.length, portalModules, specialProgramActive, automation]);
  useEffect(() => {
    if (!data || !portalModules || !section) return;
    const fallback = specialProgramActive ? "works" : "home";
    const isAnnouncementChild =
      announcementChildSections.has(section) &&
      navItems.some((item) => item.key === "home") &&
      (section !== "audio" || portalModules.audio_enabled);
    if (!navItems.some((item) => item.key === section) && !isAnnouncementChild) {
      setSection(fallback);
      writePanelSectionToHistory(fallback, fallback, { replace: true });
    }
  }, [data, navItems, portalModules, section, specialProgramActive]);
  useEffect(() => {
    const syncSectionFromHistory = () => {
      const fallback = specialProgramActive ? "works" : "home";
      setSection(panelSectionFromLocation() || fallback);
    };
    globalThis.addEventListener?.("popstate", syncSectionFromHistory);
    return () => globalThis.removeEventListener?.("popstate", syncSectionFromHistory);
  }, [data?.portal_mode, specialProgramActive]);

  const submitPractitioner = async () => {
    if (practitionerChoice === null || (practitionerChoice && !practitionerLevel)) return;
    setPractitionerSaving(true);
    setError("");
    try {
      await api.post("/client-portal/practitioner", {
        is_practitioner: practitionerChoice,
        level: practitionerChoice ? practitionerLevel : null,
      });
      await loadOverview();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setPractitionerSaving(false);
    }
  };

  const saveExperience = async (event, file) => {
    event.preventDefault();
    const draft = experienceDrafts[file.id] || {};
    if (!draft.title?.trim() || !draft.body?.trim()) return;
    const missingConsent = data?.sensitive_data_consent_required && !draft.sensitive_data_consent;
    const missingReview = !draft.reviewed;
    if (missingConsent || missingReview) {
      const validationTarget = missingConsent ? "sensitive-consent" : "reviewed";
      const validationError = missingConsent
        ? "Deneyimi göndermek için hassas içeriklerin işlenmesine ilişkin onay kutusunu işaretleyin."
        : "Deneyimi göndermek için metninizi gözden geçirdiğinizi belirten onay kutusunu işaretleyin.";
      setExperienceDrafts((current) => ({
        ...current,
        [file.id]: {
          ...current[file.id],
          validation_target: validationTarget,
          validation_error: validationError,
        },
      }));
      globalThis.document
        ?.getElementById(`experience-${validationTarget}-${file.id}`)
        ?.focus();
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { data: created } = await api.post("/client-portal/experiences", {
        file_id: file.id,
        title: draft.title,
        body: draft.body,
        ai_processing_consent: Boolean(draft.ai_processing_consent),
        sensitive_data_consent: Boolean(draft.sensitive_data_consent),
      });
      const receipt = experienceReceipt(created);
      setData((current) => ({
        ...current,
        sensitive_data_consent_required: false,
        experiences: [receipt, ...(current.experiences || [])],
        daily_experience: {
          ...(current.daily_experience || {}),
          submitted: true,
          submitted_at: receipt.created_at,
        },
      }));
      setArchiveItems((current) =>
        current.map((item) => {
          const work = archiveWork(item);
          if (work.assignment_id !== file.assignment_id) return item;
          return {
            ...item,
            experience_count: (item.experience_count || 0) + 1,
            last_experience_at: receipt.created_at,
          };
        }),
      );
      setExperienceDrafts((current) => ({ ...current, [file.id]: { title: "", body: "" } }));
      toast.success("Deneyimin rehberine ulaştı. Gönderim kaydı Arşivim’e eklendi.");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  const completeWork = async () => {
    if (!completeTarget?.assignment_id) return;
    setCompleting(true);
    try {
      await api.post(`/client-portal/work-assignments/${completeTarget.assignment_id}/complete`);
      invalidateArchive();
      await loadOverview();
      setCompleteTarget(null);
      toast.success("Çalışmanız tamamlandı ve arşivinize taşındı.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCompleting(false);
    }
  };
  const openArchiveDetail = async (item) => {
    const work = archiveWork(item);
    const assignmentId = archiveAssignmentId(item);
    if (!assignmentId) return;
    const requestId = ++archiveDetailRequestRef.current;
    setArchiveDetail({ work, experience_receipts: null });
    setArchiveDetailLoading(true);
    try {
      const { data: detail } = await api.get(`/client-portal/work-history/${assignmentId}`);
      if (archiveDetailRequestRef.current !== requestId) return;
      setArchiveDetail({
        work: detail.work,
        experience_receipts: (detail.experience_receipts || detail.receipts || []).map(
          experienceReceipt,
        ),
      });
    } catch (e) {
      if (archiveDetailRequestRef.current !== requestId) return;
      setArchiveDetail(null);
      setError(formatApiError(e));
    } finally {
      if (archiveDetailRequestRef.current === requestId) setArchiveDetailLoading(false);
    }
  };
  const closeArchiveDetail = () => {
    archiveDetailRequestRef.current += 1;
    setArchiveDetailLoading(false);
    setArchiveDetail(null);
  };
  const go = (key) => {
    const fallback = specialProgramActive ? "works" : "home";
    const next = key === "consulting" && specialProgramActive ? "journey" : key;
    if (section && section !== next) panelNavigationHistoryRef.current.push(section);
    setSection(next);
    writePanelSectionToHistory(next, fallback);
    setMenuOpen(false);
    panelScrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
  };
  const openPortalNotification = async (notification) => {
    const readAt = notification.read_at || new Date().toISOString();
    setPortalNotificationFeed((current) => ({
      items: (current.items || []).map((item) => (
        item.id === notification.id ? { ...item, read_at: readAt } : item
      )),
      unread_count: notification.read_at
        ? current.unread_count
        : Math.max(0, (current.unread_count || 0) - 1),
    }));
    try {
      await api.put(
        `/client-portal/notifications/${encodeURIComponent(notification.id)}/read`,
      );
    } catch (requestError) {
      loadPortalNotifications().catch(() => {});
    }
    if (notification.kind === "portal_topic") {
      const seriesKey = String(notification.target_id || notification.id || "").replace(/^topic:/, "");
      setTopicNotificationTarget(seriesKey || null);
      await loadPortalArticles().catch(() => {});
    }
    go(panelSectionKeys.has(notification.target_section) ? notification.target_section : "home");
  };
  const openUsageGuide = (guideId = "kilavuz-kart") => {
    if (globalThis.history && globalThis.location) {
      const url = new URL(globalThis.location.href);
      url.hash = guideId;
      globalThis.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    }
    go("guide");
  };
  const goBack = () => {
    const fallback = specialProgramActive ? "works" : "home";
    const previous = panelNavigationHistoryRef.current.pop();
    setMenuOpen(false);
    const target = previous && previous !== section ? previous : fallback;
    setSection(target);
    writePanelSectionToHistory(target, fallback, { replace: true });
    panelScrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
  };
  const selectAudio = async (item) => {
    try {
      setPlaying(false);
      setCurrentAudio(item);
      setAudioUrl("");
      const { data: playback } = await api.post(
        `/client-portal/audio-library/${item.id}/playback`,
      );
      setCurrentAudio(item);
      setAudioUrl(playback.stream_url);
    } catch (e) {
      setCurrentAudio(null);
      setAudioUrl("");
      setError(formatApiError(e));
    }
  };
  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) audioRef.current.play().catch(() => {});
    else audioRef.current.pause();
  };
  const downloadAudio = async (item) => {
    try {
      const { data: blob } = await api.get(`/client-portal/audio-library/${item.id}`, {
        params: { download: true },
        responseType: "blob",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = item.filename || `${item.title}.mp3`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const quickNavItems = specialProgramActive
    ? navItems.filter((item) => ["works", "journey", "journal", "archives"].includes(item.key))
    : [];

  return (
    <div className={`stitch-client-page ${previewMode ? "h-full min-h-0 overflow-hidden" : "h-screen h-[100dvh] min-h-0 overflow-hidden"} flex flex-col bg-pleiades-ivory text-pleiades-ink`}>
      <Dialog
        open={!previewMode && invitationDialogOpen}
        onOpenChange={(open) => {
          setInvitationDialogOpen(open);
          if (!open) {
            setSelectedConsultingPackage(null);
            setInvitationError("");
          }
        }}
      >
        <DialogContent className="stitch-invitation-dialog top-[max(1rem,env(safe-area-inset-top))] max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] translate-y-0 overflow-y-auto rounded-xl border-pleiades-border sm:top-[50%] sm:max-w-[390px] sm:translate-y-[-50%]">
          <DialogHeader className="text-left">
            <div className="text-[11px] uppercase tracking-[.14em] text-pleiades-blue">
              Danışmanlık erişimi
            </div>
            <DialogTitle className="text-[22px] leading-[1.3] mt-2">
              Davet kodunuzu girin
            </DialogTitle>
            <DialogDescription className="text-[13px] leading-[1.65] text-pleiades-slate mt-2">
              {selectedConsultingPackage
                ? `${selectedConsultingPackage.label} için rehberinizin size verdiği davet kodunu girin.`
                : "Rehberinizin sizin için oluşturduğu davet kodunu girin."}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <label className="block text-[12px] font-medium" htmlFor="invitation-code">
              Davet kodu
            </label>
            <input
              id="invitation-code"
              value={invitationCode}
              onChange={(event) => setInvitationCode(event.target.value.toUpperCase())}
              autoComplete="one-time-code"
              className="mt-2 min-h-12 w-full border border-pleiades-border bg-white px-4 text-[16px] tracking-[.12em] uppercase focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue"
              placeholder="DAVET KODU"
            />
          </div>
          {invitationError && (
            <div
              role="alert"
              className="mt-4 border border-red-200 bg-red-50 px-4 py-3 text-[12px] text-red-800"
            >
              {invitationError}
            </div>
          )}
          <div className="mt-5 space-y-3">
            <button
              type="button"
              onClick={redeemInvitationCode}
              disabled={invitationSaving || !invitationCode.trim()}
              className="w-full min-h-12 bg-pleiades-blue px-5 text-[13px] font-medium text-white disabled:bg-pleiades-slate"
            >
              {invitationSaving ? "Kod doğrulanıyor…" : "Davet kodunu doğrula"}
            </button>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <a
                href="/"
                className="min-h-11 border border-pleiades-border bg-white px-4 text-[12px] font-medium text-pleiades-ink grid place-items-center hover:bg-pleiades-ivory"
              >
                Ana siteye dön
              </a>
              {!previewMode && (
                <button
                  type="button"
                  onClick={logout}
                  className="min-h-11 border border-pleiades-border bg-white px-4 text-[12px] font-medium text-red-700 hover:bg-red-50"
                >
                  Çıkış yap
                </button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={programPickerOpen}
        onOpenChange={(open) => {
          setProgramPickerOpen(open);
          if (!open) setProgramPickerSelection(null);
        }}
      >
        <DialogContent className="stitch-program-picker left-0 right-0 top-auto bottom-0 flex max-h-[min(86dvh,760px)] w-full max-w-none translate-x-0 translate-y-0 flex-col overflow-hidden rounded-t-xl rounded-b-none border-x-0 border-b-0 border-pleiades-border p-0 sm:left-[50%] sm:right-auto sm:bottom-6 sm:w-[min(620px,calc(100vw-2rem))] sm:max-w-[620px] sm:translate-x-[-50%] sm:rounded-xl sm:border">
          <DialogHeader className="sticky top-0 z-10 border-b border-pleiades-border bg-white px-5 py-4 text-left sm:px-6">
            <div className="text-[11px] uppercase tracking-[.14em] text-pleiades-blue">
              Danışmanlık programları
            </div>
            <DialogTitle className="mt-1 text-[20px] leading-[1.3] sm:text-[24px]">
              {programPickerSelection ? programPickerSelection.label : "Çalışma sürenizi seçin"}
            </DialogTitle>
            <DialogDescription className="mt-1 text-[13px] leading-[1.55] text-pleiades-slate">
              {programPickerSelection
                ? "Davet kodu talebinizi oluşturun. Kod kayıtlı e-posta adresinize gönderilecektir."
                : "Programınızı seçin; davet kodunuz talebinizden sonra kayıtlı e-posta adresinize gönderilecektir."}
            </DialogDescription>
          </DialogHeader>

          {programPickerSelection ? (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 overflow-y-auto px-5 py-4 sm:px-6 sm:py-5">
                <button
                  type="button"
                  onClick={() => setProgramPickerSelection(null)}
                  className="mb-3 inline-flex min-h-11 items-center gap-2 text-[12px] font-medium text-pleiades-slate focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue sm:mb-5"
                >
                  <ArrowLeft size={16} aria-hidden="true" />
                  Program seçimine dön
                </button>
                <div className="border border-pleiades-border bg-pleiades-ivory p-3 sm:p-5">
                  <div className="text-[11px] font-medium text-pleiades-blue">
                    {programPickerSelection.months} aylık çalışma alanı
                  </div>
                  <h3 className="mt-1 text-[17px] font-semibold sm:mt-2 sm:text-[18px]">
                    {programPickerSelection.title}
                  </h3>
                  <p className="mt-2 text-[12px] leading-[1.55] text-pleiades-slate sm:mt-3 sm:text-[13px] sm:leading-[1.65]">
                    {programPickerSelection.summary}
                  </p>
                  <dl className="mt-3 grid grid-cols-2 gap-3 border-t border-pleiades-border pt-3 sm:mt-5 sm:gap-4 sm:pt-4">
                    <div>
                      <dt className="text-[11px] text-pleiades-muted">Süre</dt>
                      <dd className="mt-1 text-[14px] font-semibold">
                        {programPickerSelection.months} ay
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[11px] text-pleiades-muted">İlerleyiş</dt>
                      <dd className="mt-1 text-[14px] font-semibold">Günlük çalışma ve takip</dd>
                    </div>
                  </dl>
                </div>
              </div>
              <div className="shrink-0 border-t border-pleiades-border bg-white px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-4">
                <Link
                  to={`/danismanlik/talep?program=${programPickerSelection.slug}`}
                  state={{ requestKnown: true, request: automation?.request || null }}
                  className="grid min-h-12 w-full place-items-center bg-pleiades-blue px-5 text-[14px] font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2"
                >
                  Davet kodu talep et
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid min-h-0 gap-2 overflow-y-auto px-5 py-5 sm:grid-cols-2 sm:px-6">
              {consultingPrograms.map((program) => (
                <button
                  key={program.slug}
                  type="button"
                  onClick={() => setProgramPickerSelection(program)}
                  className="flex min-h-20 items-center justify-between gap-4 border border-pleiades-border bg-white px-4 py-3 text-left hover:border-pleiades-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue"
                >
                  <span>
                    <strong className="block text-[16px]">{program.months} Aylık Program</strong>
                    <small className="mt-1 block text-[12px] text-pleiades-slate">
                      {program.title}
                    </small>
                  </span>
                  <ChevronRight size={18} className="shrink-0" aria-hidden="true" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <div className="flex min-h-0 flex-1">
        <aside
          className="hidden w-64 shrink-0 border-r border-pleiades-border bg-pleiades-sidebar md:flex md:flex-col"
          data-testid="flowbite-client-sidebar"
        >
          <div className="border-b border-pleiades-border px-6 py-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-pleiades-slate">
              Pleiades / Danışan
            </p>
            <h1 className="mt-3 font-editorial text-2xl leading-tight text-pleiades-ink">
              Kişisel panel
            </h1>
            <p className="mt-2 truncate text-xs text-pleiades-slate">{user?.name}</p>
          </div>
          <Sidebar
            aria-label="Danışan paneli bölümleri"
            className="min-h-0 flex-1 [&>div]:h-full [&>div]:rounded-none [&>div]:bg-pleiades-sidebar [&>div]:px-3 [&>div]:py-4"
          >
            <SidebarItems>
              <SidebarItemGroup>
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = section === item.key;
                  return (
                    <SidebarItem
                      key={item.key}
                      as="button"
                      type="button"
                      icon={Icon}
                      onClick={() => go(item.key)}
                      aria-current={active ? "page" : undefined}
                      className={`w-full rounded-sm text-left ${
                        active
                          ? "bg-white text-pleiades-blue"
                          : "text-pleiades-ink hover:bg-white"
                      }`}
                    >
                      <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                        <span className="truncate">{item.label}</span>
                        {item.badge > 0 && <Badge value={item.badge} />}
                      </span>
                    </SidebarItem>
                  );
                })}
              </SidebarItemGroup>
            </SidebarItems>
          </Sidebar>
          {!previewMode && (
            <div className="border-t border-pleiades-border p-3">
              <button
                type="button"
                onClick={logout}
                className="flex min-h-11 w-full items-center justify-between rounded-sm px-4 text-sm text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue"
              >
                <span>Çıkış yap</span>
                <LogOut size={16} aria-hidden="true" />
              </button>
            </div>
          )}
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header
            className="shrink-0 bg-white border-b border-pleiades-border z-30"
            data-testid="client-panel-header"
          >
            <div className="stitch-client-topbar max-w-6xl mx-auto px-4 h-16 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 md:h-[72px] md:max-w-none md:px-8 lg:px-10">
          <div className="stitch-client-left">
            <button
              type="button"
              onClick={goBack}
              aria-label="Geri"
              className="stitch-client-icon-button"
            >
              <ArrowLeft size={20} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => go(specialProgramActive ? "works" : "home")}
              className="stitch-client-brand"
            >
              <span>Pleiades<span className="hidden sm:inline"> Rehberlik</span></span>
            </button>
          </div>
          <button
            type="button"
            onClick={() => go("home")}
            className="stitch-assistant-trigger"
            aria-label="Ana sayfayı aç"
            aria-current={section === "home" ? "page" : undefined}
          >
            <Home size={17} aria-hidden="true" />
            <span>Ana Sayfa</span>
          </button>
          <div className="stitch-client-actions">
            <NotificationCenter
              notifications={portalNotifications}
              onOpenNotification={openPortalNotification}
            />
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Menü"
              aria-expanded={menuOpen}
              aria-controls="client-account-menu"
              className="stitch-menu-button md:hidden"
            >
              <Menu size={20} aria-hidden="true" />
              <span>Menü</span>
            </button>
          </div>
            </div>
          </header>

      {quickNavItems.length > 0 && (
        <div className="pleiades-mobile-segmented-nav shrink-0 md:hidden">
          <div
            role="tablist"
            aria-label="Temel panel bölümleri"
            data-testid="client-quick-nav"
            className="pleiades-mobile-segmented-nav__list"
          >
            {quickNavItems.map((item) => {
              const Icon = item.icon;
              const active = section === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => go(item.key)}
                  className="pleiades-mobile-segmented-nav__button"
                >
                  <Icon size={18} className="shrink-0" aria-hidden="true" />
                  <span>
                    {item.key === "works" ? "Bugün" : item.short}
                  </span>
                  {active && (
                    <span
                      className="pleiades-mobile-segmented-nav__indicator"
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <main
        ref={panelScrollRef}
        className="w-full max-w-6xl mx-auto flex-1 min-h-0 overflow-y-auto overscroll-contain scroll-pt-3 scroll-pb-3 px-3 py-3 sm:px-6 sm:py-5 md:px-8 md:py-8 lg:px-10"
        data-testid="client-panel-scroll"
      >
        {error && (
          <div
            role="alert"
            className="mb-5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-[13px]"
          >
            {error}
          </div>
        )}
        {!data && !error && <Loading />}
        {data && section === "journey" && (
          <SpecialJourneySection
            data={data}
            automation={automation}
            refreshAutomation={refreshAutomationJourney}
            go={go}
          />
        )}
        {data && section === "home" && (
          <HomeSection
            data={data}
            go={go}
            audioItems={audioItems}
            videoItems={videoItems}
            programItems={programItems}
            stoneSetItems={stoneSetItems}
            remoteSessionOffers={remoteSessionOffers}
            onAttendanceChange={updateRemoteSessionAttendance}
            automation={automation}
            invitationRequired={invitationPrompt.loaded && invitationPrompt.required}
            openInvitation={() => {
              setSelectedConsultingPackage(null);
              setInvitationDialogOpen(true);
            }}
            openPrograms={() => {
              if (!programSelectionLocked(automation)) go("consulting-guide");
            }}
          />
        )}
        {data && section === "consulting-guide" && !programSelectionLocked(automation) && (
          <ConsultingGuideSection automation={automation} />
        )}
        {data && section === "individual-consulting" && <IndividualConsultingSection />}
        {data && section === "stone-sets" && (
          <StoneSetsSection items={stoneSetItems} />
        )}
        {data && section === "practitioner" && (
          <PractitionerSection
            data={data}
            go={go}
            portalModules={portalModules || disabledPortalModules}
          />
        )}
        {data && section === "topics" && (
          <TopicsSection
            articles={articleItems}
            notificationSeriesKey={topicNotificationTarget}
            onNotificationTargetConsumed={() => setTopicNotificationTarget(null)}
            scrollToTop={() => panelScrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" })}
          />
        )}
        {data && section === "programs" && (
          <ProgramsSection items={programItems} />
        )}
        {data && section === "audio" && (
          <AudioSection
            items={audioItems}
            current={currentAudio}
            playing={playing}
            select={selectAudio}
            download={downloadAudio}
            toggle={toggleAudio}
          />
        )}
        {data && section === "videos" && <VideoSection items={videoItems} />}
        {data && section === "works" && data.portal_mode === "special_client" && (
          <WorksSection
            data={data}
            drafts={experienceDrafts}
            setDrafts={setExperienceDrafts}
            saving={saving}
            saveExperience={saveExperience}
            requestComplete={setCompleteTarget}
            automation={automation}
            refreshAutomation={refreshAutomationJourney}
            remoteSession={automation?.daily_program?.remote_session}
            contactPhone={contactPhone}
            onOpenGuide={openUsageGuide}
            onExperienceJournalOpen={() => go("journal")}
          />
        )}
        {data && section === "journal" && data.portal_mode === "special_client" && (
          <ExperienceJournalSection
            program={automation?.daily_program}
            sensitiveConsentRequired={data.sensitive_data_consent_required === true}
          />
        )}
        {data && section === "sessions" && (
          <SessionRightsSection
            remoteSession={automation?.daily_program?.remote_session}
            contactPhone={contactPhone}
            offers={remoteSessionOffers}
          />
        )}
        {data && section === "guide" && specialProgramActive && (
          <UsageGuideSection />
        )}
        {data && section === "archives" && data.portal_mode === "special_client" && (
          <ArchivesSection
            packages={programArchiveItems}
            programLoading={programArchiveLoading}
            items={archiveItems}
            loading={archiveLoading}
            hasMore={archiveHasMore}
            loadMore={() => loadArchive()}
            openDetail={openArchiveDetail}
          />
        )}
        {data && section === "profile" && (
          <ProfileSection data={data} user={user} automation={automation} />
        )}
      </main>
        </div>
      </div>

      <audio
        ref={audioRef}
        src={audioUrl || undefined}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      {menuOpen && (
        <div
          className="fixed inset-0 z-50"
          role="dialog"
          aria-modal="true"
          aria-label="Kullanıcı menüsü"
        >
          <button
            onClick={() => setMenuOpen(false)}
            aria-label="Menüyü kapat"
            className="absolute inset-0 bg-pleiades-ink/45 backdrop-blur-[1px]"
          />
          <aside
            id="client-account-menu"
            className="absolute right-0 top-0 h-screen h-[100dvh] w-[min(88vw,360px)] bg-white border-l border-pleiades-border shadow-2xl flex flex-col"
          >
            <div className="h-20 px-5 flex items-center justify-between border-b border-pleiades-border">
              <div className="flex items-center gap-3 min-w-0">
                <div className="pleiades-initials-avatar w-10 h-10 shrink-0 grid place-items-center text-[11px] font-semibold tracking-wide">
                  {initials(user?.name)}
                </div>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold truncate">{user?.name}</div>
                  <div className="text-[10px] text-pleiades-muted mt-0.5">Kişisel Panel</div>
                </div>
              </div>
              <button
                onClick={() => setMenuOpen(false)}
                aria-label="Menüyü kapat"
                className="w-11 h-11 grid place-items-center text-pleiades-slate hover:bg-pleiades-ivory"
              >
                <X size={18} />
              </button>
            </div>
            <nav
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3"
              aria-label="Sağ panel navigasyonu"
            >
              {navItems.map((item) => (
                <DrawerNavButton
                  key={item.key}
                  item={item}
                  active={section === item.key}
                  onClick={() => go(item.key)}
                />
              ))}
              {!previewMode && <div
                data-testid="client-menu-actions"
                className="mt-3 grid gap-2 border-t border-pleiades-border pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
              >
                <button
                  onClick={logout}
                  className="w-full min-h-11 px-4 flex items-center justify-between border border-pleiades-border text-[12px] text-red-700 hover:bg-red-50"
                >
                  <span>Çıkış yap</span>
                  <LogOut size={16} aria-hidden="true" />
                </button>
              </div>}
            </nav>
          </aside>
        </div>
      )}

      {!previewMode &&
        (data?.portal_mode === "newcomer" || automation?.invitation_kind === "special_link") &&
        automation !== null &&
        !automation?.request &&
        !data.practitioner?.status && (
          <PractitionerModal
            choice={practitionerChoice}
            setChoice={setPractitionerChoice}
            level={practitionerLevel}
            setLevel={setPractitionerLevel}
            saving={practitionerSaving}
            submit={submitPractitioner}
          />
        )}
      <AlertDialog
        open={Boolean(completeTarget)}
        onOpenChange={(open) => !open && !completing && setCompleteTarget(null)}
      >
        <AlertDialogContent className="max-w-md rounded-none border-pleiades-border bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Çalışmayı tamamladınız mı?</AlertDialogTitle>
            <AlertDialogDescription className="text-pleiades-slate leading-relaxed">
              Bu işlem çalışmayı tamamlanmış olarak arşivler ve yeni çalışmanın hazırlanabilmesini
              sağlar. Çalışma ve deneyim kayıtlarınız korunur.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={completing}>Geri dön</AlertDialogCancel>
            <AlertDialogAction
              disabled={completing}
              onClick={completeWork}
              className="bg-pleiades-blue text-white hover:bg-pleiades-blue-hover"
            >
              {completing ? "Tamamlanıyor…" : "Evet, çalışmayı tamamladım"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ArchiveDetailDialog
        detail={archiveDetail}
        loading={archiveDetailLoading}
        open={Boolean(archiveDetail)}
        onOpenChange={(open) => !open && closeArchiveDetail()}
      />
    </div>
  );
}

function SpecialJourneySection({ data, automation, refreshAutomation, go }) {
  const consulting = data.special_consulting;
  const dailyProgram = automation?.daily_program;
  const clientFullName = String(data.profile?.full_name || "").trim();
  const approvedPractitionerLevel =
    data.practitioner?.status === "approved"
      ? practitionerLevels[data.practitioner.approved_level]
      : null;
  const active = data.special_access_state === "active";
  const setupRequired = data.special_access_state === "setup_required";
  const expired = data.special_access_state === "expired";
  const programState = clientProgramState(automation);
  const completedSteps = Math.max(0, Number(dailyProgram?.completed_steps) || 0);
  const targetSteps = Math.max(0, Number(dailyProgram?.target_completed_steps) || 0);
  const progressPercent = targetSteps
    ? Math.min(100, Math.round((completedSteps / targetSteps) * 100))
    : 0;
  const cycleCompletedSteps = Math.max(0, Number(dailyProgram?.cycle_completed_steps) || 0);
  const phaseNumber = Math.max(1, Number(dailyProgram?.phase_number) || 1);
  const missedDays = Math.max(0, Number(dailyProgram?.missed_days) || 0);
  const fixedPackage = dailyProgram?.package_extension_allowed === false || Boolean(dailyProgram?.automatic_policy_version);
  const extensionDays = fixedPackage ? 0 : Math.max(0, Number(dailyProgram?.extension_days) || 0);
  const activitySummary = dailyProgram?.activity_summary || {};
  const completedWorkCount = Math.max(
    0,
    Number(activitySummary.completed_work_count ?? completedSteps) || 0,
  );
  const experienceCount = Math.max(0, Number(activitySummary.experience_count) || 0);
  const panelNotificationCount = Math.max(0, Number(activitySummary.panel_notification_count) || 0);
  const sentEmailCount = Math.max(0, Number(activitySummary.sent_email_count) || 0);
  const restProgress = dailyProgramRestProgress(dailyProgram);
  const projectedCompletion =
    dailyProgram?.projected_completion_at || dailyProgram?.planned_completion_at;
  const packageDays = consulting
    ? ({ 1: 30, 3: 90, 6: 180, 12: 365 }[Number(consulting.package_months)] || 0)
    : 0;
  const remainingAccessDays = Math.max(0, Number(consulting?.remaining_days) || 0);
  const currentPackageDay = packageDays
    ? Math.min(
        packageDays,
        Math.max(
          1,
          Number(consulting?.current_day) || packageDays - remainingAccessDays,
        ),
      )
    : 0;
  const remainingPackageDays = packageDays
    ? Math.max(0, packageDays - currentPackageDay)
    : 0;
  const packageProgress = packageDays
    ? Math.min(100, Math.max(0, Math.round((currentPackageDay / packageDays) * 100)))
    : 0;

  return (
    <div className="pleiades-mobile-type-plus-2 space-y-3 sm:space-y-4">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Sürecim</h1>
        {clientFullName && (
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
            <span
              data-testid="journey-client-name"
              className="inline-flex items-center border border-pleiades-border-strong bg-white px-2.5 py-1.5 text-[11px] font-semibold text-pleiades-ink"
            >
              {clientFullName}
            </span>
            {approvedPractitionerLevel && (
              <span className="text-[11px] text-pleiades-blue">
                Pleiades Uygulayıcısı · {approvedPractitionerLevel}
              </span>
            )}
          </div>
        )}
        <p className="text-[12px] text-pleiades-slate mt-2 max-w-2xl leading-relaxed">
          Programın hangi noktasında olduğunuzu, tamamlanan çalışmaları ve sıradaki ara dönemi
          buradan takip edin.
        </p>
      </div>

      {automation?.next_action === "complete_questionnaire" && (
        <section
          className="border border-pleiades-blue bg-white p-4 sm:p-5"
          aria-labelledby="journey-questionnaire-title"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[.14em] text-pleiades-blue">
                Sıradaki adım
              </div>
              <h2 id="journey-questionnaire-title" className="mt-2 text-[18px] font-semibold">
                Başlangıç anketini tamamlayın
              </h2>
              <p className="mt-1 text-[12px] leading-relaxed text-pleiades-slate">
                Kişisel çalışmanızın hazırlanabilmesi için önce kısa başlangıç sorularını yanıtlayın.
              </p>
            </div>
            <button
              type="button"
              onClick={() => go("works")}
              className="inline-flex min-h-12 w-full shrink-0 items-center justify-center gap-2 bg-pleiades-blue px-5 text-[13px] font-semibold text-white hover:bg-pleiades-blue-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2 sm:w-auto"
            >
              <BookOpen size={17} aria-hidden="true" />
              Anketi başlat
            </button>
          </div>
        </section>
      )}

      <section
        data-testid="journey-duration-card"
        className="border border-pleiades-border-strong bg-[#14151A] p-4 text-white sm:p-6"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div>
            <div className="text-[11px] uppercase tracking-[.14em] text-white/50">
              Program süresi
            </div>
            <h2 className="text-[20px] font-semibold mt-2">
              {packageDays ? `${packageDays} Günlük Rehberlik Süreciniz` : "Program tanımlanıyor"}
            </h2>
          </div>
          <span
            className={`self-start sm:self-auto text-[10px] px-2.5 py-1.5 border ${programState === "active" ? "border-emerald-300/40 bg-emerald-400/15 text-emerald-100" : "border-amber-200/40 bg-amber-200/10 text-amber-100"}`}
          >
            {programState === "active"
              ? "Aktif"
              : programState === "completed"
                ? "Çalışma bitti"
                : "Beklemede"}
          </span>
        </div>
        {consulting && (
          <>
          <div className="mt-5 border-t border-white/15 pt-5">
            <div className="flex items-end justify-between gap-3 text-[12px]">
              <span>{currentPackageDay}. gündesiniz</span>
              <strong>{remainingPackageDays} gün kaldı</strong>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden bg-white/15" role="progressbar" aria-label="Paket süresi ilerlemesi" aria-valuemin="0" aria-valuemax="100" aria-valuenow={packageProgress}>
              <div className="h-full bg-white/75" style={{ width: `${packageProgress}%` }} />
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-3 mt-5 pt-5 border-t border-white/15 text-[11px]">
            <JourneyMetric label="Başlangıç" value={formatPortalDate(consulting.started_at)} />
            <JourneyMetric label="Erişim bitişi" value={formatPortalDate(consulting.ends_at)} />
          </dl>
          </>
        )}
      </section>

      {dailyProgram && (
        <section className="stitch-simple-journey bg-white border border-pleiades-border p-4 sm:p-6" aria-labelledby="simple-journey-title">
          <div className="text-[10px] uppercase tracking-[.14em] text-pleiades-blue">Bugüne kadar</div>
          <h2 id="simple-journey-title" className="mt-2 text-[20px] font-medium text-pleiades-ink">Süreç özetiniz</h2>
          <dl className="mt-4 grid grid-cols-2 gap-px border border-pleiades-border bg-pleiades-border">
            <JourneyProgressMetric label="Tamamlanan çalışma" value={`${completedWorkCount}`} />
            <JourneyProgressMetric label="Yazılan deneyim" value={`${experienceCount}`} />
          </dl>
          {(missedDays > 0 || extensionDays > 0) && (
            <p className="mt-4 border border-amber-200 bg-amber-50 p-3 text-[13px] leading-5 text-amber-900">
              {missedDays > 0 ? `${missedDays} gün çalışma tamamlanmadı.` : ""}
              {extensionDays > 0 ? ` Paket bitiş tarihinize ${extensionDays} gün eklendi.` : ""}
              {fixedPackage ? " Tamamlanmayan çalışma atlanmaz; paket bitiş tarihiniz değişmez." : ""}
            </p>
          )}
          <div className="mt-4">
            {dailyProgram.status === "waiting_next_day" ? (
              <NextWorkCountdown value={dailyProgram.next_transition_at} compact />
            ) : dailyProgram.status === "resting" ? (
              <div className="border border-pleiades-border bg-pleiades-ivory p-4 text-[14px] leading-6 text-pleiades-ink">
                Dinlenme döneminiz devam ediyor. Sıradaki çalışmanız ara tamamlandığında açılacak.
              </div>
            ) : dailyProgram.status === "active" ? (
              <div className="border border-pleiades-blue/20 bg-pleiades-blue-soft p-4 text-[14px] leading-6 text-pleiades-ink">
                Bugünkü çalışmanız hazır. Çalışmanızı Bugün sekmesinden açabilirsiniz.
              </div>
            ) : null}
          </div>
        </section>
      )}

      {false && dailyProgram && (
        <section
          className="bg-white border border-pleiades-border p-4 sm:p-6"
          aria-labelledby="journey-progress-title"
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[.14em] text-pleiades-blue">
                Günlük program
              </div>
              <h2 id="journey-progress-title" className="text-[20px] font-semibold mt-2">
                Program ilerlemesi
              </h2>
              <p className="text-[12px] leading-relaxed text-pleiades-slate mt-1">
                Tamamlanan çalışmalar, ara dönemleri ve eklenen günler burada birlikte görünür.
              </p>
            </div>
            <span className="self-start text-[10px] px-2.5 py-1.5 border border-pleiades-blue/20 bg-pleiades-blue-soft text-pleiades-blue">
              %{progressPercent} tamamlandı
            </span>
          </div>

          <div
            className="mt-5"
            aria-label={`${completedSteps} / ${targetSteps} çalışma tamamlandı`}
          >
            <div className="mb-4 border border-pleiades-blue/20 bg-pleiades-blue-soft px-3 py-2.5 text-[12px] leading-relaxed text-pleiades-ink">
              <strong>{completedSteps}. çalışma tamamlandı.</strong>{" "}
              {dailyProgram.status === "resting"
                ? `${restProgress.label} devam ediyor.`
                : `Sıradaki ara döneme ${Math.max(0, 15 - cycleCompletedSteps)} çalışma kaldı.`}
            </div>
            <div className="flex items-end justify-between gap-4 text-[12px]">
              <span className="text-pleiades-slate">Toplam ilerleme</span>
              <strong className="text-[18px] tabular-nums text-pleiades-ink">
                {completedSteps}{" "}
                <span className="text-[12px] font-normal text-pleiades-muted">/ {targetSteps}</span>
              </strong>
            </div>
            <div
              className="h-2 mt-2 bg-pleiades-ivory border border-pleiades-border overflow-hidden"
              role="progressbar"
              aria-label="Toplam program ilerlemesi"
              aria-valuemin="0"
              aria-valuemax={targetSteps || 1}
              aria-valuenow={completedSteps}
            >
              <div
                className="h-full bg-pleiades-blue transition-[width] motion-reduce:transition-none"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <div className="mt-5">
            <h3 className="text-[10px] uppercase tracking-[.14em] text-pleiades-muted">
              Süreç özeti
            </h3>
            <dl className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-pleiades-border border border-pleiades-border mt-2">
              <JourneyProgressMetric label="Tamamlanan çalışma" value={`${completedWorkCount}`} />
              <JourneyProgressMetric label="Yazılan deneyim" value={`${experienceCount}`} />
              <JourneyProgressMetric label="Panel bildirimi" value={`${panelNotificationCount}`} />
              <JourneyProgressMetric label="Gönderilen e-posta" value={`${sentEmailCount}`} />
            </dl>
          </div>

          <dl className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-pleiades-border border border-pleiades-border mt-5">
            <JourneyProgressMetric label="Dönem" value={`${phaseNumber}. dönem`} />
            <JourneyProgressMetric
              label="Dönem ilerlemesi"
              value={
                dailyProgram.status === "resting"
                  ? "15 çalışma tamamlandı"
                  : `${cycleCompletedSteps} / 15 çalışma`
              }
            />
            <JourneyProgressMetric label="Ara günü" value={restProgress.label} />
            <JourneyProgressMetric label="Yapılamayan gün" value={`${missedDays} gün`} />
            <JourneyProgressMetric label="Eklenen süre" value={`${extensionDays} gün`} />
            <JourneyProgressMetric
              label="Öngörülen bitiş"
              value={formatPortalDate(projectedCompletion)}
            />
          </dl>

          <div className="stitch-journey-status-note mt-4 text-[12px] leading-relaxed text-pleiades-slate">
            {dailyProgram.status === "waiting_next_day" ? (
              <NextWorkCountdown value={dailyProgram.next_transition_at} compact />
            ) : dailyProgram.status === "resting" ? (
              <p>
                {dailyProgram.next_plan?.rest_days
                  ? `${dailyProgram.next_plan.rest_days} günlük ara`
                  : "Dinlenme dönemi"}{" "}
                program süresine dahildir. Sıradaki çalışma
                {dailyProgram.rest_until
                  ? ` ${formatPortalDate(dailyProgram.rest_until)} tarihinde açılır.`
                  : " ara tamamlandığında açılır."}
              </p>
            ) : extensionDays > 0 ? (
              <p>
                Çalışmanın tamamlanmadığı günler nedeniyle takvime {extensionDays} ek gün eklendi.
              </p>
            ) : (
              <p>Henüz programa ek süre eklenmedi.</p>
            )}
          </div>
        </section>
      )}

      {automation && (
        <ClientAutomationPanel
          automation={automation}
          refresh={refreshAutomation}
          surface="process"
        />
      )}

      {(expired || setupRequired) && (
        <section
          className={`border p-4 sm:p-5 flex items-start gap-3 ${expired ? "border-amber-200 bg-amber-50 text-amber-900" : "border-pleiades-blue/25 bg-pleiades-blue-soft text-pleiades-ink"}`}
        >
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>
            <h2 className="text-[14px] font-semibold">
              {expired ? "Danışmanlık süresi tamamlandı" : "Program kurulumu bekleniyor"}
            </h2>
            <p className="text-[13px] leading-[1.6] mt-1">
              {expired
                ? "Geçmiş çalışmalarınız Arşivim’de korunuyor."
                : "Programınız tanımlandığında süreç durumu otomatik güncellenecek."}
            </p>
          </div>
        </section>
      )}
    </div>
  );
}

function HomeSection({
  data,
  go,
  audioItems,
  videoItems,
  programItems,
  stoneSetItems,
  remoteSessionOffers,
  onAttendanceChange,
  automation,
  invitationRequired,
  openInvitation,
  openPrograms,
}) {
  const featuredRemoteSession = remoteSessionOffers.find(
    (item) => item.session_status === "live" || item.session_status === "upcoming",
  );
  const [resendingInvitation, setResendingInvitation] = useState(false);
  const [invitationResendCount, setInvitationResendCount] = useState(
    Number(automation?.request?.invitation_resend_count || 0),
  );
  const [invitationHelpMessage, setInvitationHelpMessage] = useState("");
  const special = data.portal_mode === "special_client";
  const selectionLocked = programSelectionLocked(automation);
  const awaitingInvitationCode =
    !special && openProgramRequestStatuses.has(automation?.request?.status);
  const offerTerminal = terminalProgramOfferStatuses.has(automation?.offer?.status);
  const consultingProgramOwned = special || automation?.offer?.status === "active";
  const consultingWorkStarted =
    consultingProgramOwned &&
    !offerTerminal &&
    (Boolean(data.current_work) ||
      ["active", "resting", "completed"].includes(automation?.daily_program?.status));
  useEffect(() => {
    setInvitationResendCount(Number(automation?.request?.invitation_resend_count || 0));
  }, [automation?.request?.invitation_resend_count]);

  const resendInvitationFromHome = async () => {
    if (!automation?.request?.id || resendingInvitation) return;
    setResendingInvitation(true);
    setInvitationHelpMessage("");
    try {
      const { data: result } = await api.post(
        `/client-portal/program-requests/${automation.request.id}/resend-invitation`,
      );
      setInvitationResendCount(Number(result?.request?.invitation_resend_count || 1));
      setInvitationHelpMessage("Davet kodunuz kayıtlı e-posta adresinize yeniden gönderiliyor.");
    } catch (requestError) {
      setInvitationHelpMessage(formatApiError(requestError));
    } finally {
      setResendingInvitation(false);
    }
  };
  if (awaitingInvitationCode) {
    return (
      <div className="grid gap-3 md:grid-cols-[minmax(0,1.15fr)_minmax(320px,.85fr)] md:items-start">
      <section
        data-testid="invitation-code-only-card"
        className="w-full border border-pleiades-border bg-white p-5 sm:p-7"
      >
        <div className="grid h-12 w-12 place-items-center bg-pleiades-blue-soft text-pleiades-blue">
          <ShieldCheck size={22} aria-hidden="true" />
        </div>
        <div className="mt-5 text-[11px] uppercase tracking-[.14em] text-pleiades-blue">
          Davetli erişim
        </div>
        <h1 className="mt-2 text-[22px] font-semibold">Davet kodunuzla devam edin</h1>
        <p className="mt-3 text-[13px] leading-[1.65] text-pleiades-slate">
          Talebiniz alındı. Kayıtlı e-posta adresinize gönderilen davet kodunu girerek kişisel
          çalışma panelinizi açın.
        </p>
        <button
          type="button"
          onClick={openInvitation}
          className="mt-5 min-h-12 w-full bg-pleiades-blue px-5 text-[14px] font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2"
        >
          Davet kodunu gir
        </button>
        {invitationResendCount < 1 ? (
          <button
            type="button"
            disabled={resendingInvitation}
            onClick={resendInvitationFromHome}
            className="mt-2 min-h-12 w-full border border-pleiades-border bg-white px-5 text-[13px] font-medium text-pleiades-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue disabled:opacity-50"
          >
            {resendingInvitation ? "Yeniden gönderiliyor…" : "Davet kodumu almadım"}
          </button>
        ) : (
          <a
            href={`https://wa.me/905327991130?text=${encodeURIComponent("Merhaba, program davet kodum yeniden gönderildi ancak e-postama ulaşmadı. Destek rica ediyorum.")}`}
            target="_blank"
            rel="noreferrer"
            className="mt-2 flex min-h-12 w-full items-center justify-center gap-2 border border-pleiades-border bg-white px-5 text-[13px] font-medium text-pleiades-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue"
          >
            <MessageCircle size={17} aria-hidden="true" />
            Kod hâlâ gelmediyse WhatsApp’tan yaz
          </a>
        )}
        {invitationHelpMessage && (
          <p role="status" className="stitch-invitation-help-note mt-3 text-[11px] leading-relaxed">
            {invitationHelpMessage}
          </p>
        )}
      </section>
      <div className="space-y-3">
        {featuredRemoteSession && (
          <RemoteSessionAnnouncement
            offer={featuredRemoteSession}
            onAttendanceChange={onAttendanceChange}
          />
        )}
        <AnnouncementCard
          icon={CalendarCheck2}
          eyebrow={remoteSessionOffers.length ? `${remoteSessionOffers.length} duyuru` : "Danışmanlık"}
          title="Uzaktan Seanslar"
          text="Herkese açık uzaktan seans duyurularını ve size tanımlanan hakları inceleyin."
          action="Seansları gör"
          onClick={() => go("sessions")}
          badge={remoteSessionOffers.length}
        />
      </div>
      <a
        className="stitch-home-whatsapp md:col-span-2"
        href="https://wa.me/905327991130?text=Bireysel%20dan%C4%B1%C5%9Fmanl%C4%B1k%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum."
        target="_blank"
        rel="noreferrer"
      >
        <MessageCircle size={18} strokeWidth={2} aria-hidden="true" />
        WhatsApp’tan Bilgi Al
      </a>
      </div>
    );
  }
  return (
    <>
      {!consultingWorkStarted && (!selectionLocked || consultingProgramOwned) && (
        <section
          data-testid="consulting-programs-card"
          className="relative mb-3 overflow-hidden border-2 border-pleiades-border-strong bg-white p-5 text-pleiades-ink sm:p-6"
        >
          <div className="flex items-start gap-4">
            <span className="grid h-10 w-10 shrink-0 place-items-center bg-pleiades-ivory text-pleiades-blue">
              <CalendarDays size={18} aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <span className="block text-[10px] uppercase tracking-[.16em] text-pleiades-blue">
                Şeref Tolga Kuralay’ın
              </span>
              <h2 className="mt-1.5 font-editorial text-[22px] leading-tight sm:text-[26px]">
                Danışmanlık Programları
              </h2>
              <p className="mt-2 max-w-2xl text-[12px] leading-[1.6] text-pleiades-slate sm:text-[13px]">
                Kişisel çalışma ve takip sürecini inceleyin; size uygun çalışma dönemini seçin.
              </p>
              <div className="mt-4 flex flex-wrap gap-1.5" aria-label="Program süreleri">
                {[1, 3, 6, 12].map((months) => (
                  <span
                    key={months}
                    className="border border-pleiades-border bg-pleiades-ivory px-2.5 py-1 text-[10px] text-pleiades-slate"
                  >
                    {months} ay
                  </span>
                ))}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={openPrograms}
            className="mt-4 flex min-h-11 w-full items-center justify-center gap-2 border-2 border-pleiades-blue bg-white px-4 text-[13px] font-semibold text-pleiades-blue transition-colors hover:bg-pleiades-ivory focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2"
          >
            Programları incele
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </section>
      )}
      {featuredRemoteSession && (
        <section className="mb-3" aria-label="Yaklaşan uzaktan seans">
          <RemoteSessionAnnouncement
            offer={featuredRemoteSession}
            onAttendanceChange={onAttendanceChange}
          />
        </section>
      )}
      <div data-testid="announcements-grid" className="grid grid-cols-2 gap-2.5 sm:gap-3">
        <button
          type="button"
          onClick={() => go("individual-consulting")}
          className="stitch-individual-consulting-card col-span-2"
          aria-label="Şeref Tolga Kuralay bireysel danışmanlığını incele"
        >
          <span>
            <small>Bireysel çalışma</small>
            <strong>Şeref Tolga Kuralay Bireysel Danışmanlığı</strong>
          </span>
          <ChevronRight size={18} aria-hidden="true" />
        </button>
        <AnnouncementCard
          icon={CalendarCheck2}
          eyebrow="Danışmanlık"
          title="Uzaktan Seanslar"
          text="Seans haklarınızı görün ve randevu bağlantısına ulaşın."
          action="Seansları gör"
          onClick={() => go("sessions")}
          badge={remoteSessionOffers.length}
        />
        <AnnouncementCard
          icon={FileText}
          eyebrow="Okuma"
          title="Konular"
          text="Pleiades makalelerini ve uygulama rehberlerini okuyun."
          action="Konuları gör"
          onClick={() => go("topics")}
        />
        <AnnouncementCard
          icon={Headphones}
          eyebrow={audioItems.length ? `${audioItems.length} yayın` : "Ses kütüphanesi"}
          title="MP3 ve Sesler"
          text="Size açık meditasyon anlatımlarını ve ses kayıtlarını dinleyin."
          action="Sesleri aç"
          onClick={() => go("audio")}
          badge={audioItems.length}
        />
        <AnnouncementCard
          icon={Video}
          eyebrow={videoItems.length ? `${videoItems.length} video` : "Video kütüphanesi"}
          title="Röportajlar ve Söyleşiler"
          text="Pleiades sistemiyle ilgili röportajları ve uzman görüşlerini izleyin."
          action="Videoları aç"
          onClick={() => go("videos")}
          badge={videoItems.length}
        />
        <AnnouncementCard
          icon={CalendarDays}
          eyebrow={programItems.length ? `${programItems.length} yaklaşan` : "Takvim"}
          title="Etkinlikler"
          text="Kamp, eğitim ve çevrim içi buluşmaları takip edin."
          action="Etkinlikleri gör"
          onClick={() => go("programs")}
          badge={programItems.length}
        />
        <AnnouncementCard
          icon={Gem}
          eyebrow={stoneSetItems.length ? `${stoneSetItems.length} özel set` : "Kişiye özel"}
          title="Doğal Taş Meditasyon Setleri"
          text="Şeref Tolga Kuralay tarafından kişiye özel hazırlanan doğal taş setlerini inceleyin."
          action="Setleri incele"
          onClick={() => go("stone-sets")}
          badge={stoneSetItems.length}
        />
      </div>
      {!special && invitationRequired && (
        <button
          type="button"
          onClick={openInvitation}
          className="mt-3 flex min-h-12 w-full items-center gap-3 border border-pleiades-blue/25 bg-pleiades-blue-soft px-4 text-left text-[13px] font-medium text-pleiades-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue"
        >
          <ShieldCheck size={17} aria-hidden="true" />
          <span className="flex-1">Davetli erişim — kodunuzla kişisel çalışma alanınızı açın</span>
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      )}
      <a
        className="stitch-home-whatsapp mt-3"
        href="https://wa.me/905327991130?text=Bireysel%20dan%C4%B1%C5%9Fmanl%C4%B1k%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum."
        target="_blank"
        rel="noreferrer"
      >
        <MessageCircle size={18} strokeWidth={2} aria-hidden="true" />
        WhatsApp’tan Bilgi Al
      </a>
    </>
  );
}

const consultingFaqItems = [
  {
    question: "Programlar arasındaki temel fark nedir?",
    answer:
      "Programların çalışma yöntemi aynıdır. Temel fark, Şeref Tolga Kuralay’ın danışanı takip edeceği süre ve bu sürede oluşabilecek çalışma dönemlerinin kapsamıdır. Süre uzadıkça takip ve gözlem alanı genişler.",
  },
  {
    question: "Hangi program bana daha uygundur?",
    answer:
      "1 aylık program belirli bir konuya odaklanarak sistemi deneyimlemek isteyenlere; 3 ve 6 aylık programlar düzenli ve daha kapsamlı ilerlemek isteyenlere; 12 aylık program ise uzun süreli takip isteyenlere uygundur. Son seçim, ihtiyacınız ve ayırabileceğiniz süre dikkate alınarak yapılmalıdır.",
  },
  {
    question: "Herkese aynı çalışma mı verilir?",
    answer:
      "Hayır. Başlangıç anketi Şeref Tolga Kuralay tarafından değerlendirilir. Esmâ çalışmaları, meditasyon kodları ve uygulama planı kişiye özel olarak hazırlanır ve danışan panelinde paylaşılır.",
  },
  {
    question: "Çalışmalar nasıl ilerler?",
    answer:
      "Program onaylandıktan sonra başlangıç anketi açılır. Anket değerlendirildikten sonra ilk çalışma hazırlanır. Kullanıcı çalışmayı uygular, tamamlanma durumunu ve deneyimini panelden bildirir. Süreç Şeref Tolga Kuralay tarafından takip edilir.",
  },
  {
    question: "Çalışmalar her gün değişir mi?",
    answer:
      "Her çalışma kendi belirlenen süresi boyunca devam eder. Kullanıcı çalışmayı tamamladığını bildirdiğinde, programın ilerleyişine göre sonraki çalışma açılır veya hazırlanır. Yapılmayan çalışma kendiliğinden atlanmaz.",
  },
  {
    question: "Esmâ ve meditasyon kodları nasıl belirlenir?",
    answer:
      "Esmâlar ve meditasyon kodları başlangıç anketi, programın mevcut aşaması ve süreç içinde paylaşılan deneyimler dikkate alınarak Şeref Tolga Kuralay tarafından belirlenir. Başka bir kişiye ait çalışma kullanılmamalıdır.",
  },
  {
    question: "Uzaktan seans her programda bulunur mu?",
    answer:
      "Uzaktan seans otomatik veya her programa zorunlu olarak dahil değildir. Şeref Tolga Kuralay sürecin belirli bir döneminde gerekli ve uygun görürse danışanın paneline seans hakkı tanımlar.",
  },
  {
    question: "Program süresi uzadıkça çalışma daha güçlü mü olur?",
    answer:
      "Hayır. Uzun program daha güçlü veya garantili sonuç anlamına gelmez. Daha uzun süre; uygulamaların, deneyimlerin ve farklı çalışma dönemlerinin daha geniş bir zaman içinde takip edilmesini sağlar.",
  },
  {
    question: "Bir şey hissetmezsem çalışma etkisiz mi demektir?",
    answer:
      "Hayır. Çalışma sırasında belirgin bir fiziksel veya duygusal deneyim yaşamak zorunlu değildir. Kullanıcının görevi çalışmayı verilen biçimde uygulamak, süreci gözlemlemek ve deneyimini olduğu gibi aktarmaktır.",
  },
  {
    question: "Bir gün çalışmayı yapamazsam ne olur?",
    answer:
      "Çalışma atlanmaz. “Yapamadım” seçildiğinde aynı çalışma, kullanıcı tamamladığını bildirene kadar devam eder. Böylece program sırası bozulmadan kaldığı yerden sürer.",
  },
  {
    question: "Çalışmayı kısa süre yaparsam tamamlanmış sayılır mı?",
    answer:
      "Çalışmaya başlanmış ancak belirlenen sürenin tamamı uygulanamamışsa kullanıcı yine “Yaptım” diyebilir. Sürenin tamamlanması önerilir; ancak eksik dakika nedeniyle çalışma geçersiz sayılmaz.",
  },
  {
    question: "Davet kodunu nasıl alırım?",
    answer:
      "İstediğiniz programı seçip “Davet kodu talep et” butonuna basabilirsiniz. Talebiniz incelendikten sonra davet kodu kayıtlı e-posta adresinize gönderilir.",
  },
  {
    question: "Davet kodunu girdikten sonra ne olur?",
    answer:
      "Kod doğrulandığında seçilen program hesabınıza bağlanır ve başlangıç anketi açılır. Anket tamamlandığında kişisel çalışma planı hazırlanır ve Şeref Tolga Kuralay’ın onayında bekler. Onaydan sonra program başlar.",
  },
  {
    question: "Program sırasında yardım almak istersem ne yapmalıyım?",
    answer:
      "Paneldeki rehberlik alanlarını kullanabilir veya size gösterilen iletişim kanalı üzerinden Şeref Tolga Kuralay’ın asistanıyla görüşebilirsiniz. Acil veya sağlıkla ilgili durumlarda uygun sağlık hizmetine başvurulmalıdır.",
  },
];

function ConsultingGuideSection({ automation }) {
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const trackedFaq = expandedFaq === null ? null : consultingFaqItems[expandedFaq];
  usePortalContentActivity(trackedFaq ? {
    active: true,
    content_type: "faq",
    content_id: `consulting-faq-${expandedFaq}`,
    content_title: trackedFaq.question,
  } : null);

  return (
    <Page title="Danışmanlık Programları">
      <Dialog open={Boolean(selectedProgram)} onOpenChange={(open) => !open && setSelectedProgram(null)}>
        <DialogContent className="left-0 right-0 top-auto bottom-0 flex max-h-[min(86dvh,720px)] w-full max-w-none translate-x-0 translate-y-0 flex-col overflow-hidden rounded-t-xl rounded-b-none border-x-0 border-b-0 border-pleiades-border p-0 sm:left-[50%] sm:right-auto sm:bottom-6 sm:w-[min(620px,calc(100vw-2rem))] sm:max-w-[620px] sm:translate-x-[-50%] sm:rounded-xl sm:border">
          {selectedProgram && (
            <>
              <DialogHeader className="shrink-0 border-b border-pleiades-border bg-white px-5 py-4 text-left sm:px-6">
                <div className="text-[11px] uppercase tracking-[.14em] text-pleiades-blue">
                  {selectedProgram.label}
                </div>
                <DialogTitle className="mt-1 text-[20px] leading-[1.3]">
                  {selectedProgram.title}
                </DialogTitle>
                <DialogDescription className="mt-1 text-[12px] leading-[1.55] text-pleiades-slate">
                  {selectedProgram.summary}
                </DialogDescription>
              </DialogHeader>
              <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">
                <dl className="grid gap-3 text-[12px] leading-[1.55] sm:text-[13px] sm:leading-[1.6]">
                  <div className="border border-pleiades-border bg-pleiades-ivory p-3">
                    <dt className="font-semibold text-pleiades-ink">Kimler için uygun?</dt>
                    <dd className="mt-1 text-pleiades-slate">{selectedProgram.suitableFor}</dd>
                  </div>
                  <div className="border border-pleiades-border bg-pleiades-ivory p-3">
                    <dt className="font-semibold text-pleiades-ink">Neyi amaçlar?</dt>
                    <dd className="mt-1 text-pleiades-slate">{selectedProgram.focus}</dd>
                  </div>
                  <div className="border border-pleiades-border bg-pleiades-ivory p-3">
                    <dt className="font-semibold text-pleiades-ink">Diğer sürelerden farkı</dt>
                    <dd className="mt-1 text-pleiades-slate">{selectedProgram.difference}</dd>
                  </div>
                </dl>
              </div>
              <div className="shrink-0 border-t border-pleiades-border bg-white px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-4">
                <Link
                  to={`/danismanlik/talep?program=${selectedProgram.slug}`}
                  state={{ requestKnown: true, request: automation?.request || null }}
                  className="grid min-h-12 w-full place-items-center bg-pleiades-blue px-4 text-[13px] font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2"
                >
                  Davet kodu talep et
                </Link>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <section aria-label="Danışmanlık program süreleri">
        <div className="grid gap-3 md:grid-cols-2">
          {consultingPrograms.map((program) => (
              <article key={program.slug} className="border border-pleiades-border bg-white">
                <div className="p-5 sm:p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-[11px] uppercase tracking-[.13em] text-pleiades-blue">
                        {program.label}
                      </div>
                      <h3 className="mt-2 text-[18px] font-semibold leading-[1.35]">{program.title}</h3>
                    </div>
                    <div className="grid h-11 w-11 shrink-0 place-items-center border border-pleiades-border bg-pleiades-ivory text-[15px] font-semibold text-pleiades-blue">
                      {program.months}
                    </div>
                  </div>
                  <p className="mt-3 text-[13px] leading-[1.65] text-pleiades-slate">{program.summary}</p>
                  <button
                    type="button"
                    onClick={() => setSelectedProgram(program)}
                    className="mt-4 flex min-h-11 w-full items-center justify-between border-t border-pleiades-border pt-3 text-left text-[13px] font-medium text-pleiades-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue"
                  >
                    Programın ayrıntılarını incele
                    <ChevronRight size={15} />
                  </button>
                </div>
              </article>
            ))}
        </div>
      </section>

      <section aria-labelledby="consulting-faq-title" className="border border-pleiades-border bg-white">
        <div className="border-b border-pleiades-border px-5 py-5 sm:px-7">
          <div className="text-[11px] uppercase tracking-[.14em] text-pleiades-blue">
            Merak edilenler
          </div>
          <h2 id="consulting-faq-title" className="mt-2 text-[20px] font-semibold leading-[1.35]">
            Sıkça Sorulan Sorular
          </h2>
          <p className="mt-2 text-[12px] leading-[1.6] text-pleiades-slate">
            Cevabını görmek istediğiniz soruya dokunun.
          </p>
        </div>
        <div className="divide-y divide-pleiades-border">
          {consultingFaqItems.map((item, index) => {
            const expanded = expandedFaq === index;
            return (
              <div key={item.question} className="px-5 sm:px-7">
                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-controls={`consulting-faq-${index}`}
                  onClick={() => setExpandedFaq(expanded ? null : index)}
                  className="flex min-h-14 w-full items-center justify-between gap-4 py-3 text-left text-[13px] font-semibold leading-[1.45] text-pleiades-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue"
                >
                  <span>{item.question}</span>
                  <ChevronRight
                    size={16}
                    className={`shrink-0 text-pleiades-blue transition-transform motion-reduce:transition-none ${expanded ? "rotate-90" : ""}`}
                    aria-hidden="true"
                  />
                </button>
                {expanded && (
                  <div
                    id={`consulting-faq-${index}`}
                    className="max-w-3xl pb-5 text-[13px] leading-[1.7] text-pleiades-slate"
                  >
                    {item.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <p className="max-w-4xl text-[11px] leading-[1.65] text-pleiades-muted">
        Pleiades danışmanlığı meditasyon ve farkındalık rehberliğidir; sağlık hizmeti, teşhis,
        psikoterapi veya tıbbi tedavi değildir ve belirli bir sonuç vaat etmez.
      </p>
    </Page>
  );
}

const stoneSetWhatsAppUrl = (item) =>
  `https://wa.me/905327991130?text=${encodeURIComponent(
    `Merhaba, “${item.title}” doğal taş meditasyon seti hakkında bilgi almak ve kişiye özel set talep etmek istiyorum.`,
  )}`;

function StoneSetsSection({ items }) {
  return (
    <Page
      title="Doğal Taş Meditasyon Setleri"
      description="Şeref Tolga Kuralay tarafından kişiye özel hazırlanan meditasyon setlerini inceleyin."
    >
      {items.length ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {items.map((item) => (
            <article key={item.id} className="overflow-hidden border border-pleiades-border bg-white">
              <img
                src={item.image_url}
                alt={`${item.title} doğal taş meditasyon seti`}
                className="aspect-[4/3] w-full border-b border-pleiades-border object-cover"
                loading="lazy"
              />
              <div className="p-4 sm:p-5">
                <div className="text-[10px] uppercase tracking-[.12em] text-pleiades-blue">Kişiye özel hazırlık</div>
                <h2 className="mt-1 text-[17px] font-semibold">{item.title}</h2>
                <p className="mt-2 whitespace-pre-line text-[12px] leading-relaxed text-pleiades-slate">{item.description}</p>
                <a
                  href={stoneSetWhatsAppUrl(item)}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 flex min-h-12 items-center justify-center gap-2 bg-pleiades-ink px-4 text-[13px] font-medium text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2"
                >
                  <MessageCircle size={17} aria-hidden="true" />
                  WhatsApp’tan set talep et
                </a>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <section className="border border-dashed border-pleiades-border bg-white p-8 text-center">
          <Gem className="mx-auto text-pleiades-muted" aria-hidden="true" />
          <h2 className="mt-3 text-[14px] font-semibold">Henüz yayınlanmış set bulunmuyor</h2>
          <p className="mt-2 text-[11px] text-pleiades-muted">Yeni kişiye özel setler yayınlandığında burada görünecek.</p>
        </section>
      )}
    </Page>
  );
}

function _parseSimpleTopicSeries(source) {
  const lines = source.split("\n");
  const firstItemLine = lines.findIndex((line) => line.startsWith("## "));
  const items = [];
  let currentItem = null;
  lines.slice(firstItemLine).forEach((line) => {
    if (line.startsWith("## ")) {
      currentItem = { title: line.slice(3), paragraphs: [] };
      items.push(currentItem);
      return;
    }
    if (currentItem && line.trim()) currentItem.paragraphs.push(line.trim());
  });
  return {
    title: lines[0].replace(/^#\s+/, ""),
    introduction: lines.slice(1, firstItemLine).map((line) => line.trim()).filter(Boolean),
    items,
  };
}

function _parseCosmicEsmaSeries(source) {
  const lines = source.split("\n");
  const firstSectionLine = lines.findIndex((line) => line.startsWith("## "));
  const groups = [];
  const items = [];
  let special = null;
  let closing = null;
  let currentGroup = null;
  let currentItem = null;

  lines.slice(firstSectionLine).forEach((line) => {
    if (line.startsWith("## ")) {
      const title = line.slice(3);
      if (title === "İsimler Üstü İşaret: HÛ") {
        special = { title, paragraphs: [] };
        currentItem = special;
        currentGroup = null;
      } else if (title === "Varoluşun Esmâsal Akışı") {
        closing = { title, paragraphs: [] };
        currentItem = closing;
        currentGroup = null;
      } else {
        currentGroup = { title, items: [] };
        groups.push(currentGroup);
        currentItem = null;
      }
      return;
    }
    if (line.startsWith("### ")) {
      currentItem = {
        title: line.slice(4),
        paragraphs: [],
        groupTitle: currentGroup?.title || "",
      };
      items.push(currentItem);
      currentGroup?.items.push(currentItem);
      return;
    }
    if (currentItem && line.trim()) currentItem.paragraphs.push(line.trim());
  });

  return {
    title: lines[0].replace(/^#\s+/, ""),
    introduction: lines.slice(1, firstSectionLine).map((line) => line.trim()).filter(Boolean),
    groups,
    items,
    special,
    closing,
  };
}

function normalizeEsmaSearch(value) {
  return String(value || "")
    .toLocaleLowerCase("tr-TR")
    .replaceAll("ı", "i")
    .replaceAll("ğ", "g")
    .replaceAll("ş", "s")
    .replaceAll("ç", "c")
    .replaceAll("ö", "o")
    .replaceAll("ü", "u")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "");
}

function ArticleParagraph({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <p>
      {parts.map((part, index) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={index}>{part.slice(2, -2)}</strong>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </p>
  );
}

const TOPIC_CATEGORIES = [
  { key: "tasavvuf", label: "Tasavvuf", description: "Tasavvufî yaklaşım ve kavramlar" },
  { key: "pleiades", label: "Pleiades", description: "Pleiades öğretisi ve Esmâ çalışmaları" },
  { key: "kuantum", label: "Kuantum", description: "Kuantum yaklaşımı ve konu anlatımları" },
  { key: "golge-benlik", label: "Gölge Benlik", description: "Gölge benlik ve içsel farkındalık" },
];

function TopicsSection({
  articles,
  scrollToTop,
  notificationSeriesKey = null,
  onNotificationTargetConsumed,
}) {
  const seriesList = useMemo(() => {
    const grouped = new Map();
    (articles || []).forEach((article) => {
      if (!grouped.has(article.series_key)) grouped.set(article.series_key, {
        key: article.series_key,
        title: article.series_title,
        category: article.category || "pleiades",
        introduction: String(article.series_introduction || "").split(/\n\s*\n/).filter(Boolean),
        items: [],
        groups: [],
        special: null,
        closing: null,
      });
      const series = grouped.get(article.series_key);
      const item = { ...article, paragraphs: String(article.body || "").split(/\n\s*\n/).filter(Boolean), groupTitle: article.group_title || "" };
      if (article.series_key === "cosmic" && article.title === "İsimler Üstü İşaret: HÛ") series.special = item;
      else if (article.series_key === "cosmic" && article.title === "Varoluşun Esmâsal Akışı") series.closing = item;
      else series.items.push(item);
    });
    grouped.forEach((series) => {
      series.items.sort((a, b) => a.display_order - b.display_order);
      const groupMap = new Map();
      series.items.forEach((item) => {
        const title = item.groupTitle || "Konular";
        if (!groupMap.has(title)) groupMap.set(title, { title, items: [] });
        groupMap.get(title).items.push(item);
      });
      series.groups = [...groupMap.values()];
    });
    return [...grouped.values()];
  }, [articles]);
  const zikrSeries = seriesList.find((series) => series.key === "zikr") || { title: "Esmâ Zikri", introduction: [], items: [], groups: [] };
  const cosmicSeries = seriesList.find((series) => series.key === "cosmic") || { title: "Esmâların Kozmik İşlevleri", introduction: [], items: [], groups: [], special: null, closing: null };
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  const [specialReading, setSpecialReading] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!notificationSeriesKey) return;
    const targetSeries = seriesList.find((series) => series.key === notificationSeriesKey);
    if (!targetSeries) return;
    setSelectedCategory(targetSeries.category);
    setSelectedSeries(targetSeries.key);
    setSelectedItemIndex(null);
    setSpecialReading(null);
    onNotificationTargetConsumed?.();
  }, [notificationSeriesKey, onNotificationTargetConsumed, seriesList]);

  const scrollReadingToTop = () => {
    globalThis.requestAnimationFrame?.(() => scrollToTop?.());
  };
  const openSeries = (seriesKey) => {
    setSelectedSeries(seriesKey);
    setSelectedItemIndex(null);
    setSpecialReading(null);
    scrollReadingToTop();
  };
  const openCategory = (categoryKey) => {
    setSelectedCategory(categoryKey);
    setSelectedSeries(null);
    setSelectedItemIndex(null);
    setSpecialReading(null);
    scrollReadingToTop();
  };
  const returnToCategories = () => {
    setSelectedCategory(null);
    setSelectedSeries(null);
    setSelectedItemIndex(null);
    setSpecialReading(null);
    scrollReadingToTop();
  };
  const returnToSeries = () => {
    setSelectedSeries(null);
    setSelectedItemIndex(null);
    setSpecialReading(null);
    scrollReadingToTop();
  };
  const openItem = (index) => {
    setSelectedItemIndex(index);
    setSpecialReading(null);
    scrollReadingToTop();
  };
  const openSpecialReading = (item) => {
    setSelectedItemIndex(null);
    setSpecialReading(item);
    scrollReadingToTop();
  };
  const returnToItemList = () => {
    setSelectedItemIndex(null);
    setSpecialReading(null);
    scrollReadingToTop();
  };

  const activeSeries = seriesList.find((series) => series.key === selectedSeries) || zikrSeries;
  const currentItem =
    selectedItemIndex === null ? specialReading : activeSeries.items[selectedItemIndex];
  usePortalContentActivity(selectedSeries && currentItem ? {
    active: true,
    content_type: "topic",
    content_id: currentItem.id || `${selectedSeries}:${currentItem.title}`,
    content_title: currentItem.title,
  } : null);
  const isCosmicSeries = selectedSeries === "cosmic";
  const normalizedQuery = normalizeEsmaSearch(searchQuery);
  const filteredGroups = cosmicSeries.groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        normalizeEsmaSearch(item.title).includes(normalizedQuery),
      ),
    }))
    .filter((group) => group.items.length);
  const activeCategory = TOPIC_CATEGORIES.find((category) => category.key === selectedCategory);
  const categorySeries = seriesList.filter((series) => series.category === selectedCategory);

  if (selectedSeries && currentItem) {
    const itemPosition =
      selectedItemIndex === null
        ? currentItem === cosmicSeries.special
          ? "HÛ"
          : "Kapanış"
        : isCosmicSeries
          ? "Esmâ " + (selectedItemIndex + 1) + " / " + cosmicSeries.items.length
          : "Konu " + (selectedItemIndex + 1) + " / " + activeSeries.items.length;
    return (
      <Page title={currentItem.title} description={itemPosition}>
        <article className="border border-pleiades-border bg-white px-4 py-5 sm:px-7 sm:py-7">
          <button
            type="button"
            onClick={returnToItemList}
            className="mb-5 inline-flex min-h-11 items-center gap-2 text-[13px] font-semibold text-pleiades-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue"
          >
            <ArrowLeft size={16} aria-hidden="true" />
            {isCosmicSeries ? "Esmâlara dön" : "Konulara dön"}
          </button>
          <div className="mx-auto max-w-3xl space-y-4 text-[15px] leading-[1.8] text-pleiades-ink sm:text-[16px]">
            {currentItem.paragraphs.map((paragraph, index) => (
              <ArticleParagraph key={currentItem.title + "-" + index} text={paragraph} />
            ))}
          </div>
        </article>
        {selectedItemIndex !== null && (
          <nav aria-label={isCosmicSeries ? "Esmâ gezinme" : "Konu gezinme"} className="mt-3 grid grid-cols-2 gap-2.5">
            <button
              type="button"
              disabled={selectedItemIndex === 0}
              onClick={() => openItem(selectedItemIndex - 1)}
              className="min-h-12 border border-pleiades-border bg-white px-3 text-[13px] font-semibold text-pleiades-ink disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue"
            >
              {isCosmicSeries ? "Önceki Esmâ" : "Önceki konu"}
            </button>
            <button
              type="button"
              disabled={selectedItemIndex === activeSeries.items.length - 1}
              onClick={() => openItem(selectedItemIndex + 1)}
              className="min-h-12 border border-pleiades-blue bg-pleiades-blue px-3 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2"
            >
              {isCosmicSeries ? "Sonraki Esmâ" : "Sonraki konu"}
            </button>
          </nav>
        )}
      </Page>
    );
  }

  if (selectedSeries) {
    return (
      <Page
        title={activeSeries.title}
        description={isCosmicSeries ? "99 Esmâ ve kozmik işlevleri" : `${activeSeries.items.length} konu`}
      >
        <button
          type="button"
          onClick={returnToSeries}
          className="mb-3 inline-flex min-h-11 items-center gap-2 text-[13px] font-semibold text-pleiades-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue"
        >
          <ArrowLeft size={16} aria-hidden="true" />
          {activeCategory?.label || "Kategori"} konularına dön
        </button>
        <section className="border border-pleiades-border bg-white p-4 sm:p-6">
          <details>
            <summary className="min-h-11 cursor-pointer py-3 text-[13px] font-semibold text-pleiades-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue">
              Makale girişini oku
            </summary>
            <div className="space-y-3 pb-2 text-[14px] leading-[1.75] text-pleiades-ink">
              {activeSeries.introduction.map((paragraph, index) => (
                <ArticleParagraph key={"series-introduction-" + index} text={paragraph} />
              ))}
            </div>
          </details>
        </section>

        {isCosmicSeries ? (
          <>
            <button
              type="button"
              onClick={() => openSpecialReading(cosmicSeries.special)}
              className="mt-3 flex min-h-16 w-full items-center gap-3 border border-pleiades-blue/30 bg-pleiades-blue-soft px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue"
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center bg-white text-[14px] font-semibold text-pleiades-blue">HÛ</span>
              <span className="min-w-0 flex-1 text-[14px] font-semibold text-pleiades-ink">
                İsimler Üstü İşaret: HÛ
              </span>
              <ChevronRight size={16} className="shrink-0 text-pleiades-blue" aria-hidden="true" />
            </button>
            <div className="sticky top-0 z-10 mt-3 border border-pleiades-border bg-pleiades-ivory p-2">
              <label className="flex min-h-12 items-center gap-3 border border-pleiades-border bg-white px-3 focus-within:border-pleiades-blue focus-within:ring-1 focus-within:ring-pleiades-blue">
                <Search size={17} className="shrink-0 text-pleiades-blue" aria-hidden="true" />
                <span className="sr-only">Esmâ ara</span>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Esmâ ara..."
                  className="min-w-0 flex-1 bg-transparent text-[16px] text-pleiades-ink outline-none placeholder:text-pleiades-slate"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="grid h-9 w-9 shrink-0 place-items-center text-pleiades-slate"
                    aria-label="Aramayı temizle"
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                )}
              </label>
              <p className="px-1 pt-1.5 text-[11px] text-pleiades-slate">
                {normalizedQuery ? filteredGroups.reduce((total, group) => total + group.items.length, 0) + " sonuç" : "99 Esmâ"}
              </p>
            </div>
            <div className="mt-3 space-y-3">
              {filteredGroups.map((group) => (
                <section key={group.title} className="border border-pleiades-border bg-white">
                  <h2 className="border-b border-pleiades-border bg-pleiades-ivory px-4 py-3 text-[13px] font-semibold text-pleiades-ink">
                    {group.title}
                  </h2>
                  <div className="divide-y divide-pleiades-border">
                    {group.items.map((item) => {
                      const itemIndex = cosmicSeries.items.indexOf(item);
                      return (
                        <button
                          key={item.title}
                          type="button"
                          onClick={() => openItem(itemIndex)}
                          className="flex min-h-14 w-full items-center gap-3 px-3.5 py-3 text-left hover:bg-pleiades-ivory focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pleiades-blue sm:px-4"
                        >
                          <span className="w-7 shrink-0 font-mono text-[11px] text-pleiades-blue">
                            {String(itemIndex + 1).padStart(2, "0")}
                          </span>
                          <span className="min-w-0 flex-1 text-[14px] font-semibold leading-snug text-pleiades-ink">
                            {item.title.replace(/^\d+\.\s*/, "")}
                          </span>
                          <ChevronRight size={16} className="shrink-0 text-pleiades-slate" aria-hidden="true" />
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}
              {!filteredGroups.length && (
                <div className="border border-pleiades-border bg-white px-4 py-8 text-center text-[13px] text-pleiades-slate">
                  Bu adla eşleşen bir Esmâ bulunamadı.
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => openSpecialReading(cosmicSeries.closing)}
              className="mt-3 flex min-h-14 w-full items-center gap-3 border border-pleiades-border bg-white px-4 py-3 text-left hover:border-pleiades-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue"
            >
              <BookOpen size={17} className="shrink-0 text-pleiades-blue" aria-hidden="true" />
              <span className="flex-1 text-[14px] font-semibold">Varoluşun Esmâsal Akışı</span>
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </>
        ) : (
          <div className="mt-3 divide-y divide-pleiades-border border border-pleiades-border bg-white">
            {activeSeries.items.map((topic, index) => (
              <button
                key={topic.title}
                type="button"
                onClick={() => openItem(index)}
                className="flex min-h-14 w-full items-center gap-3 px-3.5 py-3 text-left hover:bg-pleiades-ivory focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pleiades-blue sm:px-4"
              >
                <span className="w-6 shrink-0 font-mono text-[11px] text-pleiades-blue">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="min-w-0 flex-1 text-[14px] font-semibold leading-snug text-pleiades-ink">
                  {topic.title}
                </span>
                <ChevronRight size={16} className="shrink-0 text-pleiades-slate" aria-hidden="true" />
              </button>
            ))}
          </div>
        )}
      </Page>
    );
  }

  if (selectedCategory) {
    return (
      <Page title={activeCategory?.label || "Konular"} description="Okumak istediğiniz ana konuyu seçin.">
        <button
          type="button"
          onClick={returnToCategories}
          className="mb-3 inline-flex min-h-11 items-center gap-2 text-[13px] font-semibold text-pleiades-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue"
        >
          <ArrowLeft size={16} aria-hidden="true" />
          Kategorilere dön
        </button>
        <div className="grid gap-3">
          {categorySeries.map((series) => <button key={series.key} type="button" onClick={() => openSeries(series.key)} className="flex min-h-24 items-center gap-4 border border-pleiades-border bg-white p-4 text-left hover:border-pleiades-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue sm:p-5"><div className="grid h-12 w-12 shrink-0 place-items-center bg-pleiades-ivory text-pleiades-blue"><BookOpen size={20} aria-hidden="true" /></div><span className="min-w-0 flex-1"><span className="block text-[11px] uppercase tracking-[.14em] text-pleiades-blue">{series.items.length + (series.special ? 1 : 0) + (series.closing ? 1 : 0)} alt başlık</span><span className="mt-1 block text-[16px] font-semibold leading-snug text-pleiades-ink">{series.title}</span></span><ChevronRight size={17} className="shrink-0 text-pleiades-slate" aria-hidden="true" /></button>)}
          {!categorySeries.length && <section className="border border-dashed border-pleiades-border bg-white p-8 text-center text-[12px] text-pleiades-muted">Bu kategoride size açık bir konu henüz yayınlanmadı.</section>}
        </div>
      </Page>
    );
  }

  return (
    <Page title="Konular" description="Önce okumak istediğiniz kategoriyi seçin.">
      <div className="grid gap-3">
        {TOPIC_CATEGORIES.map((category) => {
          const topicCount = seriesList.filter((series) => series.category === category.key).length;
          return <button key={category.key} type="button" onClick={() => openCategory(category.key)} className="flex min-h-24 items-center gap-4 border border-pleiades-border bg-white p-4 text-left hover:border-pleiades-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue sm:p-5"><div className="grid h-12 w-12 shrink-0 place-items-center bg-pleiades-ivory text-pleiades-blue"><BookOpen size={20} aria-hidden="true" /></div><span className="min-w-0 flex-1"><span className="block text-[11px] uppercase tracking-[.14em] text-pleiades-blue">{topicCount} ana konu</span><span className="mt-1 block text-[16px] font-semibold leading-snug text-pleiades-ink">{category.label}</span><span className="mt-1 block text-[12px] leading-relaxed text-pleiades-slate">{category.description}</span></span><ChevronRight size={17} className="shrink-0 text-pleiades-slate" aria-hidden="true" /></button>;
        })}
        {!seriesList.length && <section className="border border-dashed border-pleiades-border bg-white p-8 text-center text-[12px] text-pleiades-muted">Size açık bir konu henüz yayınlanmadı.</section>}
      </div>
    </Page>
  );
}

function PractitionerSection({ data, go, portalModules }) {
  const title = practitionerTitles[data.practitioner?.approved_level] || "Pleiades Uygulayıcısı";
  return (
    <Page
      title="Uygulayıcı Alanım"
      description="Onaylanmış seviyenize açık yayınlar, programlar ve meditasyon içerikleri."
    >
      <section className="bg-pleiades-ink text-white border border-pleiades-ink p-4 sm:p-6">
        <div className="grid md:grid-cols-[minmax(0,1fr)_auto] gap-6 items-end">
          <div>
            <div className="text-[11px] uppercase tracking-[.15em] text-pleiades-gold">
              Doğrulanmış uygulayıcı alanı
            </div>
            <h2 className="text-[24px] sm:text-[28px] font-semibold mt-3">{title}</h2>
            <p className="text-[14px] leading-[1.65] text-white/70 mt-3 max-w-2xl">
              Seviyenize özel çalışmalar, eğitim içerikleri ve yaklaşan programlar bu alanda bir
              araya gelir.
            </p>
          </div>
          {portalModules.programs_enabled && (
            <button
              type="button"
              onClick={() => go("programs")}
              className="min-h-12 px-5 bg-white text-pleiades-ink text-[13px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-gold"
            >
              Programları gör
            </button>
          )}
        </div>
      </section>
      {(portalModules.programs_enabled || portalModules.audio_enabled) && (
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          {portalModules.programs_enabled && (
            <ActionCard
              icon={CalendarDays}
              eyebrow="Takvim"
              title="Programlar"
              text="Yaklaşan eğitim, kamp ve çevrim içi buluşmaları takip edin."
              action="Programları gör"
              onClick={() => go("programs")}
            />
          )}
          {portalModules.audio_enabled && (
            <ActionCard
              icon={Headphones}
              eyebrow="Kütüphane"
              title="Meditasyonlar"
              text="Size açık meditasyon anlatımlarına ve ses içeriklerine ulaşın."
              action="Dinlemeye başla"
              onClick={() => go("audio")}
            />
          )}
        </div>
      )}
    </Page>
  );
}

function VideoSection({ items }) {
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(items?.[0]?.id || "");
  const [playingId, setPlayingId] = useState("");
  const playerRef = useRef(null);
  const autoplayTimerRef = useRef(null);
  useEffect(() => () => globalThis.clearTimeout(autoplayTimerRef.current), []);
  useEffect(() => {
    if (!items?.length) {
      setSelectedId("");
      setPlayingId("");
      return;
    }
    if (!items.some((item) => item.id === selectedId)) {
      setSelectedId(items[0].id);
      setPlayingId("");
    }
  }, [items, selectedId]);
  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("tr-TR");
    if (!normalized) return items || [];
    return (items || []).filter((item) =>
      `${item.title || ""} ${item.description || ""}`.toLocaleLowerCase("tr-TR").includes(normalized),
    );
  }, [items, query]);
  const selected = (items || []).find((item) => item.id === selectedId) || items?.[0];
  usePortalContentActivity(selected && playingId === selected.video_id ? {
    active: true,
    content_type: "video",
    content_id: selected.id || selected.video_id,
    content_title: selected.title,
  } : null);

  const selectAndPlay = (item) => {
    globalThis.clearTimeout(autoplayTimerRef.current);
    setSelectedId(item.id);
    setPlayingId("");
    globalThis.requestAnimationFrame?.(() => {
      const reducedMotion = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
      playerRef.current?.scrollIntoView({
        behavior: reducedMotion ? "auto" : "smooth",
        block: "start",
      });
      autoplayTimerRef.current = globalThis.setTimeout(
        () => setPlayingId(item.video_id),
        reducedMotion ? 0 : 380,
      );
    });
  };

  if (!items?.length) {
    return (
      <Page title="Videolar" description="Pleiades röportajları ve uzman görüşleri.">
        <section className="border border-dashed border-pleiades-border bg-white px-5 py-10 text-center">
          <Video className="mx-auto text-pleiades-muted" size={24} aria-hidden="true" />
          <h2 className="mt-3 text-[14px] font-semibold">Henüz video yayınlanmadı</h2>
          <p className="mt-1 text-[11px] text-pleiades-slate">Yeni röportajlar yayınlandığında burada görünecek.</p>
        </section>
      </Page>
    );
  }

  return (
    <Page title="Röportajlar ve Söyleşiler" description="Pleiades sistemiyle ilgili röportajları ve uzman görüşlerini tek listede izleyin.">
      <div className="grid items-start gap-3 lg:grid-cols-[minmax(0,1.45fr)_minmax(300px,.75fr)]">
        <section ref={playerRef} className="scroll-mt-2 overflow-hidden border border-pleiades-border bg-white">
          <div className="relative aspect-video bg-pleiades-ink">
            {playingId === selected?.video_id ? (
              <iframe
                key={playingId}
                title={selected.title}
                src={`https://www.youtube-nocookie.com/embed/${selected.video_id}?rel=0&modestbranding=1&autoplay=1`}
                className="absolute inset-0 h-full w-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            ) : (
              <button
                type="button"
                onClick={() => setPlayingId(selected.video_id)}
                className="group absolute inset-0 h-full w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pleiades-gold"
                aria-label={`${selected.title} videosunu oynat`}
              >
                <img src={selected.thumbnail_url} alt="" className="h-full w-full object-cover opacity-85 transition-opacity group-hover:opacity-70 motion-reduce:transition-none" />
                <span className="absolute inset-0 grid place-items-center bg-pleiades-ink/15">
                  <span className="grid h-14 w-14 place-items-center bg-white text-pleiades-blue shadow-lg"><Play size={24} fill="currentColor" aria-hidden="true" /></span>
                </span>
              </button>
            )}
          </div>
          <div className="p-4 sm:p-5">
            <div className="text-[10px] uppercase tracking-[.14em] text-pleiades-blue">Seçili video</div>
            <h1 className="mt-2 font-editorial text-[20px] leading-tight sm:text-[24px]">{selected.title}</h1>
            {selected.description && <p className="mt-2 line-clamp-3 text-[12px] leading-relaxed text-pleiades-slate">{selected.description}</p>}
          </div>
        </section>

        <section className="border border-pleiades-border bg-white lg:max-h-[min(72vh,690px)] lg:overflow-hidden">
          <div className="border-b border-pleiades-border p-3">
            <label className="relative block">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-pleiades-muted" aria-hidden="true" />
              <span className="sr-only">Videolarda ara</span>
              <input value={query} onChange={(event) => setQuery(event.target.value)} className="input min-h-11 pl-9" placeholder="Videolarda ara" />
            </label>
          </div>
          <div className="divide-y divide-pleiades-border lg:max-h-[calc(min(72vh,690px)-69px)] lg:overflow-y-auto">
            {filtered.map((item) => {
              const active = item.id === selected?.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => selectAndPlay(item)}
                  aria-label={`${item.title} videosunu seç`}
                  aria-current={active ? "true" : undefined}
                  className={`flex min-h-[76px] w-full items-center gap-3 p-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pleiades-blue ${active ? "bg-pleiades-blue-soft" : "bg-white hover:bg-pleiades-ivory"}`}
                >
                  <span className="relative aspect-video w-24 shrink-0 overflow-hidden bg-pleiades-ink"><img src={item.thumbnail_url} alt="" loading="lazy" className="h-full w-full object-cover" /><span className="absolute inset-0 grid place-items-center bg-pleiades-ink/10"><Play size={15} className="text-white" fill="currentColor" /></span></span>
                  <span className="min-w-0 flex-1"><strong className="line-clamp-2 text-[12px] leading-snug text-pleiades-ink">{item.title}</strong><span className="mt-1 block text-[9px] uppercase tracking-wider text-pleiades-muted">Pleiades Meditasyon Akademisi</span></span>
                </button>
              );
            })}
            {!filtered.length && <p className="p-8 text-center text-[11px] text-pleiades-slate">Aramanızla eşleşen video bulunamadı.</p>}
          </div>
        </section>
      </div>
    </Page>
  );
}

function ProgramsSection({ items }) {
  const [activeCategory, setActiveCategory] = useState("camp");
  const [expandedProgramId, setExpandedProgramId] = useState(null);
  const trackedEvent = items.find((item) => item.id === expandedProgramId);
  usePortalContentActivity(trackedEvent ? {
    active: true,
    content_type: "event",
    content_id: trackedEvent.id,
    content_title: trackedEvent.title,
  } : null);
  const groups = [
    { key: "camp", title: "Kamplar", text: "Konaklamalı kamp buluşmaları" },
    { key: "training", title: "Eğitimler", text: "Yüz yüze eğitim programları" },
    { key: "zoom", title: "Zoom", text: "Çevrim içi program ve oturumlar" },
  ];
  const activeGroup = groups.find((group) => group.key === activeCategory) || groups[0];
  const activeItems = items.filter((item) => item.program_type === activeCategory);
  return (
    <Page
      title="Etkinlikler"
      description="Yaklaşan kamp, eğitim ve çevrim içi buluşmalarınızı buradan takip edin."
    >
      <div className="grid grid-cols-3 gap-2 sm:gap-3" role="tablist" aria-label="Etkinlik kategorileri">
        {groups.map((group) => {
          const active = group.key === activeCategory;
          const count = items.filter((item) => item.program_type === group.key).length;
          return <button key={group.key} type="button" role="tab" aria-selected={active} onClick={() => { setActiveCategory(group.key); setExpandedProgramId(null); }} className={`min-h-[76px] border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue ${active ? "border-pleiades-blue bg-pleiades-blue-soft" : "border-pleiades-border bg-white hover:border-pleiades-blue"}`}>
            <span className="flex items-center justify-between gap-2"><strong className="text-[12px] text-pleiades-ink sm:text-[13px]">{group.title}</strong><span className="text-[10px] tabular-nums text-pleiades-muted">{count}</span></span>
            <span className="mt-1 hidden text-[10px] leading-snug text-pleiades-slate sm:block">{group.text}</span>
          </button>;
        })}
      </div>
      <section className="mt-3 border border-pleiades-border bg-white" role="tabpanel">
        <div className="flex items-center gap-2 border-b border-pleiades-border px-4 py-3"><CalendarDays size={15} aria-hidden="true" /><h2 className="text-[13px] font-semibold">{activeGroup.title}</h2></div>
        <div className="space-y-3 p-3 sm:p-4">
          {activeItems.length ? activeItems.map((item) => <ProgramEntry key={item.id} item={item} expanded={expandedProgramId === item.id} onToggle={() => setExpandedProgramId((current) => current === item.id ? null : item.id)} />) : <div className="px-4 py-10 text-center text-[11px] text-pleiades-muted">Henüz etkinlik bulunmuyor. Bu kategoriye yeni bir etkinlik eklendiğinde burada görünecek.</div>}
        </div>
      </section>
    </Page>
  );
}

const SESSION_WHATSAPP_PHONE = "905327991130";

function sessionWhatsAppUrl() {
  const phone = SESSION_WHATSAPP_PHONE;
  if (!phone) return "";
  const message = encodeURIComponent(
    "Merhaba, danışmanım tarafından Pleiades programımın 7 günlük ara döneminde tanımlanan uzaktan seans hakkım için randevu almak istiyorum.",
  );
  return `https://wa.me/${phone}?text=${message}`;
}

function RemoteSessionCard({ remoteSession, compact = false }) {
  if (!remoteSession) return null;
  const active = remoteSession.status === "active";
  const statusText = active
    ? "Kullanılabilir"
    : remoteSession.status === "upcoming"
      ? "Yaklaşan ara dönem için tanımlandı"
      : "Ara dönem tamamlandı";
  const canRequestAppointment = active || remoteSession.status === "upcoming";
  const whatsappUrl = sessionWhatsAppUrl();
  return (
    <section className={`border border-pleiades-gold/50 bg-white ${compact ? "p-4" : "p-5"}`}>
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center bg-pleiades-blue-soft text-pleiades-blue">
          <CalendarCheck2 size={18} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-pleiades-blue">
            Uzaktan seans hakkınız
          </div>
          <h2 className="mt-1 text-[14px] font-semibold">
            Bu 7 günlük ara dönem için {remoteSession.days} gün uzaktan seans
          </h2>
          <p className="mt-2 text-[12px] leading-[1.65] text-pleiades-slate">
            Tarih ve saat panelde belirlenmez. Randevunuzu oluşturmak için danışmanınızla görüşün.
          </p>
          <div className="mt-3 text-[11px] font-medium text-pleiades-blue">Durum: {statusText}</div>
          {canRequestAppointment && whatsappUrl && (
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex min-h-11 items-center justify-center gap-2 bg-[#1f7a52] px-4 text-[12px] font-semibold text-white"
            >
              <MessageCircle size={16} aria-hidden="true" /> WhatsApp ile randevu al
            </a>
          )}
          {canRequestAppointment && !whatsappUrl && (
            <p className="mt-4 text-[11px] text-pleiades-muted">
              İletişim bilgisi için danışmanınıza başvurun.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function remoteSessionDate(value) {
  const parsed = new Date(value);
  if (!value || Number.isNaN(parsed.getTime())) return "Tarih henüz belirlenmedi";
  return new Intl.DateTimeFormat("tr-TR", {
    timeZone: "Europe/Istanbul",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
}

function countdownText(milliseconds) {
  const seconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
}

export function anchorRemoteSessionClock(items, receivedAt = Date.now()) {
  return items.map((item) => {
    const serverNow = new Date(item.server_now).getTime();
    return {
      ...item,
      _clock_offset_ms: Number.isFinite(serverNow) ? serverNow - receivedAt : 0,
    };
  });
}

function RemoteSessionAnnouncement({ offer, onAttendanceChange }) {
  const [open, setOpen] = useState(false);
  usePortalContentActivity(open ? {
    active: true,
    content_type: "remote_session",
    content_id: offer.id,
    content_title: offer.title,
  } : null);
  const live = offer.session_status === "live";
  const publicSession = !offer.audience || offer.audience === "all";
  const audienceText = publicSession
    ? "Genel"
    : offer.audience === "client"
      ? "Size özel"
      : "Grubunuza özel";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`group flex min-h-14 w-full items-center gap-3 border px-3.5 py-2.5 text-left shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2 ${publicSession ? "border-pleiades-blue/30 bg-white" : "border-[#c9a96a] bg-[#fffdf8]"}`}
        aria-label={`${offer.title} uzaktan seans ayrıntılarını aç`}
      >
        <span className="relative flex h-3 w-3 shrink-0" aria-hidden="true">
          <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-45 motion-reduce:animate-none ${live ? "bg-emerald-500" : "bg-pleiades-blue"}`} />
          <span className={`relative inline-flex h-3 w-3 rounded-full ${live ? "bg-emerald-600" : "bg-pleiades-blue"}`} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[.13em] text-pleiades-blue">
            <span>{live ? "Seans başladı" : "Uzaktan seans"}</span>
            <span aria-hidden="true">·</span>
            <span>{audienceText}</span>
          </span>
          <span className="mt-0.5 block truncate text-[13px] font-semibold text-pleiades-ink">
            {offer.title}
          </span>
          <span className="mt-0.5 block truncate text-[10px] text-pleiades-slate">
            {remoteSessionDate(offer.starts_at)} · Rehber: Şeref Tolga Kuralay
          </span>
        </span>
        <ChevronRight size={17} className="shrink-0 text-pleiades-blue transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92dvh] w-[calc(100%-1.25rem)] max-w-2xl overflow-y-auto border-pleiades-border bg-pleiades-ivory p-3 sm:p-5">
          <DialogHeader className="sr-only">
            <DialogTitle>{offer.title}</DialogTitle>
            <DialogDescription>Uzaktan seans ayrıntıları ve katılım bilgileri</DialogDescription>
          </DialogHeader>
          <RemoteSessionOfferCard offer={offer} onAttendanceChange={onAttendanceChange} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function RemoteSessionOfferCard({ offer, onAttendanceChange }) {
  const initialOffset = useMemo(
    () => {
      const anchoredOffset = Number(offer._clock_offset_ms);
      if (Number.isFinite(anchoredOffset)) return anchoredOffset;
      return new Date(offer.server_now || Date.now()).getTime() - Date.now();
    },
    [offer._clock_offset_ms, offer.server_now],
  );
  const [tick, setTick] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const scheduled = Boolean(offer.starts_at);
  const startsAt = new Date(offer.starts_at).getTime();
  const endsAt = new Date(offer.ends_at || startsAt + Number(offer.duration_minutes || 30) * 60000).getTime();
  const correctedNow = tick + initialOffset;
  const remaining = startsAt - correctedNow;
  const status = !scheduled ? "unscheduled" : correctedNow < startsAt ? "upcoming" : correctedNow < endsAt ? "live" : "completed";
  const countdownActive = status === "upcoming" && remaining <= 5 * 60 * 1000;
  const attending = offer.attendance_status === "attending";
  const publicSession = !offer.audience || offer.audience === "all";
  const privateSession = offer.audience === "client";
  const audienceText = publicSession
    ? "Genel katılıma açık"
    : privateSession
      ? "Size özel seans"
      : "Grubunuza özel seans";
  const effectivePosition = privateSession && ["standing", "sitting", "lying"].includes(offer.position)
    ? offer.position
    : "standing";
  const positionText = {
    standing: "Ayakta",
    sitting: "Oturarak",
    lying: "Uzanarak",
  }[effectivePosition];

  useEffect(() => {
    const interval = countdownActive || status === "live" ? 1000 : 30000;
    const timer = window.setInterval(() => setTick(Date.now()), interval);
    return () => window.clearInterval(timer);
  }, [countdownActive, status]);

  const changeAttendance = async (next) => {
    if (busy || status !== "upcoming") return;
    setBusy(true);
    try {
      await onAttendanceChange(offer.id, next);
      toast.success(next ? "Katılımınız alındı." : "Katılımınız iptal edildi.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className={`border ${publicSession ? "border-pleiades-border bg-white" : "border-[#c9a96a] bg-[#fffdf8]"}`}>
      <div className="p-4 sm:p-5">
        <div className={`mb-3 inline-flex min-h-7 items-center border px-2.5 text-[10px] font-semibold uppercase tracking-[.1em] ${publicSession ? "border-pleiades-blue/25 bg-pleiades-blue-soft text-pleiades-blue" : "border-[#c9a96a] bg-[#f6edda] text-[#785b27]"}`}>
          {audienceText}
        </div>
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center bg-pleiades-blue-soft text-pleiades-blue">
            <CalendarCheck2 size={18} aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[.12em] text-pleiades-blue">
              {status === "live" ? "Canlı uzaktan seans" : status === "completed" ? "Tamamlanan seans" : status === "unscheduled" ? "Uzaktan seans duyurusu" : "Yaklaşan uzaktan seans"}
            </div>
            <h2 className="mt-1 text-[16px] font-semibold leading-tight">{offer.title}</h2>
            <p className="mt-1 text-[11px] text-pleiades-slate">
              {remoteSessionDate(offer.starts_at)}{scheduled ? ` · ${offer.duration_minutes} dakika` : ""}
            </p>
          </div>
        </div>

        {countdownActive && (
          <div role="timer" className="mt-4 border border-pleiades-border-strong bg-pleiades-ivory p-3 text-center">
            <div className="text-[10px] uppercase tracking-[.12em] text-pleiades-blue">Seansın başlamasına</div>
            <div className="mt-1 font-mono text-[24px] font-semibold text-pleiades-ink">{countdownText(remaining)}</div>
          </div>
        )}
        {status === "live" && (
          <div role="status" className="mt-4 flex items-center gap-3 border border-pleiades-blue bg-pleiades-blue px-3 py-3 text-white">
            <Radio size={18} className="shrink-0" aria-hidden="true" />
            <strong className="text-[13px]">Seans şu an başladı</strong>
          </div>
        )}
        {status === "completed" && (
          <div role="status" className="mt-4 border border-pleiades-border bg-pleiades-ivory px-3 py-2.5 text-[12px] text-pleiades-slate">
            Bu seans tamamlandı.
          </div>
        )}

        <p className="mt-4 text-[12px] leading-relaxed text-pleiades-slate">{offer.summary}</p>
        {(offer.topic || offer.intention || offer.position || offer.session_code || offer.repeat_count) && (
          <div className="mt-4 grid gap-px border border-pleiades-border bg-pleiades-border sm:grid-cols-2">
            <SessionDetail icon={User} label="Seans rehberi" value="Şeref Tolga Kuralay" />
            <SessionDetail icon={BookOpenText} label="Seans konusu" value={offer.topic} />
            <SessionDetail icon={Compass} label="Seans niyeti" value={offer.intention} />
            <SessionDetail icon={Compass} label="Uygulama pozisyonu" value={positionText} />
            <SessionDetail icon={Hash} label="Okunacak kod" value={offer.session_code} mono />
            <SessionDetail icon={Users} label="Tekrar sayısı" value={offer.repeat_count ? `${offer.repeat_count} kez` : "—"} />
            <SessionDetail icon={CalendarDays} label="Seans süresi" value={scheduled ? `${offer.duration_minutes} dakika` : "—"} />
          </div>
        )}
        {(offer.description || offer.details) && <div className="mt-4 border border-pleiades-border bg-pleiades-ivory p-3"><div className="text-[10px] text-pleiades-slate">Seans açıklaması</div><p className="mt-1 whitespace-pre-line text-[12px] leading-relaxed text-pleiades-ink">{offer.description || offer.details}</p></div>}

        <div className="mt-4 border border-pleiades-border-strong bg-white p-3.5">
          <div className="text-[10px] font-semibold uppercase tracking-[.12em] text-pleiades-blue">
            Pozisyon ve katılım kılavuzu
          </div>
          <ol className="mt-2 space-y-1.5 text-[12px] leading-relaxed text-pleiades-ink">
            <li>1. {effectivePosition === "standing" ? "Ayakta durun; ayaklarınızı omuz hizasında açın." : effectivePosition === "sitting" ? "Rahat ve dengeli bir oturuş pozisyonuna geçin." : "Rahat ve güvenli bir uzanma pozisyonuna geçin."}</li>
            <li>2. Gözlerinizi kapatın.</li>
            <li>3. Kod verilmişse kodu yavaşça tekrar edin.</li>
            <li>4. Kendinizi ve bedeninizi serbest bırakın; bedendeki hisleri ve süreci izleyin.</li>
            <li>5. İstemsiz bir bedensel tepki oluşursa, kendinizi güvende hissettiğiniz sürece bedene izin verin.</li>
          </ol>
        </div>

        <div className="mt-3 border border-pleiades-blue/25 bg-pleiades-blue-soft p-3 text-[11px] leading-relaxed text-pleiades-ink">
          <strong className="block text-[12px]">Seans rehberi: Şeref Tolga Kuralay</strong>
          Belirtilen başlangıç saatinde seans alanı Şeref Tolga Kuralay tarafından açılır ve uzaktan seans başlatılır.
        </div>

        {status === "upcoming" && !attending && (
          <button type="button" disabled={busy} onClick={() => changeAttendance(true)} className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 bg-pleiades-blue px-4 text-[13px] font-semibold text-white disabled:opacity-60">
            <CheckCircle2 size={17} aria-hidden="true" /> {busy ? "Kaydediliyor…" : "Katılacağım"}
          </button>
        )}
        {status === "upcoming" && attending && (
          <div className="mt-4 border border-pleiades-border bg-pleiades-ivory p-3">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-pleiades-ink"><CheckCircle2 size={17} className="text-pleiades-blue" /> Katılımınız alındı</div>
            <p className="mt-1 text-[11px] leading-relaxed text-pleiades-slate">Başlangıçtan 1 saat önce kayıtlı e-posta adresinize hatırlatma gönderilecek.</p>
            <button type="button" disabled={busy} onClick={() => changeAttendance(false)} className="mt-2 min-h-11 text-[11px] font-semibold text-pleiades-blue underline underline-offset-4 disabled:opacity-60">
              {busy ? "Güncelleniyor…" : "Katılımımı iptal et"}
            </button>
          </div>
        )}
        {status === "unscheduled" && (
          <a href={`https://wa.me/${SESSION_WHATSAPP_PHONE}?text=${encodeURIComponent(offer.whatsapp_message || `Merhaba, “${offer.title}” uzaktan seans duyurusu hakkında bilgi almak istiyorum.`)}`} target="_blank" rel="noreferrer" className="mt-4 inline-flex min-h-11 items-center justify-center gap-2 bg-[#1f7a52] px-4 text-[12px] font-semibold text-white">
            <MessageCircle size={16} /> WhatsApp’tan bilgi al
          </a>
        )}
      </div>
    </article>
  );
}

function SessionDetail({ icon: Icon, label, value, mono = false }) {
  return (
    <div className="flex min-w-0 items-start gap-2 bg-white p-3">
      <Icon size={15} className="mt-0.5 shrink-0 text-pleiades-blue" aria-hidden="true" />
      <div className="min-w-0"><div className="text-[10px] text-pleiades-slate">{label}</div><div className={`mt-0.5 break-words text-[12px] font-medium ${mono ? "font-mono" : ""}`}>{value || "—"}</div></div>
    </div>
  );
}

function SessionRightsSection({ remoteSession, contactPhone, offers = [] }) {
  const completedOffers = offers.filter((offer) => offer.session_status === "completed");
  const [archiveOpen, setArchiveOpen] = useState(false);
  return (
    <Page
      title="Uzaktan Seanslar"
      description="Size açık duyuruları ve kişisel seans haklarınızı aynı alanda takip edin."
    >
      {completedOffers.length > 0 && (
        <section className="mb-4 border border-pleiades-border bg-white">
          <button
            type="button"
            aria-expanded={archiveOpen}
            onClick={() => setArchiveOpen((current) => !current)}
            className="flex min-h-12 w-full items-center justify-between gap-3 px-4 py-3 text-left"
          >
            <span>
              <span className="block text-[11px] font-semibold uppercase tracking-[.12em] text-pleiades-blue">
                Uzaktan seans arşivi
              </span>
              <span className="mt-0.5 block text-[11px] text-pleiades-slate">
                {completedOffers.length} tamamlanan seans
              </span>
            </span>
            <ChevronDown
              size={17}
              aria-hidden="true"
              className={`shrink-0 text-pleiades-blue transition-transform ${archiveOpen ? "rotate-180" : ""}`}
            />
          </button>
          {archiveOpen && (
            <div className="divide-y divide-pleiades-border border-t border-pleiades-border">
              {completedOffers.map((offer) => (
                <div key={offer.id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <strong className="min-w-0 truncate text-[12px] font-medium text-pleiades-ink">
                    {offer.topic || offer.title}
                  </strong>
                  <time className="shrink-0 text-[11px] text-pleiades-slate" dateTime={offer.starts_at}>
                    {remoteSessionDate(offer.starts_at)}
                  </time>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
      <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[.12em] text-pleiades-blue">Kişisel seans haklarım</h2>
      {remoteSession ? (
        <RemoteSessionCard remoteSession={remoteSession} contactPhone={contactPhone} />
      ) : (
        <section className="border border-dashed border-pleiades-border bg-white p-8 text-center">
          <CalendarCheck2 className="mx-auto text-pleiades-muted" aria-hidden="true" />
          <h2 className="mt-3 text-[13px] font-semibold">Tanımlanmış seans hakkınız bulunmuyor</h2>
          <p className="mt-2 text-[11px] text-pleiades-muted">
            Danışmanınız bir ara dönem için uzaktan seans tanımladığında burada görünecek.
          </p>
        </section>
      )}
    </Page>
  );
}

function UsageGuideSection() {
  const sections = [
    {
      id: "kilavuz-kart",
      title: "Çalışma kartını nasıl kullanırım?",
      content: (
        <ol>
          <li>Bugünkü Çalışmam sayfasındaki kartı açın.</li>
          <li>Önce esmâ veya mantra, ardından meditasyon adımını uygulayın.</li>
          <li>Karttaki süre, tekrar, pozisyon ve kod yalnız o çalışmaya aittir.</li>
          <li>Uygulamadan sonra “Yaptım” veya “Yapmadım” seçimini kaydedin.</li>
        </ol>
      ),
    },
    {
      id: "kilavuz-esma",
      title: "Esmâ veya mantra nasıl uygulanır?",
      content: (
        <p>
          Sessiz ve mümkünse loş bir alan seçin. Kartta belirtilen pozisyona geçin,
          gözlerinizi kapatın ve verilen esmâ veya mantrayı kartta yazan süre boyunca,
          belirtilen aralıklarla sesli tekrar edin. Başka bir kişiye ait çalışmayı kullanmayın.
        </p>
      ),
    },
    {
      id: "kilavuz-meditasyon-kodu",
      title: "Meditasyon kodu nasıl kullanılır?",
      content: (
        <p>
          Kodu mümkünse uygulamadan önce ezberleyin. Kartta belirtilen tekrar sayısı ve
          aralığa göre sesli söyleyin. Ardından kartın gösterdiği meditasyon pozisyonuna geçin.
          Kod yalnız bulunduğu çalışmaya aittir.
        </p>
      ),
    },
    {
      id: "kilavuz-meditasyon",
      title: "Meditasyon sırasında ne yapmalıyım?",
      content: (
        <p>
          Gözlerinizi kapatın, bedeninizi serbest bırakın ve süreci zorlamadan gözlemleyin.
          Belirgin bir şey hissetmek zorunlu değildir. Kartta verilen sürenin tamamlanması
          önerilir; devam edemiyorsanız çalışmayı bırakıp dinlenebilirsiniz.
        </p>
      ),
    },
    {
      id: "kilavuz-yapilmadi",
      title: "Çalışmayı yapamazsam ne olur?",
      content: (
        <p>
          “Yapmadım” seçildiğinde aynı çalışma açık kalır ve yeni çalışma gelmez. Çalışmayı
          yaptığınızda “Yaptım” seçimini kullanın. Deneyim alanı açıldığında yaşadıklarınızı
          olduğu gibi yazın; hiçbir şey hissetmediyseniz bunu da belirtebilirsiniz.
        </p>
      ),
    },
  ];
  const activeGuideId = globalThis.location?.hash?.slice(1) || "kilavuz-kart";

  useEffect(() => {
    const target = globalThis.document?.getElementById(activeGuideId);
    if (!target) return undefined;
    const frame = globalThis.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => globalThis.cancelAnimationFrame(frame);
  }, [activeGuideId]);

  return (
    <Page
      title="Kullanım Kılavuzu"
      description="Çalışma kartlarını, esmâ veya mantra uygulamasını ve meditasyon adımlarını doğru sırayla kullanın."
    >
      <section className="grid gap-2" aria-label="Kullanım kılavuzu başlıkları">
        {sections.map((item) => (
          <details
            id={item.id}
            key={item.id}
            className="group scroll-mt-20 border border-pleiades-border bg-white"
            open={item.id === activeGuideId}
          >
            <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-[13px] font-semibold text-pleiades-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pleiades-blue">
              <span>{item.title}</span>
              <ChevronDown size={17} className="shrink-0 text-pleiades-blue transition-transform group-open:rotate-180" aria-hidden="true" />
            </summary>
            <div className="border-t border-pleiades-border px-4 py-3 text-[12px] leading-[1.65] text-pleiades-slate [&_ol]:grid [&_ol]:gap-1.5 [&_ol]:pl-5 [&_ol]:list-decimal [&_p]:m-0">
              {item.content}
            </div>
          </details>
        ))}
      </section>
    </Page>
  );
}

function ExperienceJournalSection({ program, sensitiveConsentRequired }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [reviewed, setReviewed] = useState(false);
  const [sensitiveConsent, setSensitiveConsent] = useState(false);
  const [consentRequired, setConsentRequired] = useState(sensitiveConsentRequired);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const requestIdRef = useRef(globalThis.crypto?.randomUUID?.() || `journal-${Date.now()}`);

  useEffect(() => setConsentRequired(sensitiveConsentRequired), [sensitiveConsentRequired]);
  useEffect(() => {
    let active = true;
    if (!program?.id) {
      setItems([]);
      setLoading(false);
      return () => { active = false; };
    }
    setLoading(true);
    api.get(`/client-portal/daily-programs/${program.id}/experiences`)
      .then(({ data: result }) => {
        if (active) setItems(result?.items || []);
      })
      .catch((requestError) => {
        if (active) setFormError(formatApiError(requestError));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [program?.id]);

  const submit = async (event) => {
    event.preventDefault();
    const body = text.trim();
    if (body.length < 5) {
      setFormError("Deneyiminizi en az 5 karakterle yazın.");
      return;
    }
    if (!reviewed) {
      setFormError("Kaydetmeden önce metninizi gözden geçirdiğinizi onaylayın.");
      globalThis.document?.getElementById("journal-reviewed")?.focus();
      return;
    }
    if (consentRequired && !sensitiveConsent) {
      setFormError("İlk kayıtta hassas içerik onayını işaretleyin.");
      globalThis.document?.getElementById("journal-sensitive-consent")?.focus();
      return;
    }
    if (!program?.id || saving) return;
    setSaving(true);
    setFormError("");
    try {
      const { data: created } = await api.post(
        `/client-portal/daily-programs/${program.id}/experience`,
        {
          text: body,
          sensitive_data_consent: sensitiveConsent,
          request_id: requestIdRef.current,
        },
      );
      setItems((current) => [created, ...current.filter((item) => item.id !== created.id)]);
      setText("");
      setReviewed(false);
      setSensitiveConsent(false);
      setConsentRequired(false);
      requestIdRef.current = globalThis.crypto?.randomUUID?.() || `journal-${Date.now()}`;
      toast.success("Deneyiminiz günlüğünüze kaydedildi. İsterseniz yeni bir kayıt daha yazabilirsiniz.");
    } catch (requestError) {
      setFormError(formatApiError(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pleiades-mobile-type-plus-2 space-y-4">
      <header className="stitch-today-editorial-header">
        <BookOpenText size={18} aria-hidden="true" />
        <div>
          <span>Kişisel kayıt alanınız</span>
          <h1>Deneyim Günlüğüm</h1>
          <p>Çalışmalarınızdan sonra veya dilediğiniz zaman yeni bir deneyim yazabilirsiniz.</p>
        </div>
      </header>

      <form onSubmit={submit} className="journal-editor border border-pleiades-border bg-white p-4 sm:p-6">
        <div className="text-[10px] uppercase tracking-[.14em] text-pleiades-blue">Yeni kayıt</div>
        <label htmlFor="journal-entry" className="mt-2 block text-[18px] font-medium text-pleiades-ink">
          Deneyiminizi yazın
        </label>
        <p className="mt-1 text-[13px] leading-6 text-pleiades-slate">
          Buraya istediğiniz kadar ayrı kayıt ekleyebilirsiniz. Her kayıt tarih ve saatiyle saklanır.
        </p>
        <textarea
          id="journal-entry"
          value={text}
          onChange={(event) => setText(event.target.value)}
          rows={8}
          maxLength={5000}
          className="mt-4 min-h-48 w-full resize-y border border-pleiades-border-strong bg-pleiades-ivory p-4 text-[16px] leading-7 text-pleiades-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue"
          placeholder="Uygulama sırasında ve sonrasında fark ettiklerinizi yazın…"
        />
        <div className="mt-2 text-right text-[11px] tabular-nums text-pleiades-slate">{text.length} / 5000</div>
        <div className="mt-4 grid gap-3">
          {consentRequired && (
            <label className="flex items-start gap-3 text-[13px] leading-5 text-pleiades-slate">
              <input id="journal-sensitive-consent" type="checkbox" checked={sensitiveConsent} onChange={(event) => setSensitiveConsent(event.target.checked)} className="mt-1 h-4 w-4 accent-pleiades-blue" />
              <span>Deneyim metnimde yer verebileceğim hassas içeriklerin rehberlik sürecim için işlenmesine açık rıza veriyorum.</span>
            </label>
          )}
          <label className="flex items-start gap-3 text-[13px] leading-5 text-pleiades-slate">
            <input id="journal-reviewed" type="checkbox" checked={reviewed} onChange={(event) => setReviewed(event.target.checked)} className="mt-1 h-4 w-4 accent-pleiades-blue" />
            <span>Metnimi gözden geçirdim ve günlüğüme kaydetmek istiyorum.</span>
          </label>
        </div>
        {formError && <p role="alert" className="mt-4 border border-red-200 bg-red-50 p-3 text-[13px] text-red-800">{formError}</p>}
        <button type="submit" disabled={saving || text.trim().length < 5} className="mt-5 inline-flex min-h-12 w-full items-center justify-center gap-2 bg-pleiades-blue px-5 text-[14px] font-semibold text-white hover:bg-pleiades-blue-hover disabled:bg-pleiades-slate sm:w-auto">
          <Save size={17} aria-hidden="true" />
          {saving ? "Kaydediliyor…" : "Deneyimi kaydet"}
        </button>
      </form>

      <section className="border border-pleiades-border bg-white" aria-labelledby="journal-history-title">
        <div className="border-b border-pleiades-border p-4 sm:p-5">
          <div className="text-[10px] uppercase tracking-[.14em] text-pleiades-blue">Geçmiş kayıtlar</div>
          <h2 id="journal-history-title" className="mt-2 text-[19px] font-medium text-pleiades-ink">Deneyimleriniz</h2>
        </div>
        {loading ? (
          <p className="p-5 text-[13px] text-pleiades-slate">Günlüğünüz yükleniyor…</p>
        ) : items.length === 0 ? (
          <p className="p-5 text-[13px] leading-6 text-pleiades-slate">Henüz bir günlük kaydınız yok. İlk deneyiminizi yukarıdaki alana yazabilirsiniz.</p>
        ) : (
          <ol className="divide-y divide-pleiades-border">
            {items.map((item) => (
              <li key={item.id} className="p-4 sm:p-5">
                <time className="font-mono text-[11px] text-pleiades-blue" dateTime={item.created_at}>
                  {formatPortalDateTime(item.created_at)}
                </time>
                <p className="mt-3 whitespace-pre-wrap text-[15px] leading-7 text-pleiades-ink">{item.body}</p>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

function WorksSection({
  data,
  drafts,
  setDrafts,
  saving,
  saveExperience,
  requestComplete,
  automation,
  refreshAutomation,
  remoteSession,
  contactPhone,
  onOpenGuide,
  onExperienceJournalOpen,
}) {
  const file = data.current_work;
  const guidanceIsPreparing = ["complete_questionnaire", "plan_preparing", "guide_review"].includes(
    automation?.next_action,
  );
  const canWriteExperience =
    !guidanceIsPreparing && file?.experience_enabled === true && data.experience_writable === true;
  const dailyProgram = automation?.daily_program;
  const dailyProgramAvailable = Boolean(
    dailyProgram &&
      dailyProgram.status !== "materializing" &&
      (dailyProgram.status !== "active" ||
        (dailyProgram.current_step && dailyProgram.current_step.practice)),
  );
  const experienceSubmittedToday = data.daily_experience?.submitted === true;
  const hasVisibleAutomationStep = [
    "complete_questionnaire",
    "plan_preparing",
    "guide_review",
    "follow_plan",
  ].includes(automation?.next_action);
  return (
    <>
      <div className="pleiades-mobile-type-plus-2 space-y-3">
        <div className="stitch-today-editorial-header mb-3 sm:mb-5">
          <CalendarCheck2 size={17} aria-hidden="true" />
          <div>
            <span>Günlük uygulama</span>
            <h1>Bugünkü Çalışmam</h1>
            <p>Bugünün uygulamasını, çalışma adımlarını ve deneyim kaydını buradan takip edin.</p>
          </div>
        </div>
        <ClientAutomationPanel
          automation={automation}
          refresh={refreshAutomation}
          surface="work"
          currentWorkAvailable={Boolean(file) && !guidanceIsPreparing}
          onOpenGuide={onOpenGuide}
          onExperienceJournalOpen={onExperienceJournalOpen}
        />
        {remoteSession?.status === "active" && (
          <RemoteSessionCard remoteSession={remoteSession} contactPhone={contactPhone} compact />
        )}
        {file && !guidanceIsPreparing && !dailyProgramAvailable ? (
          <article className="bg-white border border-pleiades-border p-3 sm:p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 grid place-items-center bg-pleiades-blue-soft text-pleiades-blue">
                <BookOpen size={17} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-pleiades-blue mb-1">
                  Rehberlik günlüğü
                </div>
                <div className="text-[13px] font-semibold">Uygulama deneyimlerim</div>
              </div>
            </div>
            {file.description && (
              <p className="text-[12px] text-pleiades-slate leading-relaxed mt-4">
                {file.description}
              </p>
            )}
            {file.work_days && <WorkTiming file={file} />}
            {canWriteExperience && experienceSubmittedToday && (
              <div
                role="status"
                className="mt-5 bg-pleiades-ivory border border-pleiades-border p-4"
              >
                <div className="text-[12px] font-semibold">Bugünkü deneyiminiz gönderildi</div>
                <p className="text-[11px] text-pleiades-slate mt-2">
                  Yeni deneyim alanı yarın açılacak.
                </p>
              </div>
            )}
            {canWriteExperience && !experienceSubmittedToday && (
              <form
                onSubmit={(event) => saveExperience(event, file)}
                className="mt-5 bg-pleiades-ivory border border-pleiades-border p-4"
              >
                <div className="text-[12px] font-semibold mb-1">Bugünkü deneyimim</div>
                <p className="text-[11px] text-pleiades-slate mb-3">
                  Bu çalışma için günde bir deneyim gönderebilirsiniz.
                </p>
                <p className="text-[11px] text-pleiades-slate leading-[1.6] mb-3">
                  Gönderdikten sonra deneyim metnini yeniden görüntüleyemez, değiştiremez veya
                  silemezsiniz. Metin rehber değerlendirmesi için güvenli kayıt olarak korunur;
                  Arşivim’de yalnız gönderim makbuzu görünür.
                </p>
                <label className="sr-only" htmlFor={`experience-title-${file.id}`}>
                  Deneyiminizin başlığı
                </label>
                <input
                  id={`experience-title-${file.id}`}
                  value={drafts[file.id]?.title || ""}
                  onChange={(e) =>
                    setDrafts((d) => ({
                      ...d,
                      [file.id]: { ...d[file.id], title: e.target.value },
                    }))
                  }
                  required
                  maxLength={160}
                  placeholder="Deneyiminizin başlığı"
                  className="w-full h-11 px-3 bg-white border border-pleiades-border text-[16px] sm:text-[13px] mb-3 focus:outline-none focus:border-pleiades-blue"
                />
                <label className="sr-only" htmlFor={`experience-body-${file.id}`}>
                  Deneyiminiz
                </label>
                <textarea
                  id={`experience-body-${file.id}`}
                  value={drafts[file.id]?.body || ""}
                  onChange={(e) =>
                    setDrafts((d) => ({ ...d, [file.id]: { ...d[file.id], body: e.target.value } }))
                  }
                  required
                  maxLength={20000}
                  rows={5}
                  placeholder="Bu çalışmada ne fark ettiniz, ne hissettiniz?"
                  className="w-full px-3 py-3 bg-white border border-pleiades-border text-[16px] sm:text-[13px] resize-y focus:outline-none focus:border-pleiades-blue"
                />
                {data.sensitive_data_consent_required && (
                  <label className="mt-3 flex items-start gap-2 text-[11px] leading-[1.55] text-pleiades-slate">
                    <input
                      id={`experience-sensitive-consent-${file.id}`}
                      type="checkbox"
                      checked={Boolean(drafts[file.id]?.sensitive_data_consent)}
                      onChange={(e) =>
                        setDrafts((d) => ({
                          ...d,
                          [file.id]: {
                            ...d[file.id],
                            sensitive_data_consent: e.target.checked,
                            validation_error: "",
                            validation_target: null,
                          },
                        }))
                      }
                      aria-describedby={
                        drafts[file.id]?.validation_target === "sensitive-consent"
                          ? `experience-validation-${file.id}`
                          : undefined
                      }
                      className="mt-0.5 accent-pleiades-blue"
                    />
                    <span>
                      Deneyim metnimde yer verebileceğim hassas içeriklerin danışmanlık sürecinin
                      yürütülmesi amacıyla işlenmesine açık rıza veriyorum. Bu onay yalnız ilk
                      deneyim gönderiminde alınır.
                    </span>
                  </label>
                )}
                <label className="mt-3 flex items-start gap-2 text-[11px] leading-[1.55] text-pleiades-slate">
                  <input
                    id={`experience-reviewed-${file.id}`}
                    type="checkbox"
                    checked={Boolean(drafts[file.id]?.reviewed)}
                    onChange={(e) =>
                      setDrafts((d) => ({
                        ...d,
                        [file.id]: {
                          ...d[file.id],
                          reviewed: e.target.checked,
                          validation_error: "",
                          validation_target: null,
                        },
                      }))
                    }
                    aria-describedby={
                      drafts[file.id]?.validation_target === "reviewed"
                        ? `experience-validation-${file.id}`
                        : undefined
                    }
                    className="mt-0.5 accent-pleiades-blue"
                  />
                  <span>
                    Metnimi gözden geçirdim; gönderimden sonra içeriğini panelde tekrar
                    göremeyeceğimi biliyorum.
                  </span>
                </label>
                {drafts[file.id]?.validation_error && (
                  <p
                    id={`experience-validation-${file.id}`}
                    role="alert"
                    className="mt-3 border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-medium leading-[1.55] text-red-700"
                  >
                    {drafts[file.id].validation_error}
                  </p>
                )}
                <p className="mt-3 text-[11px] leading-[1.55] text-pleiades-slate">
                  Deneyiminiz Şeref Tolga Kuralay tarafından değerlendirilir.
                </p>
                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
                  {file.can_complete ? (
                    <button
                      type="button"
                      onClick={() => requestComplete(file)}
                      className="min-h-11 px-4 border border-pleiades-border text-[12px] flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue"
                    >
                      <CheckCircle2 size={14} />
                      Çalışmayı bitirdim
                    </button>
                  ) : (
                    <span className="text-[11px] text-pleiades-muted">
                      Çalışma süresi tamamlandığında bitirme seçeneği açılır.
                    </span>
                  )}
                  <button
                    disabled={saving}
                    className="fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-20 min-h-12 px-4 bg-pleiades-blue text-white text-[12px] flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2 sm:static sm:min-h-11 sm:shadow-none"
                  >
                    <Save size={14} />
                    {saving ? "Gönderiliyor…" : "Deneyimi gönder"}
                  </button>
                </div>
              </form>
            )}
          </article>
        ) : (
          !hasVisibleAutomationStep && (
            <div className="bg-white border border-dashed border-pleiades-border p-10 text-center">
              <FileText className="mx-auto text-pleiades-muted mb-3" />
              <div className="text-[13px] font-medium">Şu anda aktif çalışmanız yok</div>
              <p className="text-[11px] text-pleiades-muted mt-1">
                Yeni çalışma programınız yayınlandığında burada görünecek.
              </p>
            </div>
          )
        )}
      </div>
    </>
  );
}

function NotificationCenter({ notifications, onOpenNotification }) {
  const [open, setOpen] = useState(false);
  const unreadCount = notifications.filter((notification) => !notification.read_at).length;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="stitch-notification-trigger"
        aria-label={unreadCount ? `Bildirimler, ${unreadCount} okunmamış bildirim` : "Bildirimler"}
      >
        <Bell size={18} aria-hidden="true" />
        {unreadCount > 0 && <Badge value={unreadCount} />}
      </button>
      <DialogContent className="w-[min(360px,calc(100vw-2rem))] gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-pleiades-border px-5 py-4 text-left">
          <DialogTitle className="text-[18px]">Bildirimler</DialogTitle>
          <DialogDescription className="text-[12px]">
            Programınızla ilgili güncel bilgiler.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[min(60dvh,420px)] overflow-y-auto p-3">
          {notifications.length > 0 ? (
            <div className="divide-y divide-pleiades-border">
              {notifications.map((notification, index) => (
                <button
                  type="button"
                  key={notification.id || `${notification.kind}-${index}`}
                  onClick={() => {
                    setOpen(false);
                    onOpenNotification(notification);
                  }}
                  className={`flex min-h-14 w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-pleiades-ivory focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pleiades-blue ${notification.read_at ? "bg-white" : "bg-pleiades-blue-soft"}`}
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${notification.read_at ? "bg-pleiades-border" : "bg-pleiades-blue"}`}
                    aria-hidden="true"
                  />
                  <p className="text-[13px] font-medium leading-relaxed text-pleiades-ink">
                    {notification.title}
                  </p>
                  <ChevronRight size={15} className="ml-auto shrink-0 text-pleiades-slate" aria-hidden="true" />
                </button>
              ))}
            </div>
          ) : (
            <p className="px-2 py-6 text-center text-[12px] text-pleiades-slate">
              Yeni bildiriminiz bulunmuyor.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ArchivesSection({
  packages,
  programLoading,
  items,
  loading,
  hasMore,
  loadMore,
  openDetail,
}) {
  return (
    <div className="pleiades-mobile-type-plus-2">
      <Page
        title="Arşivim"
        description="Daha önce tamamladığınız çalışmaları paket, çalışma sırası, Esma ve meditasyon koduyla inceleyin."
      >
      <div className="space-y-3 mb-3">
        {packages.map((programPackage, index) => (
          <ProgramArchiveFolder
            key={programPackage.id}
            programPackage={programPackage}
            initiallyOpen={index === 0}
          />
        ))}
        {!packages.length && programLoading && (
          <div className="border border-pleiades-border bg-white px-4 py-5 text-[11px] text-pleiades-muted">
            Program arşiviniz yükleniyor…
          </div>
        )}
      </div>
      {items.length ? (
        <details className="border border-pleiades-border bg-white">
          <summary className="min-h-12 px-4 py-3 flex items-center gap-3 cursor-pointer text-[12px] font-medium">
            <Archive size={16} className="text-pleiades-blue" /> Eski PDF çalışmaları
            <span className="ml-auto text-[10px] font-normal text-pleiades-muted">
              {items.length} kayıt
            </span>
          </summary>
          <div className="space-y-3 p-3 border-t border-pleiades-border bg-pleiades-ivory">
            {items.map((item) => {
              const work = archiveWork(item);
              const date =
                work.completed_at ||
                work.cancelled_at ||
                work.superseded_at ||
                work.archived_at ||
                work.assigned_at;
              return (
                <button
                  type="button"
                  key={work.assignment_id || work.id}
                  onClick={() => openDetail(item)}
                  className="w-full bg-white border border-pleiades-border p-4 sm:p-5 text-left hover:border-pleiades-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 shrink-0 grid place-items-center bg-pleiades-ivory text-pleiades-blue">
                      <FileText size={17} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-[14px] font-semibold">
                          {work.title || "Kişisel çalışma"}
                        </h2>
                        <span className="text-[10px] px-2 py-1 border border-pleiades-border bg-pleiades-ivory">
                          {archiveStatusLabel(work)}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-pleiades-muted mt-2">
                        <span>{formatPortalDate(date)}</span>
                        <span>{item.experience_count || 0} deneyim gönderimi</span>
                        <span>Çalışma kaydı</span>
                      </div>
                    </div>
                    <ChevronRight size={17} className="mt-1 shrink-0 text-pleiades-muted" />
                  </div>
                </button>
              );
            })}
            {hasMore && (
              <div className="flex justify-center pt-2">
                <button
                  type="button"
                  disabled={loading}
                  onClick={loadMore}
                  className="min-h-11 px-5 border border-pleiades-border bg-white text-[12px] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue"
                >
                  {loading ? "Yükleniyor…" : "Daha fazlasını göster"}
                </button>
              </div>
            )}
          </div>
        </details>
      ) : (
        !packages.length &&
        !programLoading && (
          <div className="bg-white border border-dashed border-pleiades-border p-10 text-center">
            <Archive className="mx-auto text-pleiades-muted mb-3" />
            <div className="text-[13px] font-medium">
              {loading ? "Arşiviniz yükleniyor…" : "Henüz arşiv kaydı yok"}
            </div>
            <p className="text-[11px] text-pleiades-muted mt-1">
              {loading
                ? "Çalışmalarınız hazırlanıyor."
                : "Tamamlanan veya arşivlenen çalışmalarınız burada görünecek."}
            </p>
          </div>
        )
      )}
      </Page>
    </div>
  );
}

function ProgramArchiveFolder({ programPackage, initiallyOpen }) {
  const [open, setOpen] = useState(initiallyOpen);
  const [openStep, setOpenStep] = useState(null);
  return (
    <section className="border border-pleiades-border bg-white">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="w-full min-h-14 px-4 py-3 flex items-center gap-3 text-left hover:bg-pleiades-ivory focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pleiades-blue"
      >
        <Folder size={18} className="shrink-0 text-pleiades-blue" />
        <div className="min-w-0 flex-1">
          <h2 className="text-[13px] font-semibold">
            {programPackage.package_months} Aylık Program
          </h2>
          <p className="text-[10px] text-pleiades-muted mt-0.5">
            {programPackage.items?.length || 0} tamamlanan çalışma ·{" "}
            {formatPortalDate(programPackage.first_available_at || programPackage.created_at)}
          </p>
        </div>
        <span className="text-[10px] tabular-nums text-pleiades-slate">
          {programPackage.completed_steps || 0}/{programPackage.target_completed_steps || 0}
        </span>
        <ChevronDown
          size={16}
          className={`text-pleiades-muted transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="border-t border-pleiades-border">
          {programPackage.items?.length ? (
            programPackage.items.map((step) => {
              const practice = step.practice || {};
              const expanded = openStep === step.id;
              return (
                <div key={step.id} className="border-t first:border-t-0 border-pleiades-border">
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => setOpenStep(expanded ? null : step.id)}
                    className="w-full px-4 py-3 grid grid-cols-[72px_minmax(0,1fr)_minmax(86px,1fr)_18px] sm:grid-cols-[92px_100px_minmax(130px,1fr)_minmax(130px,1fr)_55px_18px] gap-3 items-center text-left hover:bg-pleiades-ivory"
                  >
                    <span className="text-[11px] font-semibold">
                      {archiveWorkLabel(step)}
                    </span>
                    <span className="hidden sm:block text-[10px] text-pleiades-muted">
                      {formatProgramArchiveDate(step.completed_at)}
                    </span>
                    <span className="min-w-0">
                      <small className="sm:hidden block text-[8px] uppercase text-pleiades-muted">
                        Esma / Mantra
                      </small>
                      <span className="block truncate text-[11px] font-medium">
                        {practice.name || "—"}
                      </span>
                    </span>
                    <span className="min-w-0">
                      <small className="sm:hidden block text-[8px] uppercase text-pleiades-muted">
                        Meditasyon kodu
                      </small>
                      <span className="block break-all text-[11px] font-semibold tracking-[.06em]">
                        {practice.meditation_code || "—"}
                      </span>
                    </span>
                    <span className="hidden sm:block text-[10px]">
                      Dönem {archivePhaseNumber(step)}
                    </span>
                    <ChevronDown
                      size={15}
                      className={`text-pleiades-muted ${expanded ? "rotate-180" : ""}`}
                    />
                  </button>
                  {expanded && (
                    <dl className="grid grid-cols-2 sm:grid-cols-5 gap-3 px-4 py-4 bg-pleiades-ivory border-t border-pleiades-border">
                      {[
                        ["Tür", step.practice_kind === "mantra" ? "Mantra" : "Esma"],
                        ["Sesli uygulama", `${practice.vocal_minutes || 20} dakika`],
                        ["Tekrar aralığı", `${practice.repeat_interval_seconds || 5} saniye`],
                        ["Pozisyon", practice.position || "Oturarak"],
                        ["Meditasyon kodu", practice.meditation_code || "—"],
                        ["Kod tekrarı", `${practice.code_repeat_count || 0} kez`],
                        [
                          "Meditasyon",
                          `${practice.meditation_minutes || 15} dakika · ${practice.meditation_position || "Ayakta"}`,
                        ],
                        ["Tamamlanma", formatProgramArchiveDateTime(step.completed_at)],
                        ["Deneyim", step.experience?.submitted ? "Kaydedildi" : "Kaydedilmedi"],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <dt className="text-[8px] uppercase tracking-[.08em] text-pleiades-muted">
                            {label}
                          </dt>
                          <dd className="mt-0.5 text-[10px] font-medium break-words">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              );
            })
          ) : (
            <p className="px-4 py-5 text-[11px] text-pleiades-muted">
              Bu pakette henüz tamamlanan günlük çalışma yok.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function ArchiveDetailDialog({ detail, loading, open, onOpenChange }) {
  const work = archiveWork(detail?.work || {});
  const receipts = detail?.experience_receipts || detail?.receipts || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="archive-detail-dialog"
        className="pleiades-mobile-type-plus-2 w-[calc(100vw-1rem)] max-w-[720px] h-[calc(100dvh-1rem)] sm:h-auto max-h-[90dvh] rounded-none border-pleiades-border bg-white p-0 gap-0 overflow-hidden"
      >
        <DialogHeader className="px-5 sm:px-7 py-5 border-b border-pleiades-border pr-12 text-left">
          <div className="text-[10px] uppercase tracking-[.14em] text-pleiades-blue">
            {archiveStatusLabel(work)}
          </div>
          <DialogTitle className="text-[20px] sm:text-[24px] leading-tight mt-1">
            {work.title || "Kişisel çalışma"}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-pleiades-slate">
            Çalışma bilgileri ve deneyim gönderim makbuzları
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto overscroll-contain px-5 sm:px-7 py-5">
          {loading ? (
            <div role="status" className="py-10 text-center text-[12px] text-pleiades-muted">
              Arşiv detayı yükleniyor…
            </div>
          ) : (
            <>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-[11px]">
                <div>
                  <dt className="text-pleiades-muted">Durum</dt>
                  <dd className="font-medium mt-1">{archiveStatusLabel(work)}</dd>
                </div>
                <div>
                  <dt className="text-pleiades-muted">Başlangıç</dt>
                  <dd className="font-medium mt-1">
                    {formatPortalDate(work.assigned_at || work.work_started_at)}
                  </dd>
                </div>
                <div>
                  <dt className="text-pleiades-muted">Süre</dt>
                  <dd className="font-medium mt-1">
                    {work.work_days ? `${work.work_days} gün` : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-pleiades-muted">Gönderim</dt>
                  <dd className="font-medium mt-1">{receipts.length} kayıt</dd>
                </div>
              </dl>
              <section className="mt-6 pt-5 border-t border-pleiades-border">
                <h3 className="text-[13px] font-semibold">Deneyim gönderim kayıtları</h3>
                <p className="text-[11px] leading-[1.6] text-pleiades-muted mt-1">
                  Gizliliğiniz için gönderdiğiniz başlık ve metin panelde yeniden gösterilmez.
                </p>
                {receipts.length ? (
                  <ol className="space-y-2 mt-4">
                    {receipts.map((receipt, index) => (
                      <li
                        key={receipt.id || `${receipt.created_at}-${index}`}
                        className="border border-pleiades-border p-4 flex items-center gap-3"
                      >
                        <CheckCircle2
                          size={16}
                          className={
                            receipt.admin_read ? "text-emerald-700" : "text-pleiades-muted"
                          }
                        />
                        <div className="min-w-0">
                          <div className="text-[12px] font-medium">
                            {receipt.admin_read
                              ? "Rehber tarafından görüntülendi"
                              : "Rehberine ulaştı"}
                          </div>
                          <time className="text-[10px] text-pleiades-muted mt-1 block">
                            {formatPortalDateTime(receipt.created_at)}
                          </time>
                        </div>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <div className="text-[11px] text-pleiades-muted py-6">
                    Bu çalışmaya ait deneyim gönderimi yok.
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AudioSection({ items, current, playing, select, download, toggle }) {
  usePortalContentActivity(current && playing ? {
    active: true,
    content_type: "audio",
    content_id: current.id,
    content_title: current.title,
  } : null);
  const grouped = items.reduce((acc, item) => {
    const topic = item.topic || "Diğer";
    acc[topic] = acc[topic] || [];
    acc[topic].push(item);
    return acc;
  }, {});
  return (
    <Page
      title="Meditasyonlar ve Sesler"
      description="Size açık meditasyon anlatımlarını, müzikleri ve eğitim kayıtlarını panel içinde dinleyin."
    >
      {items.length ? (
        <div className="space-y-7">
          {Object.entries(grouped).map(([topic, topicItems]) => (
            <section key={topic}>
              <div className="flex items-center gap-2 mb-3">
                <Headphones size={16} />
                <h2 className="text-[14px] font-semibold">{topic}</h2>
                <span className="text-[10px] text-pleiades-muted">{topicItems.length} içerik</span>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {topicItems.map((item) => {
                  const active = current?.id === item.id;
                  return (
                    <article
                      key={item.id}
                      className={`bg-white border p-4 sm:p-5 ${active ? "border-pleiades-blue ring-2 ring-pleiades-blue/10" : "border-pleiades-border"}`}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => (active ? toggle() : select(item))}
                          aria-label={active && playing ? "Duraklat" : "Oynat"}
                          className={`w-11 h-11 shrink-0 grid place-items-center ${active ? "bg-pleiades-blue text-white" : "bg-pleiades-ivory text-pleiades-ink"}`}
                        >
                          {active && playing ? (
                            <Pause size={17} />
                          ) : (
                            <Play size={17} className="ml-0.5" />
                          )}
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] uppercase tracking-wider text-pleiades-blue">
                            {audioCategoryLabels[item.audio_category]}
                          </div>
                          <h3 className="text-[13px] font-semibold mt-1">{item.title}</h3>
                          <p className="text-[11px] text-pleiades-slate leading-relaxed mt-2">
                            {item.description || "Açıklama eklenmedi."}
                          </p>
                        </div>
                        <button
                          onClick={() => download(item)}
                          aria-label="Ses dosyasını indir"
                          className="w-9 h-9 grid place-items-center text-pleiades-muted hover:text-pleiades-blue"
                        >
                          <Download size={15} />
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-dashed border-pleiades-border p-12 text-center">
          <Headphones className="mx-auto text-pleiades-muted mb-3" />
          <div className="text-[13px] font-medium">Henüz size açık bir ses içeriği yok</div>
          <p className="text-[11px] text-pleiades-muted mt-1">
            Yeni meditasyonlar yayınlandığında burada görünecek.
          </p>
        </div>
      )}
    </Page>
  );
}

/*
 * inactive_legacy:
 * Eski fiyat, seçimlik rehberlik, sözleşme ve iptal arayüzü tarihsel referans
 * olarak bu dalda korunur; hiçbir kullanıcı rotasından render edilmez.
function ConsultingSection({ note, setNote, whatsapp, call, packages, automation, requestProgram, requesting, requestError, clearRequestError, requestCancellation, cancellationRequesting, refreshAutomation }) {
  const [expandedPackage, setExpandedPackage] = useState(null);
  const [requestCandidate, setRequestCandidate] = useState(null);
  const [blockedPackageCandidate, setBlockedPackageCandidate] = useState(null);
  const [requestGuidanceSelected, setRequestGuidanceSelected] = useState(false);
  const readiness = automation?.readiness;
  const requestIntakeReady = Boolean(
    readiness?.enabled
    && readiness?.auto_offer
    && readiness?.contract
    && readiness?.package_catalog
  );
  const hasOpenRequest = ["submitted", "reviewed"].includes(automation?.request?.status);
  const hasOpenOffer = Boolean(automation?.offer);
  const cancellationRequested = automation?.offer?.cancellation_status === "requested_by_client";
  const requestUnavailable = Boolean(automation && !requestIntakeReady && !hasOpenOffer);
  const requestButtonLabel = hasOpenRequest || hasOpenOffer
    ? "Mevcut talebiniz incelemede"
    : !automation
      ? "Talep sistemi kontrol ediliyor…"
      : requestIntakeReady
        ? "Program talebi oluştur"
        : "Talep sistemi hazırlanıyor";
  const confirmRequest = async () => {
    if (!requestCandidate) return;
    const created = await requestProgram(
      requestCandidate,
      requestGuidanceSelected,
    );
    if (created) setRequestCandidate(null);
  };
  const confirmCancellationRequest = async () => {
    const created = await requestCancellation();
    if (created) setBlockedPackageCandidate(null);
  };
  return <div className="space-y-5 sm:space-y-7">
    <Dialog open={Boolean(requestCandidate)} onOpenChange={(open) => {
      if (!requesting && !open) {
        setRequestCandidate(null);
        clearRequestError();
      }
    }}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-[560px] rounded-none border-pleiades-border p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-6 sm:px-7 sm:pt-7 text-left">
          <div className="text-[11px] uppercase tracking-[.14em] text-pleiades-blue">Talep özeti</div>
          <DialogTitle className="text-[20px] leading-[1.35] mt-2">Program talebini onayla</DialogTitle>
          <DialogDescription className="text-[13px] leading-[1.65] text-pleiades-slate mt-2">
            Seçimin kaydedildiğinde ilgili sözleşme hemen açılacak.
          </DialogDescription>
        </DialogHeader>
        {requestCandidate && <div className="px-5 pb-6 sm:px-7 sm:pb-7">
          <dl className="grid sm:grid-cols-2 border border-pleiades-border mt-5">
            <div className="p-4">
              <dt className="text-[10px] uppercase tracking-[.12em] text-pleiades-muted">Seçilen program</dt>
              <dd className="text-[14px] font-semibold mt-2">{requestCandidate.months} aylık danışmanlık</dd>
            </div>
            <div className="p-4 border-t sm:border-t-0 sm:border-l border-pleiades-border">
              <dt className="text-[10px] uppercase tracking-[.12em] text-pleiades-muted">Dijital çalışma programı</dt>
              <dd className="text-[14px] font-semibold mt-2">{formatConsultingPrice(requestCandidate.digitalProgramPrice)}</dd>
            </div>
          </dl>
          <label className="mt-4 flex items-start gap-3 border border-pleiades-border bg-white p-4 text-[12px] leading-[1.65] text-pleiades-slate">
            <input
              type="checkbox"
              checked={requestGuidanceSelected}
              onChange={(event) => setRequestGuidanceSelected(event.target.checked)}
              className="mt-0.5 h-4 w-4 accent-pleiades-blue"
            />
            <span>
              <strong className="block text-pleiades-ink">Seçimlik rehberlik hizmetini ekle · {formatConsultingPrice(requestCandidate.guidancePrice)}</strong>
              Paket süresince deneyim takibi ve yeni çalışma yönlendirmesi. Önceden seçili değildir; normal fesihte henüz sunulmamış kısmı gün bazında iade edilir.
            </span>
          </label>
          <div className="mt-3 flex items-center justify-between gap-4 bg-pleiades-blue-soft px-4 py-3 text-[12px]">
            <span>Talep toplamı</span>
            <strong>{formatConsultingPrice(
              requestCandidate.digitalProgramPrice
              + (requestGuidanceSelected ? requestCandidate.guidancePrice : 0),
            )}</strong>
          </div>
          <div role="note" className="mt-4 border-l-2 border-pleiades-gold bg-pleiades-ivory px-4 py-3 text-[12px] leading-[1.65] text-pleiades-slate">
            Bu adım sözleşmeyi incelemek için açar. Ödeme yükümlülüğü, sözleşme ve ön bilgilendirme beyanlarını ayrıca kabul etmeden oluşmaz.
          </div>
          {requestError && <div role="alert" className="mt-4 border border-red-300 bg-red-50 px-4 py-3 text-[13px] leading-[1.6] text-red-800">
            <strong className="block">Talep oluşturulamadı</strong>
            <span>{requestError}</span>
          </div>}
          <div className="grid sm:grid-cols-2 gap-2 mt-5">
            <button type="button" onClick={() => setRequestCandidate(null)} disabled={requesting} className="min-h-12 px-5 border border-pleiades-border bg-white text-[13px] font-medium disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2">
              Vazgeç
            </button>
            <button type="button" onClick={confirmRequest} disabled={requesting} className="min-h-12 px-5 bg-pleiades-blue text-white text-[13px] font-medium disabled:bg-pleiades-slate focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2">
              {requesting ? "Sözleşme hazırlanıyor…" : "Talebi gönder ve sözleşmeyi aç"}
            </button>
          </div>
        </div>}
      </DialogContent>
    </Dialog>
    <Dialog open={Boolean(blockedPackageCandidate)} onOpenChange={(open) => {
      if (!cancellationRequesting && !open) {
        setBlockedPackageCandidate(null);
        clearRequestError();
      }
    }}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-[520px] rounded-none border-pleiades-border p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-6 sm:px-7 sm:pt-7 text-left">
          <div className="text-[11px] uppercase tracking-[.14em] text-pleiades-blue">Yeni paket talebi</div>
          <DialogTitle className="text-[20px] leading-[1.35] mt-2">Önce mevcut paketinizi iptal etmeniz gerekir</DialogTitle>
          <DialogDescription className="text-[13px] leading-[1.65] text-pleiades-slate mt-2">
            Aynı anda yalnız bir paket kullanılabilir. İptal talebiniz admin tarafından onaylanmadan yeni paket oluşturulamaz.
          </DialogDescription>
        </DialogHeader>
        <div className="px-5 pb-6 sm:px-7 sm:pb-7">
          {cancellationRequested && <div role="status" className="mt-5 border border-pleiades-blue/25 bg-pleiades-blue-soft px-4 py-3 text-[13px] leading-[1.6] text-pleiades-ink">
            İptal talebiniz alındı. Admin işlemi tamamladığında yeni paket seçebilirsiniz.
          </div>}
          {requestError && <div role="alert" className="mt-5 border border-red-300 bg-red-50 px-4 py-3 text-[13px] leading-[1.6] text-red-800">
            <strong className="block">İptal talebi oluşturulamadı</strong>
            <span>{requestError}</span>
          </div>}
          <div className="grid sm:grid-cols-2 gap-2 mt-5">
            <button type="button" onClick={() => setBlockedPackageCandidate(null)} disabled={cancellationRequesting} className="min-h-12 px-5 border border-pleiades-border bg-white text-[13px] font-medium disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2">
              Kapat
            </button>
            {!cancellationRequested && <button type="button" onClick={confirmCancellationRequest} disabled={cancellationRequesting} className="min-h-12 px-5 bg-pleiades-blue text-white text-[13px] font-medium disabled:bg-pleiades-slate focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2">
              {cancellationRequesting ? "İptal talebi gönderiliyor…" : "Mevcut paket için iptal talebi oluştur"}
            </button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <section className="bg-pleiades-ink text-white border border-pleiades-ink px-5 py-7 sm:px-8 sm:py-10">
      <div className="grid lg:grid-cols-[minmax(0,7fr)_minmax(280px,5fr)] gap-8 lg:gap-12 items-end">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[.16em] text-pleiades-gold">Özel danışmanlık programları</div>
          <h1 className="text-[24px] leading-[1.25] sm:text-[30px] sm:leading-[1.2] font-semibold tracking-tight mt-3">Sana verilen bir program değil, seninle birlikte şekillenen bir yol.</h1>
          <p className="text-[14px] leading-[1.65] text-white/75 mt-5 max-w-[68ch]">Bu danışmanlık, herkese aynı biçimde sunulan hazır bir çalışma paketi değildir. Kişisel çalışmaların danışan panelinde gün gün açılır; uygulama deneyimlerin değerlendirilir ve sonraki çalışmanın yönü bu değerlendirmelerle şekillenir.</p>
        </div>
        <div className="border-l-2 border-pleiades-gold pl-5 sm:pl-6">
          <div className="text-[11px] uppercase tracking-[.14em] text-white/55">Programların ortak özü</div>
          <p className="text-[14px] leading-[1.6] mt-3">1, 3, 6 ve 12 aylık programların yöntemi aynıdır. Fark, birlikte yürünecek süre ve bu sürenin açtığı derinleşme alanıdır.</p>
        </div>
      </div>
    </section>

    {(automation?.request || automation?.offer) && <ClientAutomationPanel automation={automation} refresh={refreshAutomation} surface="process" />}

    <section aria-labelledby="consulting-process-title" className="bg-white border border-pleiades-border">
      <div className="px-5 py-5 sm:px-7 border-b border-pleiades-border">
        <div className="text-[11px] uppercase tracking-[.14em] text-pleiades-blue">Takipli ve kişisel süreç</div>
        <h2 id="consulting-process-title" className="text-[21px] leading-[1.3] sm:text-[24px] font-semibold mt-2">Danışmanlık nasıl ilerler?</h2>
      </div>
      <ol className="grid md:grid-cols-5">
        {consultingSteps.map(([number, title, text], index) => <li key={number} className={`p-5 sm:p-6 ${index ? "border-t md:border-t-0 md:border-l" : ""} border-pleiades-border`}>
          <div className="text-[11px] font-semibold tracking-[.14em] text-pleiades-gold">{number}</div>
          <h3 className="text-[14px] font-semibold mt-3">{title}</h3>
          <p className="text-[12px] leading-[1.65] text-pleiades-slate mt-2">{text}</p>
        </li>)}
      </ol>
    </section>

    <section aria-labelledby="consulting-packages-title">
      <div className="max-w-3xl mb-5 sm:mb-6">
        <div className="text-[11px] uppercase tracking-[.14em] text-pleiades-blue">Süreni seç</div>
        <h2 id="consulting-packages-title" className="text-[21px] leading-[1.3] sm:text-[24px] font-semibold mt-2">Yol ne kadar uzarsa, gözlem alanı o kadar derinleşir.</h2>
        <p className="text-[14px] leading-[1.65] text-pleiades-slate mt-3 max-w-[68ch]">Derinleşme kesin bir sonuç vaadi değildir; uygulamaları hayatına yerleştirmek, tekrar eden temaları zaman içinde fark etmek ve çalışmaları daha geniş bir deneyim birikimiyle şekillendirmek için açılan alandır.</p>
      </div>
      {requestUnavailable && <div id="consulting-request-readiness" role="status" className="mb-4 border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] leading-[1.6] text-amber-900">
        Panelden yeni talep oluşturma kısa süreliğine hazırlanıyor. Paketleri inceleyebilir; bu sırada WhatsApp veya telefon üzerinden ön görüşme isteyebilirsiniz.
      </div>}
      {hasOpenRequest && <div role="status" className="mb-4 border border-pleiades-blue/25 bg-pleiades-blue-soft px-4 py-3 text-[12px] leading-[1.6] text-pleiades-ink">
        <strong>Talebiniz alındı ve incelemede.</strong> Yeni bir talep göndermenize gerek yok; durum değiştiğinde bu alan güncellenecek.
      </div>}
      <div className="grid lg:grid-cols-2 gap-4">
        {packages.map((item) => {
          const isExpanded = expandedPackage === item.id;
          return <article key={item.id} className={`bg-white border ${isExpanded ? "border-pleiades-blue" : "border-pleiades-border"} flex flex-col`}>
            <div className="p-5 sm:p-6 flex-1">
              <div className="flex items-start justify-between gap-5">
                <div>
                  <div className="text-[11px] uppercase tracking-[.14em] text-pleiades-blue">{item.label}</div>
                  <h3 className="text-[18px] leading-[1.4] font-semibold mt-2">{item.title}</h3>
                </div>
                <div className="shrink-0 w-12 h-12 border border-pleiades-border grid place-items-center text-[18px] font-semibold text-pleiades-blue">{item.months}</div>
              </div>
              <p className="text-[14px] leading-[1.65] text-pleiades-slate mt-4">{item.summary}</p>
              <div className="mt-5 pt-4 border-t border-pleiades-border">
                <div className="text-[11px] uppercase tracking-[.12em] text-pleiades-muted">Dijital çalışma programı</div>
                <div className="text-[20px] leading-none font-semibold text-pleiades-ink mt-2">{formatConsultingPrice(item.digitalProgramPrice)}</div>
                <div className="text-[11px] leading-[1.5] text-pleiades-muted mt-2">Rehberlik isteğe bağlı · +{formatConsultingPrice(item.guidancePrice)}</div>
              </div>
              <div className="mt-4 flex items-center justify-between gap-4">
                <span className="text-[11px] text-pleiades-muted">{item.intent}</span>
                <button type="button" aria-expanded={isExpanded} aria-controls={`${item.id}-details`} onClick={() => setExpandedPackage(isExpanded ? null : item.id)} className="min-h-11 px-3 text-[12px] font-medium text-pleiades-blue flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2">
                  {isExpanded ? "Detayları kapat" : "Programın detayları"}<ChevronRight size={14} className={`transition-transform motion-reduce:transition-none ${isExpanded ? "rotate-90" : ""}`} />
                </button>
              </div>
              {!hasOpenRequest && <button
                type="button"
                disabled={requesting || (!hasOpenOffer && !requestIntakeReady)}
                aria-describedby={requestUnavailable ? "consulting-request-readiness" : undefined}
                onClick={() => {
                  if (hasOpenOffer) {
                    setBlockedPackageCandidate(item);
                    return;
                  }
                  setRequestGuidanceSelected(false);
                  setRequestCandidate(item);
                }}
                className="w-full min-h-12 px-5 mt-4 bg-pleiades-blue text-white text-[13px] font-medium flex items-center justify-center gap-2 disabled:bg-pleiades-slate disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2"
              >
                <FileText size={15} /> {hasOpenOffer ? "Yeni paket seç" : requesting ? "Talep oluşturuluyor…" : requestButtonLabel}
              </button>}
            </div>
            {isExpanded && <div id={`${item.id}-details`} className="border-t border-pleiades-border bg-pleiades-ivory p-5 sm:p-6">
              <p className="text-[14px] leading-[1.65] text-pleiades-ink">{item.depth}</p>
              <ul className="mt-5 space-y-3 text-[13px] leading-[1.55] text-pleiades-slate">
                <li className="flex gap-3"><span aria-hidden="true" className="text-pleiades-gold">—</span><span>Sana özel hazırlanan ve danışan panelinde gün gün açılan çalışma programı</span></li>
                <li className="flex gap-3"><span aria-hidden="true" className="text-pleiades-gold">—</span><span>İhtiyacına göre esma, meditasyon, mantra ve meditasyon kodu uygulamaları</span></li>
                <li className="flex gap-3"><span aria-hidden="true" className="text-pleiades-gold">—</span><span>Deneyimlerini güvenli danışan alanına yazabilme</span></li>
                <li className="flex gap-3"><span aria-hidden="true" className="text-pleiades-gold">—</span><span>Deneyimlerin rehber tarafından değerlendirilmesi ve sonraki çalışmanın buna göre şekillenmesi</span></li>
              </ul>
              <div className="grid sm:grid-cols-2 gap-2 mt-6">
                <button type="button" onClick={() => whatsapp(item)} className="min-h-12 px-5 bg-[#1f7a52] text-white text-[13px] font-medium flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1f7a52] focus-visible:ring-offset-2">
                  <MessageCircle size={15} /> WhatsApp’tan görüş
                </button>
                <button type="button" onClick={() => call(item)} className="min-h-12 px-5 border border-pleiades-border bg-white text-pleiades-ink text-[13px] font-medium flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2">
                  <Phone size={15} /> Ara
                </button>
              </div>
            </div>}
          </article>;
        })}
      </div>
      <p className="text-[12px] leading-[1.65] text-pleiades-slate mt-4 max-w-4xl">Program talebi güvenli danışan panelinden gönderilir ve önce incelemeye alınır. Talep oluşturmak satın alma, sözleşme kabulü veya ödeme yükümlülüğü doğurmaz. Uygun sonraki adım, inceleme tamamlandığında ayrıca panelinde gösterilir. İstersen karar vermeden önce WhatsApp veya telefon üzerinden görüşebilirsin.</p>
    </section>

    <section className="grid lg:grid-cols-[minmax(0,5fr)_minmax(300px,7fr)] bg-white border border-pleiades-border">
      <div className="p-5 sm:p-7 bg-pleiades-blue-soft">
        <div className="text-[11px] uppercase tracking-[.14em] text-pleiades-blue">Karar vermeden önce</div>
        <h2 className="text-[21px] leading-[1.35] font-semibold mt-2">Hangi sürenin sana uygun olduğundan emin değil misin?</h2>
        <p className="text-[13px] leading-[1.65] text-pleiades-slate mt-3">Kısaca hangi konuda ilerlemek istediğini yaz. Ön görüşmede ihtiyacını birlikte değerlendirerek uygun danışmanlık süresini konuşabiliriz.</p>
      </div>
      <div className="p-5 sm:p-7">
        <label htmlFor="consulting-whatsapp-note" className="block text-[11px] uppercase tracking-[.12em] text-pleiades-muted mb-2">WhatsApp mesajına eklenecek not <span className="normal-case tracking-normal">(isteğe bağlı)</span></label>
        <textarea id="consulting-whatsapp-note" value={note} onChange={(e) => setNote(e.target.value)} rows={4} maxLength={1000} placeholder="Kısaca hangi konuda danışmanlık almak istediğini yazabilirsin." className="w-full px-3 py-3 border border-pleiades-border text-[16px] sm:text-[13px] resize-y focus:outline-none focus:border-pleiades-blue focus:ring-2 focus:ring-pleiades-blue/10" />
        <div className="grid sm:grid-cols-[minmax(0,1fr)_auto] gap-2 mt-3">
          <button type="button" onClick={() => whatsapp(consultingIntro)} className="min-h-12 px-5 bg-pleiades-ink text-white text-[13px] font-medium flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2">
            <MessageCircle size={15} /> WhatsApp’tan ön görüşme başlat
          </button>
          <button type="button" onClick={() => call(consultingIntro)} className="min-h-12 px-5 border border-pleiades-border bg-white text-pleiades-ink text-[13px] font-medium flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue focus-visible:ring-offset-2">
            <Phone size={15} /> Ara
          </button>
        </div>
      </div>
    </section>

    <p className="text-[11px] leading-[1.65] text-pleiades-muted max-w-4xl">Pleiades danışmanlığı meditasyon ve farkındalık rehberliği hizmetidir; sağlık hizmeti, psikoterapi, teşhis veya tıbbi tedavi değildir ve belirli bir sonuç vaat etmez. Uygulamalar kendi deneyim ve sorumluluğun içinde yürütülür.</p>
  </div>;
}
*/

function ProfileSection({ data, user, automation }) {
  const title =
    data.practitioner?.status === "approved" &&
    practitionerTitles[data.practitioner.approved_level];
  const userStatus =
    {
      newcomer: "Pleiades Kullanıcısı",
      practitioner_pending: "Uygulayıcı Onayı Bekleniyor",
      practitioner: "Onaylı Uygulayıcı",
      special_client: "Özel Danışan",
    }[data.portal_mode] || "Pleiades Kullanıcısı";
  const programState = clientProgramState(automation);
  const programMonths =
    data.special_consulting?.package_months ||
    data.consulting_offer?.package_months ||
    automation?.offer?.package_months ||
    automation?.request?.package_months;
  return (
    <Page title="Profilim" description="Hesap bilgileriniz ve doğrulanmış Pleiades durumunuz.">
      <div className="bg-white border border-pleiades-border p-4 sm:p-6">
        <div className="flex items-center gap-4 pb-6 border-b border-pleiades-border">
          <div className="pleiades-initials-avatar w-14 h-14 grid place-items-center text-lg font-semibold">
            {initials(user?.name)}
          </div>
          <div>
            <h2 className="text-lg font-semibold">{user?.name}</h2>
            <div className="text-[12px] text-pleiades-muted mt-1">{data.profile.email}</div>
          </div>
        </div>
        <dl className="grid sm:grid-cols-2 gap-5 mt-6 text-[13px]">
          <Info label="Kullanıcı durumu" value={userStatus} />
          {programMonths && (
            <Info
              label="Program durumu"
              value={
                programState === "active"
                  ? "Aktif"
                  : programState === "completed"
                    ? "Çalışma bitti"
                    : "Beklemede"
              }
            />
          )}
          <Info
            label="Uygulayıcılık"
            value={
              title || (data.practitioner?.status === "pending" ? "Onay bekliyor" : "Bulunmuyor")
            }
          />
          {programMonths && (
            <>
              <Info
                label="Danışmanlık programı"
                value={packageLabel(programMonths)}
              />
              {data.special_consulting && (
                <Info
                  label="Program bitişi"
                  value={
                    data.special_access_state === "active"
                      ? `${formatPortalDate(data.special_consulting.ends_at)} · ${data.special_consulting.remaining_days} gün kaldı`
                      : formatPortalDate(data.special_consulting.ends_at)
                  }
                />
              )}
            </>
          )}
          <Info label="Telefon" value={data.profile.phone || "Henüz eklenmedi"} />
          <Info label="Şehir" value={data.profile.city || "Henüz eklenmedi"} />
        </dl>
        {title && (
          <div className="mt-6 inline-flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-[12px]">
            <ShieldCheck size={15} /> {title} · Onaylandı
          </div>
        )}
      </div>
    </Page>
  );
}

function clientProgramState(automation) {
  if (automation?.daily_program?.status === "completed") {
    return "completed";
  }
  if (automation?.next_action === "follow_plan") {
    return "active";
  }
  return "pending";
}

function PractitionerModal({ choice, setChoice, level, setLevel, saving, submit }) {
  const dialogRef = useRef(null);
  useEffect(() => {
    const previousFocus = document.activeElement;
    const dialog = dialogRef.current;
    dialog?.querySelector("button")?.focus();
    const trapFocus = (event) => {
      if (event.key !== "Tab" || !dialog) return;
      const focusable = [...dialog.querySelectorAll("button:not([disabled])")];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", trapFocus);
    return () => {
      document.removeEventListener("keydown", trapFocus);
      previousFocus?.focus?.();
    };
  }, []);
  return (
    <div className="fixed inset-0 z-50 bg-pleiades-ink/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="practitioner-dialog-title"
        aria-describedby="practitioner-dialog-description"
        className="w-full sm:max-w-lg bg-white border border-pleiades-border shadow-2xl p-5 sm:p-8 max-h-[90vh] overflow-y-auto"
      >
        <Award size={21} className="mb-4" />
        <h2 id="practitioner-dialog-title" className="text-2xl font-semibold tracking-tight">
          Pleiades uygulayıcısı mısınız?
        </h2>
        <p
          id="practitioner-dialog-description"
          className="text-[12px] text-pleiades-slate leading-relaxed mt-2 mb-5"
        >
          Uygulayıcıysanız seviyenizi seçin. Bilginiz yönetici onayından sonra panelinizde
          gösterilir.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Choice
            active={choice === true}
            onClick={() => {
              setChoice(true);
              setLevel("");
            }}
            title="Uygulayıcıyım"
          />
          <Choice
            active={choice === false}
            onClick={() => {
              setChoice(false);
              setLevel("");
            }}
            title="Uygulayıcı değilim"
          />
        </div>
        {choice === true && (
          <div className="grid grid-cols-2 gap-2 mt-4">
            {Object.entries(practitionerLevels).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setLevel(key)}
                className={`min-h-11 px-3 text-[12px] border ${level === key ? "bg-pleiades-ink text-white border-pleiades-ink" : "border-pleiades-border"}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        <button
          onClick={submit}
          disabled={saving || choice === null || (choice && !level)}
          className="w-full min-h-12 bg-pleiades-blue text-white text-[13px] mt-5 disabled:opacity-40"
        >
          {saving ? "Kaydediliyor…" : choice ? "Onaya gönder" : "Kaydet ve devam et"}
        </button>
      </div>
    </div>
  );
}

function Page({ title, description, children }) {
  return (
    <>
      <div className="mb-3 sm:mb-5">
        <h1 className="text-xl sm:text-3xl font-semibold tracking-tight">{title}</h1>
        <p className="text-[11px] sm:text-[12px] text-pleiades-slate mt-1 sm:mt-2 max-w-2xl leading-relaxed">
          {description}
        </p>
      </div>
      {children}
    </>
  );
}
function DrawerNavButton({ item, active, onClick }) {
  const Icon = item.icon;
  return (
    <button
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`relative w-full min-h-12 px-3 flex items-center gap-3 text-left text-[13px] border mb-1 ${active ? "bg-pleiades-blue-soft border-pleiades-blue/20 text-pleiades-blue" : "border-transparent text-pleiades-slate hover:bg-pleiades-ivory hover:text-pleiades-ink"}`}
    >
      <Icon size={17} />
      <span className="flex-1">{item.label}</span>
      {item.badge > 0 && <Badge value={item.badge} />}
      <ChevronRight size={14} />
    </button>
  );
}
function Badge({ value }) {
  return (
    <span className="min-w-5 h-5 px-1 grid place-items-center rounded-full bg-pleiades-blue text-white text-[9px]">
      {value > 99 ? "99+" : value}
    </span>
  );
}
function AnnouncementCard({ icon: Icon, eyebrow, title, text, action, onClick, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative flex min-h-44 min-w-0 flex-col border border-pleiades-border bg-white p-3 text-left transition-colors hover:border-pleiades-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue sm:min-h-48 sm:p-5"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="grid h-9 w-9 shrink-0 place-items-center bg-pleiades-ivory text-pleiades-blue sm:h-10 sm:w-10">
          <Icon size={17} aria-hidden="true" />
        </div>
        {badge > 0 && <Badge value={badge} />}
      </div>
      <div className="mt-3 text-[10px] font-medium tracking-wide text-pleiades-muted sm:text-[11px]">
        {eyebrow}
      </div>
      <h2 className="mt-0.5 text-[15px] font-semibold leading-tight sm:text-[17px]">{title}</h2>
      <p className="mt-1.5 line-clamp-3 text-[11px] leading-relaxed text-pleiades-slate sm:text-[12px]">
        {text}
      </p>
      <div className="mt-auto flex items-center justify-between border-t border-pleiades-border pt-2.5 text-[11px] font-medium text-pleiades-ink sm:text-[12px]">
        <span>{action}</span>
        <ChevronRight size={14} className="transition-transform group-hover:translate-x-0.5" />
      </div>
    </button>
  );
}
function ActionCard({ icon: Icon, eyebrow, title, text, action, onClick, accent, badge }) {
  return (
    <button
      onClick={onClick}
      className={`relative flex min-w-0 flex-col border p-3 text-left transition-colors sm:min-h-48 sm:p-5 ${accent ? "bg-pleiades-ink text-white border-pleiades-ink hover:border-pleiades-gold" : "bg-white border-pleiades-border hover:border-pleiades-blue"}`}
    >
      <div className="flex min-w-0 items-start gap-3 sm:block">
        <div
          className={`grid h-11 w-11 shrink-0 place-items-center sm:h-10 sm:w-10 ${accent ? "bg-white/10" : "bg-pleiades-ivory"}`}
        >
          <Icon size={17} />
        </div>
        <div className="min-w-0 flex-1 sm:mt-5">
          <div
            className={`text-[12px] font-medium tracking-wide ${accent ? "text-white/60" : "text-pleiades-muted"}`}
          >
            {eyebrow}
          </div>
          <h2 className="mt-0.5 text-[17px] font-semibold leading-snug sm:mt-1 sm:text-[16px]">
            {title}
          </h2>
          <p
            className={`mt-1.5 text-[13px] leading-relaxed sm:mt-2 ${accent ? "text-white/75" : "text-pleiades-slate"}`}
          >
            {text}
          </p>
        </div>
      </div>
      {badge > 0 && (
        <span className="absolute right-4 top-4 sm:right-5 sm:top-5">
          <Badge value={badge} />
        </span>
      )}
      <div className="mt-2 flex min-h-11 items-center justify-between border-t border-current/10 pt-2 text-[13px] font-medium sm:mt-auto sm:min-h-0 sm:justify-start sm:border-0 sm:pt-4">
        {action}
        <ChevronRight size={14} />
      </div>
    </button>
  );
}
function ProgramEntry({ item, expanded, onToggle }) {
  const guides = (item.guides || []).join(", ");
  const location = [item.city, item.venue || item.location].filter(Boolean).join(" · ");
  const whatsappPhone = String(item.whatsapp_phone || "905327991130").replace(/\D/g, "");
  const whatsappText = encodeURIComponent(`Merhaba, ${item.title} etkinliği hakkında bilgi almak istiyorum.`);
  const rows = item.program_type === "camp" ? [
    ["Kamp başlangıcı", `${formatPortalDate(item.event_date)} · ${item.event_time}`],
    ["Otele giriş", item.check_in_date ? `${formatPortalDate(item.check_in_date)} · ${item.check_in_time || "—"}` : "—"],
    ["Otelden çıkış", item.check_out_date ? `${formatPortalDate(item.check_out_date)} · ${item.check_out_time || "—"}` : "—"],
    ["İl ve mekân", location || "—"],
    ["Konaklama", item.accommodation_included ? "Ücrete dahil" : "Ücrete dahil değil"],
    ["Ücret", item.price || "Bilgi için iletişime geçin"],
    ["Erken kayıt", item.early_registration || "Bulunmuyor"],
    ["Rehberler", guides || "Şeref Tolga Kuralay"],
  ] : item.program_type === "training" ? [
    ["Tarih ve saat", `${formatPortalDate(item.event_date)} · ${item.event_time}`],
    ["İl ve mekân", location || "—"],
    ["Ücret", item.price || "Bilgi için iletişime geçin"],
    ["Rehberler", guides || "Şeref Tolga Kuralay"],
  ] : [
    ["Başlangıç", `${formatPortalDate(item.event_date)} · ${item.event_time}`],
    ["Süre", item.duration_weeks ? `${item.duration_weeks} hafta` : "—"],
    ["Oturum", item.session_count ? `${item.session_count} oturum` : "—"],
    ["Ücret", item.price || "Bilgi için iletişime geçin"],
    ["Rehberler", guides || "Şeref Tolga Kuralay"],
  ];
  const detailsId = `program-details-${item.id}`;
  return (
    <article className="border border-pleiades-border bg-white">
      <h3>
        <button type="button" onClick={onToggle} aria-expanded={expanded} aria-controls={detailsId} className="flex min-h-14 w-full items-center justify-between gap-4 px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pleiades-blue">
          <span className="min-w-0"><span className="block truncate text-[14px] font-semibold leading-snug text-pleiades-ink">{item.title}</span><span className="mt-1 block text-[11px] text-pleiades-slate">{formatPortalDate(item.event_date)}{item.event_end_date ? ` – ${formatPortalDate(item.event_end_date)}` : ""}</span></span>
          <ChevronDown size={17} className={`shrink-0 text-pleiades-blue transition-transform ${expanded ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
      </h3>
      {expanded && <div id={detailsId}>
        <div className="border-t border-pleiades-border px-4 py-3"><div className="text-[10px] font-medium uppercase tracking-wider text-pleiades-blue">{item.program_type === "camp" ? "Kamp" : item.program_type === "training" ? "Eğitim" : "Zoom"}</div><div className="mt-1 text-[12px] text-pleiades-slate">{item.topic}</div></div>
        <dl className="divide-y divide-pleiades-border border-t border-pleiades-border">
          {rows.map(([label, value]) => <div key={label} className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-3 px-4 py-2.5 text-[11px] sm:grid-cols-[10rem_minmax(0,1fr)] sm:text-[12px]"><dt className="text-pleiades-muted">{label}</dt><dd className="min-w-0 font-medium text-pleiades-ink">{value}</dd></div>)}
        </dl>
        <div className="border-t border-pleiades-border px-4 py-3"><div className="text-[10px] font-medium uppercase tracking-wider text-pleiades-muted">Açıklama</div><p className="mt-2 whitespace-pre-wrap text-[12px] leading-relaxed text-pleiades-slate">{item.description}</p></div>
        <div className="flex flex-col gap-2 border-t border-pleiades-border p-3 sm:flex-row">
          <a href={`https://wa.me/${whatsappPhone}?text=${whatsappText}`} target="_blank" rel="noreferrer" className="inline-flex min-h-11 flex-1 items-center justify-center bg-emerald-600 px-4 text-[12px] font-semibold text-white hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700">WhatsApp ile iletişim</a>
          {item.program_type === "zoom" && item.join_url && <a href={item.join_url} target="_blank" rel="noreferrer" className="inline-flex min-h-11 flex-1 items-center justify-center border border-pleiades-blue px-4 text-[12px] font-semibold text-pleiades-blue">Zoom bağlantısı</a>}
        </div>
      </div>}
    </article>
  );
}
function Info({ label, value }) {
  return (
    <div>
      <dt className="text-[12px] font-medium text-pleiades-muted">{label}</dt>
      <dd className="mt-1">{value}</dd>
    </div>
  );
}

function JourneyMetric({ label, value }) {
  return (
    <div>
      <div className="text-white/60">{label}</div>
      <div className="mt-1 text-white/90">{value}</div>
    </div>
  );
}

function JourneyProgressMetric({ label, value }) {
  return (
    <div className="min-w-0 bg-white p-3.5 sm:p-4">
      <dt className="text-[10px] uppercase tracking-[.08em] text-pleiades-muted">{label}</dt>
      <dd className="mt-1.5 text-[13px] font-semibold text-pleiades-ink tabular-nums break-words">
        {value}
      </dd>
    </div>
  );
}

function dailyProgramRestProgress(program) {
  if (program?.status !== "resting") return { label: "Ara dönemi değil" };
  if (!program?.rest_until || !program?.server_now) return { label: "7 günlük ara" };

  const restUntilDay = istanbulCalendarDay(program.rest_until);
  const serverDay = istanbulCalendarDay(program.server_now);
  if (restUntilDay === null || serverDay === null) return { label: "7 günlük ara" };

  const daysUntilResume = Math.max(0, Math.round((restUntilDay - serverDay) / 86400000));
  const restDay = Math.max(0, Math.min(7, 8 - daysUntilResume));
  return { label: restDay === 0 ? "Yarın başlıyor" : `${restDay} / 7` };
}

function istanbulCalendarDay(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day));
}

function WorkTiming({ file }) {
  const labels = {
    active: "Çalışma devam ediyor",
    eligible_to_complete: "Tamamlamaya uygun",
    completed: "Danışan tarafından tamamlandı",
    cancelled: "Rehber tarafından sonlandırıldı",
    superseded: "Yeni çalışma ile değiştirildi",
    archived: "Sistem geçişinde arşivlendi",
  };
  const remainingDays = Math.max(
    0,
    Math.ceil((new Date(file.eligible_at || file.work_ends_at).getTime() - Date.now()) / 86400000),
  );
  return (
    <div className="mt-4 border border-pleiades-border bg-pleiades-ivory p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[12px] font-semibold">Çalışma takvimi</div>
        <span className="text-[10px] px-2 py-1 bg-white border border-pleiades-border">
          {labels[file.display_status || file.work_status] || "Planlandı"}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-[13px]">
        <div>
          <div className="text-pleiades-muted">Çalışma süresi</div>
          <div className="mt-1">{file.work_days} gün</div>
        </div>
        <div>
          <div className="text-pleiades-muted">Başlangıç</div>
          <div className="mt-1">{formatPortalDate(file.assigned_at || file.work_started_at)}</div>
        </div>
        <div>
          <div className="text-pleiades-muted">Tamamlamaya uygun</div>
          <div className="mt-1">{formatPortalDate(file.eligible_at || file.work_ends_at)}</div>
        </div>
        <div>
          <div className="text-pleiades-muted">Kalan süre</div>
          <div className="mt-1">
            {file.can_complete ? "Süre tamamlandı" : `${remainingDays} gün`}
          </div>
        </div>
      </div>
      {file.work_status === "active" && !file.can_complete && (
        <div className="text-[11px] text-pleiades-blue mt-3">
          Çalışmanın {file.current_day || 1}. günündesiniz. Süre dolmadan tamamlama işlemi açılamaz.
        </div>
      )}
      {file.can_complete && (
        <div className="text-[11px] text-emerald-800 mt-3">
          Önerilen süre tamamlandı. Hazır olduğunuzda çalışmayı tamamlayabilirsiniz.
        </div>
      )}
    </div>
  );
}

function packageLabel(months) {
  return months === 12 ? "1 Yıllık Özel Danışmanlık" : `${months} Aylık Özel Danışmanlık`;
}
function archiveStatusLabel(file) {
  const status = file.assignment_status || file.status;
  if (status === "active") return "Aktif";
  if (status === "cancelled_by_admin") return "Rehber tarafından sonlandırıldı";
  if (status === "superseded_by_admin") return "Yeni çalışma ile değiştirildi";
  if (status === "archived_legacy") return "Sistem geçişinde arşivlendi";
  return "Danışan tarafından tamamlandı";
}
function archiveAssignmentId(item) {
  return item?.assignment_id || item?.id || item?.work?.assignment_id || item?.work?.id;
}
function overviewWorkFingerprint(work) {
  if (!work) return "none";
  return `${work.assignment_id || work.id || "unknown"}:${work.assignment_status || work.work_status || work.status || "unknown"}`;
}
function experienceReceipt(item = {}) {
  return {
    id: item.id,
    assignment_id: item.assignment_id,
    file_id: item.file_id,
    created_at: item.created_at,
    admin_read: Boolean(item.admin_read),
    admin_read_at: item.admin_read_at,
  };
}
function archiveWork(item) {
  if (!item) return {};
  if (item.work) return archiveWork(item.work);
  if (item.file && typeof item.file === "object") {
    return {
      ...item,
      ...item.file,
      id: item.file.id,
      assignment_id: item.assignment_id || item.id,
      assignment_status: item.assignment_status || item.status,
      work_status: item.work_status || item.status,
      work_days: item.work_days || item.duration_days,
      title: item.title || item.file.title || null,
      pdf_available: item.pdf_available ?? Boolean(item.file.id),
    };
  }
  return {
    ...item,
    assignment_id: item.assignment_id || item.id,
    assignment_status: item.assignment_status || item.status,
    work_status: item.work_status || item.status,
    work_days: item.work_days || item.duration_days,
  };
}
function formatPortalDate(value) {
  return value ? new Date(value).toLocaleDateString("tr-TR") : "—";
}
function formatPortalDateTime(value) {
  return value ? new Date(value).toLocaleString("tr-TR") : "—";
}
function Choice({ active, onClick, title }) {
  return (
    <button
      onClick={onClick}
      className={`min-h-16 p-3 text-left border text-[12px] font-semibold ${active ? "border-pleiades-blue ring-2 ring-pleiades-blue/10" : "border-pleiades-border"}`}
    >
      {title}
    </button>
  );
}
function Loading() {
  return (
    <div role="status" aria-live="polite" className="space-y-4 animate-pulse">
      <span className="sr-only">Panel hazırlanıyor…</span>
      <div className="h-32 bg-white border border-pleiades-border" />
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="h-48 bg-white" />
        <div className="h-48 bg-white" />
        <div className="h-48 bg-white" />
      </div>
    </div>
  );
}
function initials(name) {
  return (name || "P")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}
